/**
 * Mapove zobrazenie zariadeni (Leaflet + markercluster).
 *
 * Vyclenene z App.js 2026-08-05. Kniznica L (Leaflet) je globalna -
 * nacitava sa v public/index.html z public/vendor/, nie cez import.
 */
/* global L */
import React, { useState, useEffect, useRef } from 'react';
import { CE, S, PING_OPTS, pingLabel, formatOidValue } from './shared';

// Co je to za port. Typ z databazy sa ukazovat neda — SNMP discovery hlasi
// aj radio ako 'ethernet', takze by pri ath0 svietilo "[ethernet]", co je
// presne to, co zvadza vybrat zly port. Backend posiela pole `druh`.
function popisDruhu(i) {
  if (i.druh === 'radio') return '\uD83D\uDCE1 r\u00e1dio';
  if (i.druh === 'logicke') return 'logick\u00e9';
  if (i.druh === 'metalika') return 'metalika';
  return i.interface_type || 'ethernet';   // stare odpovede bez `druh`
}

function MapView(props) {
  var devices = props.devices;
  var onRefresh = props.onRefresh;
  var isMobile = props.isMobile || false;
  var onOpenTicket = props.onOpenTicket || function() {};
  var onViewTicket = props.onViewTicket || function() {};
  var mapRef = useRef(null);
  var leafletMap = useRef(null);
  var savedViewRef = useRef(null); // { center: {lat,lng}, zoom } — persists across re-renders
  var markersRef = useRef({});   // keyed by device id
  var moveModeRef = useRef(null); // { marker, device } when drag-to-reposition active
  var linksLayerRef = useRef(null); // L.LayerGroup for link polylines
  var [showLinks, setShowLinks] = useState(true); // default: prepojenia zapnuté
  // Stitky s nameranymi hodnotami na spojoch.
  // Nastavuju sa PRE KAZDY SPOJ ZVLAST (pravy klik na spoj) a ulozene su v DB,
  // nie v prehliadaci — patria k spoju, nie k pocitacu.
  var [stitkySpojov, setStitkySpojov] = useState({});   // { link_id: [ {name,value,unit,device_name} ] }
  var [stitokModal, setStitokModal] = useState(null);   // { link, devices, selected }
  var [stitokUklada, setStitokUklada] = useState(false);
  var [vybrateRozhranie, setVybrateRozhranie] = useState({});   // { device_id: interface_id }
  var [mapMobileMenuOpen, setMapMobileMenuOpen] = useState(false);
  var [topo, setTopo] = useState({ devices: [], interfaces: [], links: [] });
  var [mapInitTs, setMapInitTs] = useState(0); // incremented on each map reinit
  var [savingMsg, setSavingMsg] = useState(null);
  var [ctxMenu, setCtxMenu] = useState(null); // { x, y, device|null, lat, lng }
  var [moveHint, setMoveHint] = useState(false);
  var [addForm, setAddForm] = useState(null);   // { lat, lng }
  var [editForm, setEditForm] = useState(null); // device object
  var [formData, setFormData] = useState({ name: '', ip: '', type: 'router', location: '' });
  var [editIfaces, setEditIfaces] = useState([]);
  var [newIfaceDat, setNewIfaceDat] = useState({ name: '', type: 'ethernet', speed: '', ip: '' });
  var [editingEditIface, setEditingEditIface] = useState(null); // { id, name, type, speed, ip }
  var editFormId = editForm ? editForm.id : null;
  // Sites
  var siteMarkersRef = useRef({});
  var clusterRef = useRef(null); // ref to MarkerClusterGroup — used by startSiteMoveMode
  var [sites, setSites] = useState([]);
  var [currentSite, setCurrentSite] = useState(null); // opened site panel
  var [siteModal, setSiteModal] = useState(null);  // { mode:'create'|'edit', lat, lng, site? }
  var [siteForm, setSiteForm] = useState({ name: '', description: '', device_ids: [] });
  var [sitePanelCtx, setSitePanelCtx] = useState(null); // context menu inside site panel
  var [solarNaSite, setSolarNaSite] = useState([]);     // solárne elektrárne otvorenej lokality
  // Edit-device tabbed modal (same as TopologyView)
  var [editDevModal, setEditDevModal] = useState(null);
  var [editDevForm, setEditDevForm] = useState({ name: '', ip: '', type: 'router', location: '', web_url: '' });
  var [editDevTab, setEditDevTab] = useState('basic');
  var [editDevSnmp, setEditDevSnmp] = useState({ enabled: false, community: 'public', port: 161, version: 'v2c', show_map: true, show_topo: true, show_info: true, v3_user: '', v3_level: 'noAuthNoPriv', v3_auth_proto: 'SHA', v3_auth_pass: '', v3_priv_proto: 'AES', v3_priv_pass: '' });
  var [editDevNotes, setEditDevNotes] = useState({ description: '', notes: '' });
  var [editDevWifi, setEditDevWifi] = useState({ azimuth: '', tilt: '', polarization: '', frequency: '', ssid: '' });
  var [editDevPing, setEditDevPing] = useState(60);
  var [editDevOids, setEditDevOids] = useState([]);
  // Sablony SNMP OID-ov podla typu hardveru
  var [snmpSablony, setSnmpSablony] = useState([]);
  var [vybranaSablona, setVybranaSablona] = useState('');
  var [sablonaNahlad, setSablonaNahlad] = useState(null);
  var [sablonaMsg, setSablonaMsg] = useState(null);
  var [newOidForm, setNewOidForm] = useState({ name: '', oid: '', unit: '', multiplier: '1', category: 'general' });
  var [snmpLatest, setSnmpLatest] = useState([]);
  var [snmpHistory, setSnmpHistory] = useState([]);
  var [mapOidScanning, setMapOidScanning] = useState(false);
  var [mapOidResults, setMapOidResults] = useState(null);
  var [mapEditingOid, setMapEditingOid] = useState(null);
  var [mapNewIface, setMapNewIface] = useState({ name: '', type: 'ethernet', speed: '', ip: '' });
  var [mapEditingIface, setMapEditingIface] = useState(null);
  var [mapSnmpDiscovering, setMapSnmpDiscovering] = useState(false);
  var [mapSnmpDiscovered, setMapSnmpDiscovered] = useState(null);
  var [mapAlertRules, setMapAlertRules] = useState([]);
  var [mapNewAlert, setMapNewAlert] = useState({ name: '', metric: 'ping', oid_id: '', operator: '>', threshold: '', ping_minutes: 10, notify_emails: '', notify_phones: '' });
  var [mapEditingAlert, setMapEditingAlert] = useState(null);
  var [mapDevTickets, setMapDevTickets] = useState([]);
  var [mapDevTicketsLoading, setMapDevTicketsLoading] = useState(false);
  var [mapDevVpn, setMapDevVpn] = useState(null);
  var [mapDevVpnLoading, setMapDevVpnLoading] = useState(false);
  var [mapDevVpnMsg, setMapDevVpnMsg] = useState(null);
  var [mapDevVpnRoutes, setMapDevVpnRoutes] = useState('10.200.201.0/24');
  var [mapDevVpnConfigShown, setMapDevVpnConfigShown] = useState(false);
  var [mapDevVpnConfig, setMapDevVpnConfig] = useState('');
  var [mapDiagPing, setMapDiagPing] = useState('');       // streaming ping text output
  var [mapDiagPingLoading, setMapDiagPingLoading] = useState(false);
  var [mapDiagPingOk, setMapDiagPingOk] = useState(null); // null | true | false
  var [mapDiagDns, setMapDiagDns] = useState(null);       // dns result object
  var [mapDiagDnsLoading, setMapDiagDnsLoading] = useState(false);
  var [mapDiagDnsHost, setMapDiagDnsHost] = useState('');
  var [mapDiagCustomIp, setMapDiagCustomIp] = useState('');
  var [mapDiagRoute, setMapDiagRoute] = useState('');
  var [mapDiagRouteLoading, setMapDiagRouteLoading] = useState(false);
  var [mapDiagRouteIp, setMapDiagRouteIp] = useState('');
  var [mapLinkModal, setMapLinkModal] = useState(null);   // { device, ifaces }
  var [mapLinkData, setMapLinkData] = useState({ srcIfaceId: '', tgtDeviceId: '', tgtIfaceId: '', cableLabel: '', cableType: 'utp' });
  var [mapTgtIfaces, setMapTgtIfaces] = useState([]);
  // Draw-link mode (click device A → click device B → interface modal)
  var mapDrawLinkModeRef = useRef(false);
  var mapDrawLinkSrcRef = useRef(null);
  var [mapDrawLinkMode, setMapDrawLinkMode] = useState(false);
  var [mapDrawLinkSrc, setMapDrawLinkSrc] = useState(null);
  var [mapQuickLinkModal, setMapQuickLinkModal] = useState(null); // { srcDevice, srcIfaces, tgtDevice, tgtIfaces }
  var [mapQuickLinkData, setMapQuickLinkData] = useState({ srcIfaceId: '', tgtIfaceId: '', cableLabel: '', cableType: 'utp' });
  var [mapSiteDevPicker, setMapSiteDevPicker] = useState(null); // { site, x, y } — shown when in draw-link mode and site is clicked
  var [mapEditLinkModal, setMapEditLinkModal] = useState(null); // { link, ifacesA, ifacesB, devA, devB }
  var [mapEditLinkData, setMapEditLinkData] = useState({ srcIfaceId: '', tgtIfaceId: '', cableLabel: '', cableType: 'utp' });
  var [mapDeleteConfirm, setMapDeleteConfirm] = useState(null); // device to delete (confirm dialog)


  function closeCtx() { setCtxMenu(null); }

  function fetchMapTopo() {
    fetch('/api/topology').then(function(r) { return r.json(); }).then(function(data) { setTopo(data); });
  }
  function openMapLinkModal(device) {
    fetch('/api/devices/' + device.id + '/interfaces')
      .then(function(r) { return r.json(); })
      .then(function(data) {
        setMapLinkModal({ device: device, ifaces: data.interfaces || [] });
        setMapLinkData({ srcIfaceId: '', tgtDeviceId: '', tgtIfaceId: '', cableLabel: '', cableType: 'utp' });
        setMapTgtIfaces([]);
      });
  }
  function onMapTgtDeviceChange(devId) {
    setMapLinkData(function(f) { return Object.assign({}, f, { tgtDeviceId: devId, tgtIfaceId: '' }); });
    if (!devId) { setMapTgtIfaces([]); return; }
    fetch('/api/devices/' + devId + '/interfaces')
      .then(function(r) { return r.json(); })
      .then(function(data) { setMapTgtIfaces(data.interfaces || []); });
  }
  function saveMapLink() {
    if (!mapLinkData.srcIfaceId || !mapLinkData.tgtIfaceId) { setSavingMsg('\u274C Vyber oba interfacy!'); setTimeout(function() { setSavingMsg(null); }, 3000); return; }
    fetch('/api/links', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ interface_a_id: parseInt(mapLinkData.srcIfaceId), interface_b_id: parseInt(mapLinkData.tgtIfaceId), cable_label: mapLinkData.cableLabel || null, cable_type: mapLinkData.cableType }),
    }).then(function(r) { if (!r.ok) return r.json().then(function(e) {
      throw new Error((e && e.detail) || ''); }); return r.json(); })
      .then(function() { setSavingMsg('\u2705 Prepojenie vytvorene!'); setTimeout(function() { setSavingMsg(null); }, 3000); setMapLinkModal(null); setMapTgtIfaces([]); fetchMapTopo(); })
      .catch(function(e) { setSavingMsg('\u274C ' + ((e && e.message) || 'Prepojenie sa nepodarilo vytvori\u0165')); setTimeout(function() { setSavingMsg(null); }, 6000); });
  }

  function toggleMapDrawLinkMode() {
    var newMode = !mapDrawLinkModeRef.current;
    mapDrawLinkModeRef.current = newMode;
    mapDrawLinkSrcRef.current = null;
    setMapDrawLinkMode(newMode);
    setMapDrawLinkSrc(null);
    if (!newMode) setSavingMsg(null);
    if (leafletMap.current) leafletMap.current.getContainer().style.cursor = newMode ? 'crosshair' : '';
  }

  function saveMapQuickLink() {
    if (!mapQuickLinkData.srcIfaceId || !mapQuickLinkData.tgtIfaceId) { setSavingMsg('\u274C Vyber oba interfacy!'); setTimeout(function() { setSavingMsg(null); }, 3000); return; }
    var srcDev = mapQuickLinkModal ? mapQuickLinkModal.srcDevice : null;
    var srcIfaces = mapQuickLinkModal ? mapQuickLinkModal.srcIfaces : [];
    fetch('/api/links', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ interface_a_id: parseInt(mapQuickLinkData.srcIfaceId), interface_b_id: parseInt(mapQuickLinkData.tgtIfaceId), cable_label: mapQuickLinkData.cableLabel || null, cable_type: mapQuickLinkData.cableType }),
    }).then(function(r) { if (!r.ok) return r.json().then(function(e) {
      throw new Error((e && e.detail) || ''); }); return r.json(); })
      .then(function() {
        setMapQuickLinkModal(null);
        // Keep source selected for rapid multi-connect (WiFi AP → many clients)
        mapDrawLinkSrcRef.current = srcDev;
        setMapDrawLinkSrc(srcDev);
        setSavingMsg('\u2705 Prepojenie vytvorene! \uD83D\uDEEB AP ost\u00e1va zdroj \u2014 klikni na \u010falšieho klienta');
        setTimeout(function() { setSavingMsg(null); }, 4000);
        setMapQuickLinkData({ srcIfaceId: mapQuickLinkData.srcIfaceId, tgtIfaceId: '', cableLabel: '', cableType: mapQuickLinkData.cableType });
        fetchMapTopo();
      })
      .catch(function(e) { setSavingMsg('\u274C ' + ((e && e.message) || 'Prepojenie sa nepodarilo vytvori\u0165')); setTimeout(function() { setSavingMsg(null); }, 6000); });
  }

  // Shared draw-link device selection — used by both device markers and site device picker
  function handleMapPickDevice(d) {
    var src = mapDrawLinkSrcRef.current;
    setMapSiteDevPicker(null);
    if (!src) {
      mapDrawLinkSrcRef.current = d;
      setMapDrawLinkSrc(d);
      setSavingMsg('\uD83D\uDD17 Zdroj: ' + d.name + ' \u2014 klikni na cie\u013eov\u00e9 zariadenie');
    } else if (src.id !== d.id) {
      var srcDev = src;
      mapDrawLinkSrcRef.current = null;
      setMapDrawLinkSrc(null);
      setSavingMsg(null);
      Promise.all([
        fetch('/api/devices/' + srcDev.id + '/interfaces').then(function(r) { return r.json(); }),
        fetch('/api/devices/' + d.id + '/interfaces').then(function(r) { return r.json(); }),
      ]).then(function(res) {
        setMapQuickLinkModal({ srcDevice: srcDev, srcIfaces: res[0].interfaces || [], tgtDevice: d, tgtIfaces: res[1].interfaces || [] });
        setMapQuickLinkData({ srcIfaceId: '', tgtIfaceId: '', cableLabel: '', cableType: 'utp' });
      });
    }
  }

  function deleteMapDevice(device) {
    setMapDeleteConfirm(null);
    fetch('/api/devices/' + device.id, { method: 'DELETE' })
      .then(function(r) { if (!r.ok) throw new Error(); })
      .then(function() {
        setSavingMsg('\u2705 Zariadenie \u201e' + device.name + '\u201c bolo zmazané');
        setTimeout(function() { setSavingMsg(null); }, 3000);
        fetchMapTopo();
      })
      .catch(function() {
        setSavingMsg('\u274C Chyba pri mazaní zariadenia!');
        setTimeout(function() { setSavingMsg(null); }, 3000);
      });
  }

  function openMapEditLink(link) {
    closeCtx();
    var devAId = link.device_a_id;
    var devBId = link.device_b_id;
    Promise.all([
      fetch('/api/devices/' + devAId + '/interfaces').then(function(r) { return r.json(); }),
      fetch('/api/devices/' + devBId + '/interfaces').then(function(r) { return r.json(); }),
    ]).then(function(results) {
      var ifacesA = results[0].interfaces || [];
      var ifacesB = results[1].interfaces || [];
      var devA = (topo.devices || []).find(function(d) { return d.id === devAId; }) || { id: devAId, name: 'Zariadenie A' };
      var devB = (topo.devices || []).find(function(d) { return d.id === devBId; }) || { id: devBId, name: 'Zariadenie B' };
      setMapEditLinkModal({ link: link, ifacesA: ifacesA, ifacesB: ifacesB, devA: devA, devB: devB });
      setMapEditLinkData({ srcIfaceId: String(link.interface_a_id), tgtIfaceId: String(link.interface_b_id), cableLabel: link.cable_label || '', cableType: link.cable_type || 'utp' });
    });
  }

  function saveMapEditLink() {
    if (!mapEditLinkModal) return;
    if (!mapEditLinkData.srcIfaceId || !mapEditLinkData.tgtIfaceId) { setSavingMsg('\u274C Vyber oba interfacy!'); setTimeout(function() { setSavingMsg(null); }, 3000); return; }
    fetch('/api/links/' + mapEditLinkModal.link.id, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ interface_a_id: parseInt(mapEditLinkData.srcIfaceId), interface_b_id: parseInt(mapEditLinkData.tgtIfaceId), cable_label: mapEditLinkData.cableLabel || null, cable_type: mapEditLinkData.cableType }),
    }).then(function(r) { if (!r.ok) return r.json().then(function(e) { throw new Error(e.detail || 'Chyba'); }); return r.json(); })
      .then(function() { setSavingMsg('\u2705 Prepojenie upravene!'); setTimeout(function() { setSavingMsg(null); }, 3000); setMapEditLinkModal(null); fetchMapTopo(); })
      .catch(function(e) { setSavingMsg('\u274C ' + (e.message || 'Chyba')); setTimeout(function() { setSavingMsg(null); }, 4000); });
  }

  function deleteMapLink(link) {
    closeCtx();
    fetch('/api/links/' + link.id, { method: 'DELETE' })
      .then(function() { setSavingMsg('\u2705 Prepojenie vymazane!'); setTimeout(function() { setSavingMsg(null); }, 3000); fetchMapTopo(); })
      .catch(function() { setSavingMsg('\u274C Chyba pri mazani'); setTimeout(function() { setSavingMsg(null); }, 3000); });
  }

  // doRefresh=true only when user explicitly ends move mode (Save/Cancel buttons)
  function stopMoveMode(doRefresh) {
    if (moveModeRef.current) {
      var m = moveModeRef.current.marker;
      if (m && m.dragging) { try { m.dragging.disable(); } catch(e) {} }
      // If it was a site, move it back from direct-map layer to cluster
      if (moveModeRef.current.site && clusterRef.current && leafletMap.current && !doRefresh) {
        try { leafletMap.current.removeLayer(m); clusterRef.current.addLayer(m); } catch(e) {}
      }
      moveModeRef.current = null;
    }
    setMoveHint(false);
    if (leafletMap.current) leafletMap.current.getContainer().style.cursor = '';
    if (doRefresh && onRefresh) onRefresh();
  }

  function startMoveMode(device) {
    stopMoveMode(false); // stop any previous move mode WITHOUT triggering a refresh
    var marker = markersRef.current[device.id];
    if (!marker) return;
    marker.dragging.enable();
    marker.closePopup();
    moveModeRef.current = { marker: marker, device: device };
    setMoveHint(true);
    if (leafletMap.current) leafletMap.current.getContainer().style.cursor = 'crosshair';
  }

  function startSiteMoveMode(site) {
    stopMoveMode(false);
    var marker = siteMarkersRef.current[site.id];
    if (!marker) return;
    // Remove from cluster and add directly to map so Leaflet dragging works
    if (clusterRef.current) { try { clusterRef.current.removeLayer(marker); } catch(e) {} }
    if (leafletMap.current) { try { marker.addTo(leafletMap.current); } catch(e) {} }
    marker.dragging.enable();
    try { marker.closeTooltip(); } catch(e) {}
    moveModeRef.current = { marker: marker, site: site };
    setMoveHint(true);
    if (leafletMap.current) leafletMap.current.getContainer().style.cursor = 'crosshair';
  }

  // Solárne elektrárne otvorenej lokality. Hodnoty sú z DB (plní ju monitor),
  // takže otvorenie panelu nesiaha na regulátor.
  useEffect(function() {
    if (!currentSite) { setSolarNaSite([]); return; }
    var zrusene = false;
    fetch('/api/sites/' + currentSite.id + '/solar')
      .then(function(r) { return r.json(); })
      .then(function(d) { if (!zrusene) setSolarNaSite(d.plants || []); })
      .catch(function() { if (!zrusene) setSolarNaSite([]); });
    return function() { zrusene = true; };
  }, [currentSite]);

  // Hodnoty pre stitky — jeden dopyt pre celu mapu. Vracia iba spoje, ktore
  // maju nieco nastavene, takze mapa bez nastavenia zostava cista.
  function nacitajStitkySpojov() {
    fetch('/api/map/link-labels')
      .then(function(r) { return r.json(); })
      .then(function(d) { setStitkySpojov(d.labels || {}); })
      .catch(function() {});
  }

  useEffect(function() {
    nacitajStitkySpojov();
    var t = setInterval(nacitajStitkySpojov, 60000);
    return function() { clearInterval(t); };
  }, []);

  // Pravy klik na spoj -> nastavenie, co sa na nom ukazuje
  function otvorStitokModal(link) {
    fetch('/api/links/' + link.id + '/label-metrics')
      .then(function(r) { return r.json(); })
      .then(function(d) {
        setStitokModal({ link: link, devices: d.devices || [], selected: d.selected || [] });
      })
      .catch(function() { setSavingMsg('\u274C Nepodarilo sa na\u010d\u00edta\u0165 hodnoty'); setTimeout(function() { setSavingMsg(null); }, 3000); });
  }

  // Prida prevadzku zvoleneho rozhrania. OID-y sa vytvoria az teraz — switch
  // ma vela portov a merat vsetky nema zmysel. Index rozhrania si backend
  // najde sam podla mena (mena pochadzaju zo SNMP discovery).
  // Ktore porty uz zariadenie meria. Sablona hardveru pridava prevadzku cez
  // firemny MIB ("Eth2"), vyber portu cez IF-MIB ("p4_data_e2") — je to ten
  // isty fyzicky port, len inou cestou, takze by sa na stitku objavil dvakrat.
  // Mena sa nedaju sparovat automaticky, preto sa pouzivatela opytame.
  function uzMeranaPrevadzka(zar) {
    return (zar && zar.oids || [])
      .filter(function(o) { return String(o.name || '').indexOf('prev\u00e1dzka') >= 0; })
      .map(function(o) { return String(o.name).split(' - ')[0]; })
      .filter(function(m, i, pole) { return pole.indexOf(m) === i; });
  }

  function pridajPrevadzkuRozhrania(deviceId) {
    var ifId = vybrateRozhranie[deviceId];
    if (!ifId || !stitokModal) return;
    var zar = stitokModal.devices.filter(function(z) { return z.id === deviceId; })[0];
    var uz = uzMeranaPrevadzka(zar);
    if (uz.length) {
      var meno = ((zar.interfaces || []).filter(function(i) { return String(i.id) === String(ifId); })[0] || {}).name || 'port';
      if (!window.confirm('Zariadenie u\u017E meria prev\u00e1dzku: ' + uz.join(', ') + '.\n\n'
            + 'Ak je „' + meno + '\u201c ten ist\u00fd fyzick\u00fd port, bude\u0161 ma\u0165 t\u00fa ist\u00fa hodnotu dvakr\u00e1t '
            + '(raz cez firemn\u00fd MIB, raz cez IF-MIB).\n\nPrida\u0165 aj tak?')) return;
    }
    setStitokUklada(true);
    fetch('/api/devices/' + deviceId + '/snmp/interface-traffic', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ interface_id: parseInt(ifId) }),
    }).then(function(r) { return r.json().then(function(d) { return { ok: r.ok, d: d }; }); })
      .then(function(v) {
        setStitokUklada(false);
        if (!v.ok) {
          var m = v.d && v.d.detail;
          setSavingMsg('\u274C ' + ((m && m.message) || m || 'Nepodarilo sa prida\u0165'));
          setTimeout(function() { setSavingMsg(null); }, 5000);
          return;
        }
        // Predvyplnime kratky popis — "p6_air - prevádzka DNU" je na mape
        // zbytocne dlhe. Pouzivatel si to moze prepisat.
        var nove2 = (v.d.oids || []).map(function(o) {
          var meno = String(o.name || '');
          var sip = meno.indexOf('DNU') >= 0 ? '\u2193' : meno.indexOf('VON') >= 0 ? '\u2191' : '';
          var zaklad = meno.split(' - ')[0];
          return { oid_id: o.id, popis: sip ? (zaklad + ' ' + sip) : zaklad };
        });
        // znovu nacitame zoznam, aby sa nove OID-y objavili, a rovno ich zapneme
        fetch('/api/links/' + stitokModal.link.id + '/label-metrics')
          .then(function(r) { return r.json(); })
          .then(function(d2) {
            setStitokModal(function(p2) {
              if (!p2) return p2;
              var doterajsie = p2.selected.slice();
              nove2.forEach(function(n) {
                if (!doterajsie.some(function(x) { return x.oid_id === n.oid_id; }))
                  doterajsie.push(n);
              });
              return { link: p2.link, devices: d2.devices || [], selected: doterajsie };
            });
          });
      })
      .catch(function() { setStitokUklada(false); setSavingMsg('\u274C Chyba spojenia'); setTimeout(function() { setSavingMsg(null); }, 3000); });
  }

  // 'selected' je pole { oid_id, popis }. Popis je volitelny — prazdny znamena
  // "pouzi povodny nazov OID-u".
  function jeZvolene(oidId) {
    return stitokModal && stitokModal.selected.some(function(x) { return x.oid_id === oidId; });
  }
  function popisPre(oidId) {
    if (!stitokModal) return '';
    var n = null;
    stitokModal.selected.forEach(function(x) { if (x.oid_id === oidId) n = x; });
    return n && n.popis ? n.popis : '';
  }
  function prepniZvolene(oidId) {
    setStitokModal(function(p2) {
      if (!p2) return p2;
      var je = p2.selected.some(function(x) { return x.oid_id === oidId; });
      var nove = je
        ? p2.selected.filter(function(x) { return x.oid_id !== oidId; })
        : p2.selected.concat([{ oid_id: oidId, popis: null }]);
      return Object.assign({}, p2, { selected: nove });
    });
  }
  function nastavPopis(oidId, text) {
    setStitokModal(function(p2) {
      if (!p2) return p2;
      return Object.assign({}, p2, {
        selected: p2.selected.map(function(x) {
          return x.oid_id === oidId ? Object.assign({}, x, { popis: text }) : x;
        }),
      });
    });
  }

  function ulozStitokModal() {
    if (!stitokModal) return;
    setStitokUklada(true);
    fetch('/api/links/' + stitokModal.link.id + '/label-metrics', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: stitokModal.selected }),
    }).then(function(r) { if (!r.ok) return r.json().then(function(e) {
      throw new Error((e && e.detail) || ''); }); return r.json(); })
      .then(function() {
        setStitokUklada(false); setStitokModal(null);
        setSavingMsg('\u2705 Ulo\u017een\u00e9');
        setTimeout(function() { setSavingMsg(null); }, 2500);
        nacitajStitkySpojov();
      })
      .catch(function() { setStitokUklada(false); setSavingMsg('\u274C Chyba pri ukladan\u00ed'); setTimeout(function() { setSavingMsg(null); }, 3000); });
  }

  // Kym je otvoreny vyber zariadenia (prepajanie), stranka sa nesmie posuvat.
  // Bez tohto koliesko mysi posuvalo stranku a mapa "utekala" pod oknom - vyzeralo
  // to, akoby sa hybala mapa namiesto zoznamu.
  useEffect(function() {
    if (!mapSiteDevPicker) return;
    var povodne = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return function() { document.body.style.overflow = povodne; };
  }, [mapSiteDevPicker]);

  // Escape closes everything
  useEffect(function() {
    function onKey(e) { if (e.key === 'Escape') { closeCtx(); setAddForm(null); setEditForm(null); setEditDevModal(null); stopMoveMode(true); setCurrentSite(null); setSiteModal(null); setSitePanelCtx(null); } }
    document.addEventListener('keydown', onKey);
    return function() { document.removeEventListener('keydown', onKey); };
  }, []);

  // Fetch interfaces when edit form opens
  useEffect(function() {
    if (!editFormId) { setEditIfaces([]); setNewIfaceDat({ name: '', type: 'ethernet', speed: '', ip: '' }); return; }
    fetch('/api/devices/' + editFormId + '/interfaces')
      .then(function(r) { return r.json(); })
      .then(function(data) { setEditIfaces(data.interfaces || []); });
  }, [editFormId]);

  // Fetch topology data whenever devices list changes (so links know current coords)
  useEffect(function() {
    fetch('/api/topology')
      .then(function(r) { return r.json(); })
      .then(function(data) { setTopo(data); });
  }, [devices]);

  // Fetch sites
  useEffect(function() {
    fetch('/api/map/sites')
      .then(function(r) { return r.json(); })
      .then(function(data) { setSites(data.sites || []); });
  }, [devices]);

  // Draw / hide link polylines on map
  useEffect(function() {
    var L = window.L;
    var map = leafletMap.current;
    if (!L || !map) return;

    // Remove old layer if present
    if (linksLayerRef.current) { linksLayerRef.current.remove(); linksLayerRef.current = null; }
    if (!showLinks || !topo.links || topo.links.length === 0) return;

    // Build lookup: device_id -> device (with coords)
    var devById = {};
    (topo.devices || []).forEach(function(d) { devById[d.id] = d; });

    // Build lookup: interface_id -> device_id
    var ifaceToDevice = {};
    (topo.interfaces || []).forEach(function(iface) { ifaceToDevice[iface.id] = iface.device_id; });

    var layer = L.layerGroup();
    topo.links.forEach(function(lk) {
      var dIdA = ifaceToDevice[lk.interface_a_id];
      var dIdB = ifaceToDevice[lk.interface_b_id];
      var devA = devById[dIdA];
      var devB = devById[dIdB];
      if (!devA || !devB || !devA.latitude || !devA.longitude || !devB.latitude || !devB.longitude) return;

      var ctype = (lk.cable_type || '').toLowerCase();
      var color = ctype === 'sfp+' ? '#38bdf8' : ctype === 'sfp' ? '#818cf8' : ctype === 'fiber' ? '#a78bfa' : '#94a3b8';

      var line = L.polyline(
        [[devA.latitude, devA.longitude], [devB.latitude, devB.longitude]],
        { color: color, weight: 2, opacity: 0.75, dashArray: ctype === 'wifi' ? '5,6' : null }
      );
      var labelA = (topo.interfaces || []).find(function(i) { return i.id === lk.interface_a_id; });
      var labelB = (topo.interfaces || []).find(function(i) { return i.id === lk.interface_b_id; });
      var tipText =
        (devA.name || '?') + ' [' + (labelA ? labelA.name : '?') + ']' +
        ' ↔ ' +
        (devB.name || '?') + ' [' + (labelB ? labelB.name : '?') + ']' +
        (lk.cable_label ? '<br><small>' + lk.cable_label + '</small>' : '');
      line.bindTooltip(tipText, { sticky: true, opacity: 0.92 });
      // Make line thicker on hover for easier clicking
      line.on('mouseover', function() { line.setStyle({ weight: 5, opacity: 1 }); });
      line.on('mouseout', function() { line.setStyle({ weight: 2, opacity: 0.75 }); });
      // Right-click on link → context menu
      (function(capturedLk) {
        line.on('contextmenu', function(e) {
          L.DomEvent.stopPropagation(e);
          setCtxMenu({ x: e.originalEvent.clientX, y: e.originalEvent.clientY, link: capturedLk });
        });
        // Left-click also opens context menu for mobile (touch) convenience
        line.on('click', function(e) {
          L.DomEvent.stopPropagation(e);
          setCtxMenu({ x: e.originalEvent.clientX, y: e.originalEvent.clientY, link: capturedLk });
        });
      })(lk);
      layer.addLayer(line);

      // ── Stitok s nameranymi hodnotami v strede spoja ──
      // Obsah si urcuje kazdy spoj sam (pravy klik -> Udaje na spoji).
      // Pri kazdej hodnote je aj zariadenie, z ktoreho pochadza — na spoji
      // su dve a bez toho by nebolo jasne, ktoreho konca sa cislo tyka.
      var stitokData = stitkySpojov[String(lk.id)] || [];
      if (stitokData.length) {
        var cislo = function(v) {
          if (v === null || v === undefined) return '\u2014';
          var des = Math.abs(v) >= 100 ? 0 : Math.abs(v) >= 10 ? 1 : 2;
          var s2 = v.toFixed(des);
          if (des > 0) s2 = s2.replace(/\.?0+$/, '');
          return s2.replace('.', ',');
        };
        var esc = function(t) { return String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); };
        // Meno zariadenia sa na stitku NEuvadza — kazda hodnota ma vlastny popis,
        // ktory si pouzivatel nastavi (napr. "signal M.Cetin"), takze meno
        // zariadenia bolo len zbytocna dlzka navyse.
        var riadky = stitokData.map(function(m) {
          return '<div style="white-space:nowrap">' +
            '<span style="color:#64748b">' + esc(m.name) + '</span> ' +
            '<b style="color:#e2e8f0">' + cislo(m.value) + '</b>' +
            (m.unit ? ' <span style="color:#64748b">' + esc(m.unit) + '</span>' : '') +
            '</div>';
        });
        // Kde na ciare stitok sedi. Ked idu dva spoje vedla seba, stitky v
        // strede sa prekryvaju — preto sa da stitok chytit mysou a posunut po
        // ciare. Poloha sa uklada do links.label_pos, takze prezije obnovenie.
        var bodNa = function(t) {
          return [devA.latitude + (devB.latitude - devA.latitude) * t,
                  devA.longitude + (devB.longitude - devA.longitude) * t];
        };
        // Kolmy priemet bodu na usecku A-B. Pocita sa v pixeloch mapy, nie v
        // stupnoch — inak by sa stitok pri vacsich zemepisnych sirkach "lepil"
        // inam, nez kam ho pouzivatel tahal.
        var tZBodu = function(latlng) {
          var pA = map.latLngToLayerPoint(L.latLng(devA.latitude, devA.longitude));
          var pB = map.latLngToLayerPoint(L.latLng(devB.latitude, devB.longitude));
          var pM = map.latLngToLayerPoint(latlng);
          var dx = pB.x - pA.x, dy = pB.y - pA.y;
          var dlzka2 = dx * dx + dy * dy;
          if (!dlzka2) return 0.5;
          var t = ((pM.x - pA.x) * dx + (pM.y - pA.y) * dy) / dlzka2;
          // Okraje sa nechavaju volne, aby stitok nesadol priamo na ikonu zariadenia.
          return Math.max(0.06, Math.min(0.94, t));
        };
        var poz = (lk.label_pos === null || lk.label_pos === undefined) ? 0.5 : Number(lk.label_pos);
        if (!(poz >= 0 && poz <= 1)) poz = 0.5;

        var stitok = L.marker(bodNa(poz), {
          draggable: true,
          autoPan: false,
          zIndexOffset: 500,
          icon: L.divIcon({
            className: '',
            html: '<div style="background:rgba(15,23,42,0.88);border:1px solid ' + color +
                  ';border-radius:6px;padding:4px 7px;font-size:11px;line-height:1.45;' +
                  'font-family:\'Segoe UI\',Arial,sans-serif;box-shadow:0 2px 10px rgba(0,0,0,0.5);' +
                  'transform:translate(-50%,-50%) scale(var(--nw-stitok,1));transform-origin:center;' +
                  'transition:transform .15s;display:inline-block;cursor:grab">' + riadky.join('') + '</div>',
            iconSize: null,
          }),
        });
        (function(capturedLk) {
          // Pocas tahania sa stitok drzi na ciare — pustit sa da kdekolvek,
          // ale sadne vzdy na najblizsi bod prepoja.
          stitok.on('drag', function(ev) {
            stitok.setLatLng(bodNa(tZBodu(ev.latlng)));
          });
          stitok.on('dragend', function() {
            var t = tZBodu(stitok.getLatLng());
            stitok.setLatLng(bodNa(t));
            capturedLk.label_pos = t;   // aby prekreslenie nevratilo stitok spat
            fetch('/api/links/' + capturedLk.id + '/label-pos', {
              method: 'PUT', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ label_pos: t }),
            }).catch(function() {
              setSavingMsg('\u274C Poz\u00edciu \u0161t\u00edtku sa nepodarilo ulo\u017ei\u0165');
              setTimeout(function() { setSavingMsg(null); }, 3000);
            });
          });
          // Pravy klik na stitok otvara to iste menu ako pravy klik na ciaru —
          // stitok casto prekryva prepoj a trafit samotnu ciaru je otrava.
          stitok.on('contextmenu', function(e) {
            L.DomEvent.stopPropagation(e);
            setCtxMenu({ x: e.originalEvent.clientX, y: e.originalEvent.clientY, link: capturedLk });
          });
        })(lk);
        stitok.bindTooltip('Potiahnut\u00edm posunie\u0161 \u0161t\u00edtok po prepoji',
                           { direction: 'top', offset: [0, -14], opacity: 0.9 });
        layer.addLayer(stitok);
      }
    });
    layer.addTo(map);
    linksLayerRef.current = layer;
  }, [showLinks, topo, mapInitTs, stitkySpojov]);

  // Map initialization
  useEffect(function() {
    var L = window.L;
    if (!L || !mapRef.current) return;
    // Save current view BEFORE destroying the map (cleanup already nullified leafletMap.current)
    if (leafletMap.current) {
      savedViewRef.current = { center: leafletMap.current.getCenter(), zoom: leafletMap.current.getZoom() };
      leafletMap.current.remove();
    }
    // Clear stale move-mode ref — old marker objects are destroyed on re-init
    moveModeRef.current = null;
    setMoveHint(false);
    markersRef.current = {};
    siteMarkersRef.current = {};

    // Build set of device IDs that belong to a site (hide from main cluster)
    var siteMemberSet = new Set();
    sites.forEach(function(s) { (s.device_ids || []).forEach(function(id) { siteMemberSet.add(id); }); });

    var sv = savedViewRef.current;
    var initCenter = sv ? [sv.center.lat, sv.center.lng] : [48.7, 19.5];
    var initZoom   = sv ? sv.zoom : 7;
    var map = L.map(mapRef.current, { zoomControl: true }).setView(initCenter, initZoom);
    leafletMap.current = map;

    // Stitky na spojoch su divIcon, takze maju velkost v pixeloch obrazovky a pri
    // oddialeni mapy by zostali rovnako velke — mapa by sa nimi zahltila.
    // Mierku preto drzime v CSS premennej na kontajneri mapy a menime ju pri zoome;
    // stitky ju cez var() pouziju samy, netreba ich prekreslovat.
    // Pod urcity zoom sa scale(0) postara o to, ze zmiznu uplne — v takom priblizeni
    // su aj tak necitatelne.
    function prepocitajMierkuStitkov() {
      var z = map.getZoom();
      var k = z <= 11 ? 0 : Math.max(0.55, Math.min(1.3, 1 + (z - 15) * 0.12));
      map.getContainer().style.setProperty('--nw-stitok', String(k));
    }
    prepocitajMierkuStitkov();
    map.on('zoomend', prepocitajMierkuStitkov);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap', maxZoom: 18,
    }).addTo(map);

    var withCoords = devices.filter(function(d) { return d.latitude && d.longitude && !siteMemberSet.has(d.id); });

    // MarkerClusterGroup — groups devices at the same/nearby location, spiderfies on click
    var cluster = L.markerClusterGroup({
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      disableClusteringAtZoom: 18,
      maxClusterRadius: 40,
      iconCreateFunction: function(c) {
        var n = c.getChildCount();
        var markers = c.getAllChildMarkers();
        // _sitePartial = true for partial sites → counts as BOTH up AND down → forces orange
        var upCount   = markers.filter(function(m) { return m._deviceStatus === true  || m._sitePartial; }).length;
        var downCount = markers.filter(function(m) { return m._deviceStatus === false || m._sitePartial; }).length;
        // Green = all UP, Red = all DOWN (or none known UP), Orange = mixed
        var bg, glow, textColor;
        if (upCount === n && downCount === 0) {
          bg = '#16a34a'; glow = 'rgba(22,163,74,0.7)'; textColor = '#dcfce7';
        } else if (upCount === 0 && downCount > 0) {
          bg = '#dc2626'; glow = 'rgba(220,38,38,0.7)'; textColor = '#fee2e2';
        } else if (upCount > 0 && downCount > 0) {
          bg = '#ea580c'; glow = 'rgba(234,88,12,0.7)'; textColor = '#ffedd5';
        } else {
          bg = '#475569'; glow = 'rgba(71,85,105,0.5)'; textColor = '#e2e8f0';
        }
        return L.divIcon({ className: '', iconSize: [36, 36], iconAnchor: [18, 18],
          html: '<div style="width:36px;height:36px;border-radius:50%;background:' + bg + ';border:2px solid rgba(255,255,255,0.25);display:flex;align-items:center;justify-content:center;font-weight:700;font-size:13px;color:' + textColor + ';box-shadow:0 0 12px ' + glow + ';">' + n + '</div>'
        });
      }
    });
    clusterRef.current = cluster; // store for startSiteMoveMode

    withCoords.forEach(function(d) {
      var inMaint = d.maintenance_until && new Date(d.maintenance_until + 'Z') > new Date();
      var color = inMaint ? '#3b82f6' : d.is_reachable === true ? '#22c55e' : d.is_reachable === false ? '#ef4444' : '#64748b';
      var icon = L.divIcon({
        className: '',
        html: '<div style="width:16px;height:16px;border-radius:50%;background:' + color + ';border:2px solid #fff;box-shadow:0 0 8px ' + color + ';"></div>',
        iconSize: [16, 16], iconAnchor: [8, 8],
      });
      var ms = d.response_time_ms ? parseFloat(d.response_time_ms).toFixed(1) + ' ms' : 'N/A';
      var status = inMaint ? '\uD83D\uDEE0 \u00DAdr\u017eba' : d.is_reachable === true ? 'UP' : d.is_reachable === false ? 'DOWN' : '?';
      // Format down_since as human-readable duration
      var downSinceHtml = '';
      if (d.is_reachable === false && d.down_since) {
        var diffMs = Date.now() - new Date(d.down_since).getTime();
        var diffMin = Math.floor(diffMs / 60000);
        var diffH   = Math.floor(diffMin / 60);
        var diffD   = Math.floor(diffH / 24);
        var durStr;
        if (diffD > 0)       durStr = diffD + 'd ' + (diffH % 24) + 'h';
        else if (diffH > 0)  durStr = diffH + 'h ' + (diffMin % 60) + 'min';
        else                 durStr = diffMin + ' min';
        var sinceDate = new Date(d.down_since).toLocaleString('sk-SK', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' });
        downSinceHtml = '<br><span style="color:#fca5a5;font-size:12px">\u23F1 DOWN ' + durStr + ' (od ' + sinceDate + ')</span>';
      }
      var marker = L.marker([d.latitude, d.longitude], { icon: icon, draggable: false });
      marker._deviceStatus = d.is_reachable; // used by cluster iconCreateFunction
      marker.bindPopup(
          '<b>' + d.name + '</b><br>' + d.ip_address + '<br>' +
          '<span style="color:' + color + '">' + status + '</span>' +
          (d.response_time_ms ? ' &mdash; ' + ms : '') +
          downSinceHtml +
          (d.location ? '<br><small>' + d.location + '</small>' : '') +
          '<br><small style="color:#64748b">Prav\u00E9 tla\u010Didlo \u2192 mo\u017Enosti</small>'
        );

      // right-click on marker → context menu with device
      marker.on('contextmenu', function(e) {
        L.DomEvent.stopPropagation(e);
        e.originalEvent.preventDefault();
        setCtxMenu({ x: e.originalEvent.clientX, y: e.originalEvent.clientY, device: d, lat: d.latitude, lng: d.longitude });
      });

      marker.on('dragend', function(e) {
        var latlng = e.target.getLatLng();
        var lat = Math.round(latlng.lat * 1e5) / 1e5;
        var lng = Math.round(latlng.lng * 1e5) / 1e5;
        // Keep dragging enabled — user can reposition repeatedly until they click "Zrušiť"
        setSavingMsg('\u23F3 Ukladam ' + d.name + '...');
        fetch('/api/devices/' + d.id, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ latitude: lat, longitude: lng }),
        }).then(function(r) { if (!r.ok) return r.json().then(function(e) {
      throw new Error((e && e.detail) || ''); }); return r.json(); })
          .then(function() {
            setSavingMsg('\u2705 ' + d.name + ' ulozeny (' + lat.toFixed(4) + ', ' + lng.toFixed(4) + ')');
            // Do NOT call onRefresh here — that would re-init the map and break drag mode
            setTimeout(function() { setSavingMsg(null); }, 2000);
          }).catch(function() {
            setSavingMsg('\u274C Chyba pri ukladani!');
            marker.setLatLng([d.latitude, d.longitude]);
            setTimeout(function() { setSavingMsg(null); }, 3000);
          });
      });

      // left-click in Draw Link Mode
      marker.on('click', function(e) {
        if (!mapDrawLinkModeRef.current) return;
        L.DomEvent.stopPropagation(e);
        handleMapPickDevice(d);
      });

      cluster.addLayer(marker);
      markersRef.current[d.id] = marker;
    });

    cluster.addTo(map);

    // ── Site markers ──
    var siteStatusColors = { up: '#16a34a', down: '#dc2626', partial: '#ea580c', unknown: '#475569' };
    sites.forEach(function(site) {
      var bg = siteStatusColors[site.status] || siteStatusColors.unknown;
      var n  = (site.device_ids || []).length;
      var siteIcon = L.divIcon({
        className: '',
        iconSize: [44, 44], iconAnchor: [22, 22],
        html: '<div style="width:44px;height:44px;border-radius:10px;background:' + bg + ';border:2px solid rgba(255,255,255,0.3);display:flex;flex-direction:column;align-items:center;justify-content:center;box-shadow:0 0 14px ' + bg + ';cursor:pointer;">' +
              '<span style="font-size:18px;line-height:1">\uD83C\uDFE2</span>' +
              '<span style="font-size:10px;font-weight:700;color:#fff;line-height:1.2">' + n + '</span>' +
              '</div>'
      });
      var siteMarker = L.marker([site.latitude, site.longitude], { icon: siteIcon, draggable: true });
      siteMarker.bindTooltip('<b>' + site.name + '</b><br>' + n + ' zariadení — ' + (site.description || '') + '<br><small style="color:#94a3b8">Klik = otvoriť | Pravý klik = možnosti</small>', { direction: 'top', offset: [0, -24] });
      siteMarker.on('dragend', function(e) {
        var latlng = e.target.getLatLng();
        var lat = Math.round(latlng.lat * 1e5) / 1e5;
        var lng = Math.round(latlng.lng * 1e5) / 1e5;
        setSavingMsg('\u23F3 Ukladám ' + site.name + '...');
        fetch('/api/map/sites/' + site.id, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ latitude: lat, longitude: lng }),
        }).then(function(r) { if (!r.ok) return r.json().then(function(e) {
      throw new Error((e && e.detail) || ''); }); return r.json(); })
          .then(function() {
            setSavingMsg('\u2705 ' + site.name + ' uložený (' + lat.toFixed(4) + ', ' + lng.toFixed(4) + ')');
            setTimeout(function() { setSavingMsg(null); }, 2000);
          }).catch(function() {
            setSavingMsg('\u274C Chyba pri ukladaní!');
            setTimeout(function() { setSavingMsg(null); }, 2000);
          });
      });
      siteMarker.on('click', function(e) {
        L.DomEvent.stopPropagation(e);
        closeCtx();
        if (mapDrawLinkModeRef.current) {
          // In draw-link mode: show device picker instead of opening site panel
          setMapSiteDevPicker({ site: site, x: e.originalEvent.clientX, y: e.originalEvent.clientY });
          return;
        }
        setCurrentSite(site);
      });
      siteMarker.on('contextmenu', function(e) {
        L.DomEvent.stopPropagation(e);
        e.originalEvent.preventDefault();
        setCtxMenu({ x: e.originalEvent.clientX, y: e.originalEvent.clientY, site: site, lat: site.latitude, lng: site.longitude });
      });
      // Contribute to cluster colour calculation
      siteMarker._deviceStatus = site.status === 'up' ? true : site.status === 'down' ? false : null;
      siteMarker._sitePartial  = site.status === 'partial'; // partial → cluster goes orange
      siteMarker._isSite = true;
      // Add to cluster so it merges with nearby device markers at low zoom
      cluster.addLayer(siteMarker);
      siteMarkersRef.current[site.id] = siteMarker;
    });

    // right-click on empty map → context menu without device
    map.on('contextmenu', function(e) {
      e.originalEvent.preventDefault();
      setCtxMenu({ x: e.originalEvent.clientX, y: e.originalEvent.clientY, device: null, lat: e.latlng.lat, lng: e.latlng.lng });
    });

    // left-click on map → close any open context menu + close site panel
    map.on('click', function() { closeCtx(); setCurrentSite(null); });

    if (withCoords.length > 1 && !sv) {
      map.fitBounds(L.latLngBounds(withCoords.map(function(d) { return [d.latitude, d.longitude]; })), { padding: [40, 40] });
    }
    // Persist zoom/center on every user interaction so savedViewRef stays current
    map.on('moveend zoomend', function() {
      savedViewRef.current = { center: map.getCenter(), zoom: map.getZoom() };
    });
    // Signal to links effect that map is ready
    setMapInitTs(function(t) { return t + 1; });
    return function() {
      if (leafletMap.current) {
        savedViewRef.current = { center: leafletMap.current.getCenter(), zoom: leafletMap.current.getZoom() };
        leafletMap.current.remove();
        leafletMap.current = null;
      }
    };
  }, [devices, sites]);

  function handleAdd() {
    if (!formData.name || !formData.ip) { setSavingMsg('\u274C Meno a IP su povinne!'); setTimeout(function() { setSavingMsg(null); }, 3000); return; }
    setSavingMsg('\u23F3 Pridavam zariadenie...');
    var targetSiteId = addForm.siteId || null;
    fetch('/api/devices', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: formData.name, ip_address: formData.ip, device_type: formData.type || 'router', location: formData.location, latitude: addForm.lat, longitude: addForm.lng }),
    }).then(function(r) { if (!r.ok) return r.json().then(function(e) { throw e; }); return r.json(); })
      .then(function(newDev) {
        var devId = newDev.id || (newDev.device && newDev.device.id);
        if (targetSiteId && devId) {
          // Add new device to the site automatically
          var site = sites.find(function(s) { return s.id === targetSiteId; });
          var existingIds = site ? (site.device_ids || []) : [];
          return fetch('/api/map/sites/' + targetSiteId, {
            method: 'PATCH', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ device_ids: existingIds.concat([devId]) }),
          }).then(function(r) { if (!r.ok) return r.json().then(function(e) {
      throw new Error((e && e.detail) || ''); }); return r.json(); })
            .then(function() {
              setSavingMsg('\u2705 ' + formData.name + ' pridan\u00e9 do situ!');
              setAddForm(null);
              refreshSites();
              if (onRefresh) onRefresh();
              setTimeout(function() { setSavingMsg(null); }, 3000);
            });
        }
        setSavingMsg('\u2705 ' + formData.name + ' pridane!');
        setAddForm(null);
        if (onRefresh) onRefresh();
        setTimeout(function() { setSavingMsg(null); }, 3000);
      })
      .catch(function(e) {
        var msg = (e && e.detail === 'IP address already exists')
          ? '\u274C IP adresa ' + formData.ip + ' je u\u017E zaregistrovan\u00E1 v syst\u00E9me!'
          : '\u274C Chyba pri prid\u00E1van\u00ED!';
        setSavingMsg(msg); setTimeout(function() { setSavingMsg(null); }, 5000);
      });
  }

  function handleEditSave() {
    if (!formData.name || !formData.ip) { setSavingMsg('\u274C Meno a IP su povinne!'); setTimeout(function() { setSavingMsg(null); }, 3000); return; }
    setSavingMsg('\u23F3 Ukladam zmeny...');
    fetch('/api/devices/' + editForm.id, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: formData.name, ip_address: formData.ip, device_type: formData.type || 'router', location: formData.location }),
    }).then(function(r) { if (!r.ok) return r.json().then(function(e) { throw e; }); return r.json(); })
      .then(function() { setSavingMsg('\u2705 ' + formData.name + ' ulozeny!'); setEditForm(null); if (onRefresh) onRefresh(); setTimeout(function() { setSavingMsg(null); }, 3000); })
      .catch(function(e) {
        var msg = (e && e.detail === 'IP address already exists')
          ? '\u274C IP adresa ' + formData.ip + ' je u\u017E zaregistrovan\u00E1 v syst\u00E9me!'
          : '\u274C Chyba pri ukladan\u00ED!';
        setSavingMsg(msg); setTimeout(function() { setSavingMsg(null); }, 5000);
      });
  }

  function refreshEditIfaces() {
    fetch('/api/devices/' + editFormId + '/interfaces')
      .then(function(r) { return r.json(); })
      .then(function(data) { setEditIfaces(data.interfaces || []); });
  }

  function addEditIface() {
    if (!newIfaceDat.name) { setSavingMsg('\u274C Meno interfacu je povinne!'); setTimeout(function() { setSavingMsg(null); }, 3000); return; }
    fetch('/api/devices/' + editFormId + '/interfaces', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newIfaceDat.name, interface_type: newIfaceDat.type, speed_mbps: newIfaceDat.speed ? parseInt(newIfaceDat.speed) : null, ip_address: newIfaceDat.ip || null }),
    }).then(function(r) { if (!r.ok) return r.json().then(function(e) {
      throw new Error((e && e.detail) || ''); }); return r.json(); })
      .then(function() { setNewIfaceDat({ name: '', type: 'ethernet', speed: '', ip: '' }); refreshEditIfaces(); })
      .catch(function() { setSavingMsg('\u274C Interface uz existuje!'); setTimeout(function() { setSavingMsg(null); }, 3000); });
  }

  function delEditIface(ifaceId) {
    fetch('/api/interfaces/' + ifaceId, { method: 'DELETE' })
      .then(function() { refreshEditIfaces(); });
  }

  function patchEditIface() {
    if (!editingEditIface || !editingEditIface.name) { setSavingMsg('\u274C Meno je povinne!'); setTimeout(function() { setSavingMsg(null); }, 3000); return; }
    fetch('/api/interfaces/' + editingEditIface.id, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: editingEditIface.name, interface_type: editingEditIface.type, speed_mbps: editingEditIface.speed ? parseInt(editingEditIface.speed) : null, ip_address: editingEditIface.ip || null }),
    }).then(function(r) { if (!r.ok) return r.json().then(function(e) {
      throw new Error((e && e.detail) || ''); }); return r.json(); })
      .then(function() { setEditingEditIface(null); refreshEditIfaces(); })
      .catch(function() { setSavingMsg('\u274C Chyba pri ukladani!'); setTimeout(function() { setSavingMsg(null); }, 3000); });
  }

  function refreshSites() {
    fetch('/api/map/sites').then(function(r) { return r.json(); }).then(function(data) { setSites(data.sites || []); });
  }

  function saveSite() {
    if (!siteForm.name) { setSavingMsg('\u274C N\u00E1zov situ je povinn\u00FD!'); setTimeout(function() { setSavingMsg(null); }, 3000); return; }
    var isEdit = siteModal && siteModal.mode === 'edit';
    var url = isEdit ? '/api/map/sites/' + siteModal.site.id : '/api/map/sites';
    var method = isEdit ? 'PATCH' : 'POST';
    var body = isEdit
      ? { name: siteForm.name, description: siteForm.description, device_ids: siteForm.device_ids,
          latitude: parseFloat(siteForm.lat) || siteModal.lat, longitude: parseFloat(siteForm.lng) || siteModal.lng }
      : { name: siteForm.name, description: siteForm.description, latitude: siteModal.lat, longitude: siteModal.lng, device_ids: siteForm.device_ids };
    setSavingMsg('\u23F3 Uklad\u00E1m site...');
    fetch(url, { method: method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      .then(function(r) { if (!r.ok) throw new Error(); return r.json(); })
      .then(function() { setSavingMsg('\u2705 Site ulo\u017Een\u00FD!'); setSiteModal(null); refreshSites(); setTimeout(function() { setSavingMsg(null); }, 3000); })
      .catch(function() { setSavingMsg('\u274C Chyba pri ukladan\u00ED situ!'); setTimeout(function() { setSavingMsg(null); }, 3000); });
  }

  function deleteSite(siteId) {
    if (!window.confirm('Naozaj zmazať tento site?')) return;
    fetch('/api/map/sites/' + siteId, { method: 'DELETE' })
      .then(function() { setCurrentSite(null); refreshSites(); });
  }

  // ── Edit-device modal helpers (map scope) ──────────────────────────────────
  function openEditDevModal(device) {
    setEditDevTab('basic');
    setEditDevForm({ name: device.name, ip: device.ip_address || '', type: device.device_type || 'router', location: device.location || '', web_url: device.web_url || '' });
    setEditDevSnmp({ enabled: !!device.snmp_enabled, community: device.snmp_community || 'public', port: device.snmp_port || 161, version: device.snmp_version || 'v2c', show_map: device.snmp_show_map !== false, show_topo: device.snmp_show_topo !== false, show_info: device.snmp_show_info !== false, v3_user: device.snmp_v3_user || '', v3_level: device.snmp_v3_level || 'noAuthNoPriv', v3_auth_proto: device.snmp_v3_auth_proto || 'SHA', v3_auth_pass: device.snmp_v3_auth_pass || '', v3_priv_proto: device.snmp_v3_priv_proto || 'AES', v3_priv_pass: device.snmp_v3_priv_pass || '' });
    setEditDevNotes({ description: device.description || '', notes: device.notes || '' });
    setEditDevWifi({ azimuth: device.wifi_azimuth != null ? String(device.wifi_azimuth) : '', tilt: device.wifi_tilt != null ? String(device.wifi_tilt) : '', polarization: device.wifi_polarization || '', frequency: device.wifi_frequency || '', ssid: device.wifi_ssid || '' });
    setEditDevPing(device.ping_interval || 60);
    setNewOidForm({ name: '', oid: '', unit: '', multiplier: '1', category: 'general' });
    setSnmpLatest([]); setSnmpHistory([]);
    setSablonaNahlad(null); setSablonaMsg(null);
    setVybranaSablona(device.snmp_template_id ? String(device.snmp_template_id) : '');
    fetch('/api/snmp/templates').then(function(r) { return r.json(); })
      .then(function(d) { setSnmpSablony(d.templates || []); })
      .catch(function() { setSnmpSablony([]); });
    setMapAlertRules([]); setMapNewAlert({ name: '', metric: 'ping', oid_id: '', operator: '>', threshold: '', ping_minutes: 10, notify_emails: '', notify_phones: '' });
    setMapDevVpn(null); setMapDevVpnLoading(false); setMapDevVpnMsg(null); setMapDevVpnConfigShown(false);
    Promise.all([
      fetch('/api/devices/' + device.id + '/interfaces').then(function(r) { return r.json(); }),
      fetch('/api/devices/' + device.id + '/snmp/oids').then(function(r) { return r.json(); }),
      fetch('/api/devices/' + device.id + '/snmp/latest').then(function(r) { return r.json(); }),
      fetch('/api/devices/' + device.id + '/alert-rules').then(function(r) { return r.json(); }),
    ]).then(function(res) {
      setEditDevModal({ device: device, ifaces: res[0].interfaces || [] });
      setEditDevOids(res[1].oids || []);
      setSnmpLatest(res[2].latest || []);
      setMapAlertRules(res[3].rules || []);
      setMapNewIface({ name: '', type: 'ethernet', speed: '', ip: '' }); setMapEditingIface(null);
    }).catch(function() {
      fetch('/api/devices/' + device.id + '/interfaces').then(function(r) { return r.json(); })
        .then(function(data) { setEditDevModal({ device: device, ifaces: data.interfaces || [] }); });
    });
  }
  // --- Sablony SNMP ---
  // Nahlad najprv ukaze, co sa prida a aky index radioveho rozhrania sa zistil.
  // Index sa NEberie zo sablony - na kazdom zariadeni je iny (ath0 = 5, 7 aj 10).
  function nahladSablony() {
    if (!editDevModal || !vybranaSablona) return;
    setSablonaMsg('Zis\u0165ujem...'); setSablonaNahlad(null);
    fetch('/api/devices/' + editDevModal.device.id + '/snmp/apply-template', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ template_id: parseInt(vybranaSablona), preview: true })
    }).then(function(r) { return r.json(); }).then(function(d) {
      setSablonaMsg(null);
      if (d.ok === false) { setSablonaMsg('\u274C ' + (d.error || 'Nepodarilo sa')); return; }
      setSablonaNahlad(d);
    }).catch(function() { setSablonaMsg('\u274C Chyba spojenia'); });
  }

  function priradSablonu() {
    if (!editDevModal || !vybranaSablona) return;
    setSablonaMsg('Priradzujem...');
    fetch('/api/devices/' + editDevModal.device.id + '/snmp/apply-template', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ template_id: parseInt(vybranaSablona), preview: false })
    }).then(function(r) { return r.json(); }).then(function(d) {
      if (d.ok === false) { setSablonaMsg('\u274C ' + (d.error || 'Nepodarilo sa')); return; }
      setSablonaMsg('\u2705 Pridan\u00fdch ' + d.added + ' OID-ov' +
                    (d.skipped ? ', ' + d.skipped + ' u\u017e existovalo' : '') +
                    (d.radio_index != null ? ' (r\u00e1dio = index ' + d.radio_index + ')' : ''));
      setSablonaNahlad(null);
      refreshMapEditDevOids();
      setTimeout(function() { setSablonaMsg(null); }, 6000);
    }).catch(function() { setSablonaMsg('\u274C Chyba spojenia'); });
  }

  function refreshMapEditDevOids() {
    if (!editDevModal) return;
    fetch('/api/devices/' + editDevModal.device.id + '/snmp/oids').then(function(r) { return r.json(); }).then(function(d) { setEditDevOids(d.oids || []); });
  }
  function addMapEditDevOid() {
    if (!newOidForm.name || !newOidForm.oid) { setSavingMsg('\u274C Meno a OID s\u00FA povinn\u00E9!'); setTimeout(function() { setSavingMsg(null); }, 3000); return; }
    fetch('/api/devices/' + editDevModal.device.id + '/snmp/oids', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: newOidForm.name, oid: newOidForm.oid, unit: newOidForm.unit || null, multiplier: parseFloat(newOidForm.multiplier) || 1, category: newOidForm.category }) })
      .then(function(r) { if (!r.ok) throw new Error(); return r.json(); })
      .then(function() { setNewOidForm({ name: '', oid: '', unit: '', multiplier: '1', category: 'general' }); refreshMapEditDevOids(); })
      .catch(function() { setSavingMsg('\u274C Chyba pri prid\u00E1van\u00ED OID!'); setTimeout(function() { setSavingMsg(null); }, 3000); });
  }
  function delMapEditDevOid(oidId) {
    fetch('/api/snmp/oids/' + oidId, { method: 'DELETE' }).then(function() { refreshMapEditDevOids(); });
  }
  function updateMapOid(oidId, fields) {
    fetch('/api/snmp/oids/' + oidId, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(fields) })
      .then(function(r) { if (!r.ok) throw new Error(); return r.json(); })
      .then(function() { setMapEditingOid(null); refreshMapEditDevOids(); })
      .catch(function() { setSavingMsg('\u274C Chyba pri ukladan\u00ED OID!'); setTimeout(function() { setSavingMsg(null); }, 3000); });
  }
  function scanMapOids() {
    if (!editDevModal) return;
    setMapOidScanning(true); setMapOidResults(null);
    fetch('/api/devices/' + editDevModal.device.id + '/snmp/discover-oids', { method: 'POST' })
      .then(function(r) { return r.json(); })
      .then(function(d) { setMapOidResults(d.oids || []); setMapOidScanning(false); })
      .catch(function() { setMapOidResults([]); setMapOidScanning(false); });
  }
  function addMapOidDirect(entry) {
    if (!editDevModal) return;
    fetch('/api/devices/' + editDevModal.device.id + '/snmp/oids', { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: entry.name, oid: entry.oid, unit: entry.unit || null, multiplier: entry.multiplier || 1, category: entry.category }) })
      .then(function(r) { if (!r.ok) throw new Error(); return r.json(); })
      .then(function() { refreshMapEditDevOids(); setMapOidResults(function(prev) { return (prev || []).filter(function(o) { return o.oid !== entry.oid; }); }); })
      .catch(function() { setSavingMsg('\u274C Chyba pri pridávaní OID!'); setTimeout(function() { setSavingMsg(null); }, 3000); });
  }
  function setMapMaintenance(minutes) {
    fetch('/api/devices/' + editDevModal.device.id + '/maintenance', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ duration_minutes: minutes }) })
      .then(function(r) { if (!r.ok) throw new Error(); })
      .then(function() {
        var newUntil = minutes === -1 ? null : minutes === 0 ? '9999-12-31T00:00:00' : new Date(Date.now() + minutes * 60000).toISOString();
        setEditDevModal(function(m) { return Object.assign({}, m, { device: Object.assign({}, m.device, { maintenance_until: newUntil }) }); });
        setSavingMsg(minutes === -1 ? '\u2705 \u00DAdr\u017eba zru\u0161en\u00E1' : '\u2705 \u00DAdr\u017eba nastaven\u00E1');
        setTimeout(function() { setSavingMsg(null); }, 3000);
        if (onRefresh) onRefresh();
      })
      .catch(function() { setSavingMsg('\u274C Chyba!'); setTimeout(function() { setSavingMsg(null); }, 3000); });
  }
  function loadMapSnmpHistory(oidId, hours) {
    fetch('/api/devices/' + editDevModal.device.id + '/snmp/values?oid_id=' + oidId + '&hours=' + (hours || 24)).then(function(r) { return r.json(); }).then(function(d) { setSnmpHistory(d.values || []); });
  }
  function refreshMapEditDevIfaces() {
    if (!editDevModal) return;
    fetch('/api/devices/' + editDevModal.device.id + '/interfaces').then(function(r) { return r.json(); }).then(function(d) { setEditDevModal(function(m) { return Object.assign({}, m, { ifaces: d.interfaces || [] }); }); });
  }
  function discoverMapSnmpIfaces() {
    if (!editDevModal) return;
    setMapSnmpDiscovering(true); setMapSnmpDiscovered(null);
    fetch('/api/devices/' + editDevModal.device.id + '/snmp/discover-interfaces', { method: 'POST' })
      .then(function(r) { if (!r.ok) throw new Error(); return r.json(); })
      .then(function(d) { setMapSnmpDiscovering(false); setMapSnmpDiscovered(d.interfaces || []); })
      .catch(function() { setMapSnmpDiscovering(false); setMapSnmpDiscovered([]); setSavingMsg('\u274C SNMP chyba \u2013 skontroluj community/IP!'); setTimeout(function() { setSavingMsg(null); }, 4000); });
  }
  function addMapSnmpIface(iface) {
    fetch('/api/devices/' + editDevModal.device.id + '/interfaces', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: iface.name, interface_type: iface.interface_type, speed_mbps: iface.speed_mbps || null, ip_address: null }) })
      .then(function(r) { if (!r.ok) throw new Error(); return r.json(); })
      .then(function() { refreshMapEditDevIfaces(); })
      .catch(function() { setSavingMsg('\u274C Interface u\u017E existuje!'); setTimeout(function() { setSavingMsg(null); }, 3000); });
  }
  function saveMapEditDev() {
    if (!editDevForm.name || !editDevForm.ip) { setSavingMsg('\u274C Meno a IP s\u00FA povinn\u00E9!'); setTimeout(function() { setSavingMsg(null); }, 3000); return; }
    var payload = { name: editDevForm.name, ip_address: editDevForm.ip, device_type: editDevForm.type, location: editDevForm.location || null, description: editDevNotes.description || null, notes: editDevNotes.notes || null, snmp_enabled: editDevSnmp.enabled, snmp_community: editDevSnmp.community, snmp_port: editDevSnmp.port, snmp_version: editDevSnmp.version, snmp_show_map: editDevSnmp.show_map, snmp_show_topo: editDevSnmp.show_topo, snmp_show_info: editDevSnmp.show_info, ping_interval: parseInt(editDevPing) || 60, wifi_azimuth: editDevWifi.azimuth !== '' ? parseFloat(editDevWifi.azimuth) : null, wifi_tilt: editDevWifi.tilt !== '' ? parseFloat(editDevWifi.tilt) : null, wifi_polarization: editDevWifi.polarization || null, wifi_frequency: editDevWifi.frequency || null, wifi_ssid: editDevWifi.ssid || null, web_url: editDevForm.web_url || null, snmp_v3_user: editDevSnmp.v3_user || null, snmp_v3_level: editDevSnmp.v3_level || 'noAuthNoPriv', snmp_v3_auth_proto: editDevSnmp.v3_auth_proto || 'SHA', snmp_v3_auth_pass: editDevSnmp.v3_auth_pass || null, snmp_v3_priv_proto: editDevSnmp.v3_priv_proto || 'AES', snmp_v3_priv_pass: editDevSnmp.v3_priv_pass || null };
    fetch('/api/devices/' + editDevModal.device.id, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
      .then(function(r) { if (!r.ok) return r.json().then(function(e) { throw e; }); return r.json(); })
      .then(function() { setSavingMsg('\u2705 Zariadenie ulo\u017Een\u00E9!'); setTimeout(function() { setSavingMsg(null); }, 3000); onRefresh(); setEditDevModal(null); })
      .catch(function(e) {
        var msg = (e && e.detail === 'IP address already exists')
          ? '\u274C IP adresa ' + editDevForm.ip + ' je u\u017E zaregistrovan\u00E1 v syst\u00E9me!'
          : '\u274C Chyba pri ukl\u00E1dan\u00ED!';
        setSavingMsg(msg); setTimeout(function() { setSavingMsg(null); }, 5000);
      });
  }
  function addMapEditDevIface() {
    if (!mapNewIface.name) { setSavingMsg('\u274C Meno interfacu je povinn\u00E9!'); setTimeout(function() { setSavingMsg(null); }, 3000); return; }
    fetch('/api/devices/' + editDevModal.device.id + '/interfaces', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: mapNewIface.name, interface_type: mapNewIface.type, speed_mbps: mapNewIface.speed ? parseInt(mapNewIface.speed) : null, ip_address: mapNewIface.ip || null }) })
      .then(function(r) { if (!r.ok) throw new Error(); return r.json(); })
      .then(function() { setMapNewIface({ name: '', type: 'ethernet', speed: '', ip: '' }); refreshMapEditDevIfaces(); })
      .catch(function() { setSavingMsg('\u274C Interface u\u017E existuje!'); setTimeout(function() { setSavingMsg(null); }, 3000); });
  }
  function delMapEditDevIface(id) {
    fetch('/api/interfaces/' + id, { method: 'DELETE' }).then(function() { refreshMapEditDevIfaces(); });
  }
  function patchMapEditDevIface() {
    if (!mapEditingIface || !mapEditingIface.name) { setSavingMsg('\u274C Meno je povinn\u00E9!'); setTimeout(function() { setSavingMsg(null); }, 3000); return; }
    fetch('/api/interfaces/' + mapEditingIface.id, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: mapEditingIface.name, interface_type: mapEditingIface.type, speed_mbps: mapEditingIface.speed ? parseInt(mapEditingIface.speed) : null, ip_address: mapEditingIface.ip || null }) })
      .then(function(r) { if (!r.ok) throw new Error(); return r.json(); })
      .then(function() { setMapEditingIface(null); refreshMapEditDevIfaces(); })
      .catch(function() { setSavingMsg('\u274C Chyba pri ukl\u00E1dan\u00ED!'); setTimeout(function() { setSavingMsg(null); }, 3000); });
  }
  // ───────────────────────────────────────────────────────────────────────────

  var iStyle = { width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', padding: '8px 10px', color: '#e2e8f0', fontSize: '13px', boxSizing: 'border-box' };
  var lStyle = { fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '4px', textTransform: 'uppercase' };
  var withCoords = devices.filter(function(d) { return d.latitude && d.longitude; });
  var noCoords   = devices.filter(function(d) { return !d.latitude || !d.longitude; });

  // Reusable form fields block
  function renderFormFields() {
    return CE('div', null,
      CE('div', { style: { marginBottom: '12px' } },
        CE('label', { style: lStyle }, 'Nazov *'),
        CE('input', { type: 'text', value: formData.name, placeholder: 'napr. Router Kosice', style: iStyle, onChange: function(ev) { setFormData(function(f) { return Object.assign({}, f, { name: ev.target.value }); }); } })
      ),
      CE('div', { style: { marginBottom: '12px' } },
        CE('label', { style: lStyle }, 'IP Adresa *'),
        CE('input', { type: 'text', value: formData.ip, placeholder: 'napr. 192.168.1.1', style: iStyle, onChange: function(ev) { setFormData(function(f) { return Object.assign({}, f, { ip: ev.target.value }); }); } })
      ),
      CE('div', { style: { marginBottom: '12px' } },
        CE('label', { style: lStyle }, 'Typ'),
        CE('select', { value: formData.type, style: iStyle, onChange: function(ev) { setFormData(function(f) { return Object.assign({}, f, { type: ev.target.value }); }); } },
          CE('option', { value: 'router' }, 'Router'), CE('option', { value: 'switch' }, 'Switch'),
          CE('option', { value: 'server' }, 'Server'), CE('option', { value: 'ap' }, 'Access Point'),
          CE('option', { value: 'olt' }, 'OLT (Optical Line Terminal)'), CE('option', { value: 'onu' }, 'ONU (Optical Network Unit)'),
          CE('option', { value: 'other' }, 'Ine')
        )
      ),
      CE('div', { style: { marginBottom: '20px' } },
        CE('label', { style: lStyle }, 'Lokalita'),
        CE('input', { type: 'text', value: formData.location, placeholder: 'napr. Kosice - ul. Letna', style: iStyle, onChange: function(ev) { setFormData(function(f) { return Object.assign({}, f, { location: ev.target.value }); }); } })
      )
    );
  }

  var modalBox = { background: '#1e293b', borderRadius: '12px', padding: '28px', width: '360px', boxShadow: '0 20px 60px rgba(0,0,0,0.6)', border: '1px solid #334155' };
  var modalOv  = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 };
  var btnCancel = { flex: 1, background: '#334155', color: '#94a3b8', border: 'none', borderRadius: '6px', padding: '10px', cursor: 'pointer', fontSize: '13px' };
  var btnPrimary = { flex: 2, background: '#1e40af', color: '#fff', border: 'none', borderRadius: '6px', padding: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' };

  // Context menu items depending on what was right-clicked
  var ctxItems = !ctxMenu ? []
    : ctxMenu.link ? [
      { icon: '\u270F\uFE0F', label: 'Editova\u0165 spoj', action: function() { openMapEditLink(ctxMenu.link); } },
      { icon: '\uD83D\uDCCA', label: '\u00dadaje na spoji', action: function() { otvorStitokModal(ctxMenu.link); } },
      { icon: '\uD83D\uDDD1\uFE0F', label: 'Vymaza\u0165 spoj', action: function() { deleteMapLink(ctxMenu.link); } },
    ]
    : ctxMenu.site ? [
      { icon: '\uD83C\uDFE2', label: 'Otvori\u0165 Site panel', action: function() { setCurrentSite(ctxMenu.site); closeCtx(); } },
      { icon: '\u2795', label: 'Prida\u0165 zariadenie', action: function() {
        setAddForm({ lat: ctxMenu.site.latitude, lng: ctxMenu.site.longitude, siteId: ctxMenu.site.id });
        setFormData({ name: '', ip: '', type: 'router', location: '' });
        closeCtx();
      }},
      { icon: '\u270F\uFE0F', label: 'Upravi\u0165 Site', action: function() {
        setSiteForm({ name: ctxMenu.site.name, description: ctxMenu.site.description || '', device_ids: ctxMenu.site.device_ids || [], lat: ctxMenu.site.latitude, lng: ctxMenu.site.longitude });
        setSiteModal({ mode: 'edit', site: ctxMenu.site, lat: ctxMenu.site.latitude, lng: ctxMenu.site.longitude });
        closeCtx();
      }},
      { icon: '\uD83D\uDCCD', label: 'Upravi\u0165 polohu', action: function() { startSiteMoveMode(ctxMenu.site); closeCtx(); } },
      { icon: '\uD83D\uDDD1\uFE0F', label: 'Zma\u017Ea\u0165 Site', action: function() { deleteSite(ctxMenu.site.id); closeCtx(); } },
    ]
    : ctxMenu.device ? [
      { icon: '\u270F\uFE0F', label: 'Upravi\u0165 zariadenie', action: function() {
        closeCtx();
        openEditDevModal(ctxMenu.device);
      }},
      { icon: '\uD83D\uDCCD', label: 'Upravit polohu', action: function() { startMoveMode(ctxMenu.device); closeCtx(); } },
      { icon: '\uD83D\uDD17', label: 'Vytvorit prepojenie', action: function() { openMapLinkModal(ctxMenu.device); closeCtx(); } },
    ].concat(ctxMenu.device.web_url ? [{ icon: '\uD83C\uDF10', label: 'Prejs\u0165 na str\u00E1nku', action: function() { window.open(ctxMenu.device.web_url, '_blank', 'noopener,noreferrer'); closeCtx(); } }] : [])
     .concat([{ icon: '\uD83D\uDDD1\uFE0F', label: 'Zmaza\u0165 zariadenie', danger: true, action: function() { setMapDeleteConfirm(ctxMenu.device); closeCtx(); } }])
    : [
      { icon: '\u2795', label: 'Pridat zariadenie', action: function() { setAddForm({ lat: ctxMenu.lat, lng: ctxMenu.lng }); setFormData({ name: '', ip: '', type: 'router', location: '' }); closeCtx(); } },
      { icon: '\uD83C\uDFE2', label: 'Vytvori\u0165 Site tu', action: function() {
        setSiteForm({ name: '', description: '', device_ids: [] });
        setSiteModal({ mode: 'create', lat: ctxMenu.lat, lng: ctxMenu.lng });
        closeCtx();
      }},
    ];

  return CE('div', null,
    // Info bar
    CE('div', { style: Object.assign({}, S.mapNote, { marginBottom: '10px' }) },
      '\uD83D\uDCCD Na mape: ' + withCoords.length + ' | Bez suradnic: ' + noCoords.length +
      (noCoords.length > 0 ? ' (' + noCoords.map(function(d) { return d.name; }).join(', ') + ')' : '') +
      ' \u2014 Prav\u00E9 tla\u010Didlo na mape = mo\u017Enosti'
    ),
    // Saving toast
    savingMsg && CE('div', { style: { background: '#1e293b', border: '1px solid #38bdf8', borderRadius: '6px', padding: '8px 14px', marginBottom: '8px', fontSize: '13px', color: '#e2e8f0' } }, savingMsg),
    // Move-mode hint bar
    moveHint && CE('div', { style: { background: '#1c1917', border: '1px solid #854d0e', borderRadius: '6px', padding: '8px 14px', marginBottom: '8px', fontSize: '12px', color: '#fde68a', display: 'flex', alignItems: 'center', gap: '10px' } },
      CE('span', null, '\uD83D\uDCCD Potiahnite marker na nov\u00e9 miesto \u2014 m\u00f4\u017eete presun\u00fa\u0165 viackr\u00e1t. Klik na Ulo\u017ei\u0165 = koniec presunu.'),
      CE('button', { style: { marginLeft: 'auto', background: '#1e40af', color: '#bfdbfe', border: 'none', borderRadius: '4px', padding: '3px 10px', cursor: 'pointer', fontSize: '12px', marginRight: '6px' }, onClick: function() { stopMoveMode(true); } }, '\uD83D\uDCBE Ulo\u017ei\u0165'),
      CE('button', { style: { background: '#334155', color: '#94a3b8', border: 'none', borderRadius: '4px', padding: '3px 10px', cursor: 'pointer', fontSize: '12px' }, onClick: function() { if (moveModeRef.current) { var mm = moveModeRef.current; var orig = mm.site ? [mm.site.latitude, mm.site.longitude] : mm.device ? [mm.device.latitude, mm.device.longitude] : null; if (orig) try { mm.marker.setLatLng(orig); } catch(e) {} } stopMoveMode(true); } }, 'Zru\u0161i\u0165')
    ),
    // Context menu — transparent overlay catches outside clicks
    ctxMenu && CE('div', { style: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 998 }, onClick: closeCtx }),
    ctxMenu && CE('div', { style: { position: 'fixed', top: ctxMenu.y, left: ctxMenu.x, background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', boxShadow: '0 8px 32px rgba(0,0,0,0.55)', zIndex: 999, minWidth: '200px', overflow: 'hidden' } },
      // Header row — link / site / device / coordinates
      CE('div', { style: { padding: '8px 14px', borderBottom: '1px solid #334155', fontSize: '12px', color: '#64748b', fontWeight: '600' } },
        ctxMenu.link   ? '\uD83D\uDD17 ' + (ctxMenu.link.cable_label || 'Spoj #' + ctxMenu.link.id) + ' [' + (ctxMenu.link.cable_type || '') + ']'
        : ctxMenu.site   ? '\uD83C\uDFE2 ' + ctxMenu.site.name
        : ctxMenu.device ? ctxMenu.device.name + ' \u2014 ' + ctxMenu.device.ip_address
                         : '\uD83D\uDCCD ' + (ctxMenu.lat ? ctxMenu.lat.toFixed(5) : '') + ', ' + (ctxMenu.lng ? ctxMenu.lng.toFixed(5) : '')
      ),
      ctxItems.map(function(item, i) {
        var isDanger = !!item.danger;
        return CE('div', {
          key: i,
          style: { padding: '10px 14px', cursor: 'pointer', fontSize: '13px', color: isDanger ? '#fca5a5' : '#e2e8f0', display: 'flex', alignItems: 'center', gap: '9px', borderTop: isDanger ? '1px solid #334155' : 'none', borderBottom: i < ctxItems.length - 1 && !isDanger ? '1px solid #0f172a' : 'none' },
          onClick: item.action,
          onMouseEnter: function(ev) { ev.currentTarget.style.background = isDanger ? '#7f1d1d' : '#334155'; },
          onMouseLeave: function(ev) { ev.currentTarget.style.background = 'transparent'; },
        }, CE('span', null, item.icon), CE('span', null, item.label));
      })
    ),
    // Add-device modal
    addForm && CE('div', { style: modalOv },
      CE('div', { style: modalBox },
        CE('h3', { style: { margin: '0 0 4px', color: '#38bdf8', fontSize: '16px' } }, '\u2795 Nove zariadenie'),
        CE('p', { style: { fontSize: '12px', color: '#64748b', margin: '0 0 18px' } }, '\uD83D\uDCCD ' + addForm.lat.toFixed(5) + ', ' + addForm.lng.toFixed(5)),
        renderFormFields(),
        CE('div', { style: { display: 'flex', gap: '8px' } },
          CE('button', { style: btnCancel, onClick: function() { setAddForm(null); } }, 'Zrusit'),
          CE('button', { style: btnPrimary, onClick: handleAdd }, '\u2795 Pridat zariadenie')
        )
      )
    ),
    // Edit-device modal (with interface management)
    editForm && CE('div', { style: modalOv },
      CE('div', { style: Object.assign({}, modalBox, { width: '520px', maxHeight: '88vh', overflowY: 'auto' }) },
        CE('h3', { style: { margin: '0 0 4px', color: '#38bdf8', fontSize: '16px' } }, '\u270F\uFE0F Upravit zariadenie'),
        CE('p', { style: { fontSize: '12px', color: '#64748b', margin: '0 0 18px' } }, 'ID: ' + editForm.id + ' \u2014 ' + editForm.ip_address),
        renderFormFields(),
        // Interface section
        CE('div', { style: { borderTop: '1px solid #334155', paddingTop: '16px', marginBottom: '16px' } },
          CE('div', { style: { fontSize: '11px', color: '#64748b', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' } }, '\uD83D\uDD0C Interfacy / Porty'),
          editIfaces.length === 0
            ? CE('p', { style: { color: '#475569', fontSize: '12px', marginBottom: '10px' } }, 'Ziadne interfacy. Pridajte prvy port.')
            : CE('div', { style: { marginBottom: '10px' } },
                editIfaces.map(function(iface) {
                  var isEd = editingEditIface && editingEditIface.id === iface.id;
                  var btnSm = { border: 'none', borderRadius: '4px', padding: '3px 8px', cursor: 'pointer', fontSize: '11px' };
                  var smIn = Object.assign({}, iStyle, { fontSize: '12px', padding: '4px 7px' });
                  if (isEd) {
                    return CE('div', { key: iface.id, style: { display: 'grid', gridTemplateColumns: '1fr 75px 65px 1fr auto auto', gap: '4px', alignItems: 'center', padding: '5px 8px', background: '#172033', borderRadius: '6px', marginBottom: '5px', border: '1px solid #38bdf8' } },
                      CE('input', { type: 'text', value: editingEditIface.name, style: smIn,
                        onChange: function(ev) { setEditingEditIface(function(f) { return Object.assign({}, f, { name: ev.target.value }); }); } }),
                      CE('select', { value: editingEditIface.type, style: smIn,
                        onChange: function(ev) { setEditingEditIface(function(f) { return Object.assign({}, f, { type: ev.target.value }); }); } },
                        CE('option', { value: 'ethernet' }, 'ETH'), CE('option', { value: 'sfp' }, 'SFP'),
                        CE('option', { value: 'sfp+' }, 'SFP+'), CE('option', { value: 'qsfp' }, 'QSFP'),
                        CE('option', { value: 'wifi' }, 'WiFi'),
                        CE('option', { value: 'gpon' }, 'GPON'), CE('option', { value: 'epon' }, 'EPON')
                      ),
                      CE('input', { type: 'number', value: editingEditIface.speed, placeholder: 'Mbps', style: smIn,
                        onChange: function(ev) { setEditingEditIface(function(f) { return Object.assign({}, f, { speed: ev.target.value }); }); } }),
                      CE('input', { type: 'text', value: editingEditIface.ip, placeholder: '192.168.1.1/30', style: smIn,
                        onChange: function(ev) { setEditingEditIface(function(f) { return Object.assign({}, f, { ip: ev.target.value }); }); } }),
                      CE('button', { style: Object.assign({}, btnSm, { background: '#14532d', color: '#86efac' }), onClick: patchEditIface }, '\u2705'),
                      CE('button', { style: Object.assign({}, btnSm, { background: '#334155', color: '#94a3b8' }), onClick: function() { setEditingEditIface(null); } }, '\u2716')
                    );
                  }
                  return CE('div', { key: iface.id, style: { display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', background: '#0f172a', borderRadius: '6px', marginBottom: '5px', fontSize: '13px' } },
                    CE('span', { style: { color: '#38bdf8', fontFamily: 'monospace', fontWeight: '600', minWidth: '110px' } }, iface.name),
                    CE('span', { style: { color: '#64748b', fontSize: '11px', flex: 1 } }, iface.interface_type + (iface.speed_mbps ? ' | ' + iface.speed_mbps + 'M' : '')),
                    iface.ip_address ? CE('span', { style: { color: '#94a3b8', fontSize: '11px', fontFamily: 'monospace' } }, iface.ip_address) : null,
                    CE('button', { style: Object.assign({}, btnSm, { background: '#1e3a5f', color: '#93c5fd' }),
                      onClick: function() { setEditingEditIface({ id: iface.id, name: iface.name, type: iface.interface_type, speed: iface.speed_mbps ? String(iface.speed_mbps) : '', ip: iface.ip_address || '' }); } }, '\u270F\uFE0F'),
                    CE('button', { style: Object.assign({}, btnSm, { background: '#7f1d1d', color: '#fca5a5' }),
                      onClick: function() { delEditIface(iface.id); } }, '\uD83D\uDDD1\uFE0F')
                  );
                })
              ),
          // Add interface mini-form
          CE('div', { style: { display: 'grid', gridTemplateColumns: '1fr 90px 80px 1fr auto', gap: '6px', alignItems: 'flex-end' } },
            CE('div', null,
              CE('label', { style: lStyle }, 'Meno portu'),
              CE('input', { type: 'text', value: newIfaceDat.name, placeholder: 'napr. GE-0/0/1', style: iStyle,
                onChange: function(ev) { setNewIfaceDat(function(f) { return Object.assign({}, f, { name: ev.target.value }); }); } })
            ),
            CE('div', null,
              CE('label', { style: lStyle }, 'Typ'),
              CE('select', { value: newIfaceDat.type, style: iStyle,
                onChange: function(ev) { setNewIfaceDat(function(f) { return Object.assign({}, f, { type: ev.target.value }); }); } },
                CE('option', { value: 'ethernet' }, 'ETH'), CE('option', { value: 'sfp' }, 'SFP'),
                CE('option', { value: 'sfp+' }, 'SFP+'), CE('option', { value: 'qsfp' }, 'QSFP'),
                CE('option', { value: 'wifi' }, 'WiFi'),
                CE('option', { value: 'gpon' }, 'GPON'), CE('option', { value: 'epon' }, 'EPON')
              )
            ),
            CE('div', null,
              CE('label', { style: lStyle }, 'Mbps'),
              CE('input', { type: 'number', value: newIfaceDat.speed, placeholder: '1000', style: iStyle,
                onChange: function(ev) { setNewIfaceDat(function(f) { return Object.assign({}, f, { speed: ev.target.value }); }); } })
            ),
            CE('div', null,
              CE('label', { style: lStyle }, 'IP adresa'),
              CE('input', { type: 'text', value: newIfaceDat.ip, placeholder: '192.168.1.1/30', style: iStyle,
                onChange: function(ev) { setNewIfaceDat(function(f) { return Object.assign({}, f, { ip: ev.target.value }); }); } })
            ),
            CE('button', { style: { background: '#1e40af', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 12px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', alignSelf: 'flex-end' },
              onClick: addEditIface }, '+ Pridat')
          )
        ),
        CE('div', { style: { display: 'flex', gap: '8px' } },
          CE('button', { style: btnCancel, onClick: function() { setEditForm(null); } }, 'Zrusit'),
          CE('button', { style: btnPrimary, onClick: handleEditSave }, '\uD83D\uDCBE Ulozit zmeny')
        )
      )
    ),
    // ── Nastavenie: co sa ukazuje na konkretnom spoji ──
    stitokModal && CE('div', { style: { position: 'fixed', inset: 0, zIndex: 4200, background: 'rgba(0,0,0,0.45)' },
      onClick: function() { setStitokModal(null); } }),
    stitokModal && CE('div', {
      style: { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 4201,
               background: '#1e293b', border: '2px solid #10b981', borderRadius: '12px',
               boxShadow: '0 12px 40px rgba(0,0,0,0.7)', width: '420px', maxWidth: 'calc(100vw - 24px)',
               maxHeight: '78vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
      onClick: function(ev) { ev.stopPropagation(); }
    },
      CE('div', { style: { padding: '12px 14px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 } },
        CE('span', { style: { fontSize: '16px' } }, '\uD83D\uDCCA'),
        CE('div', { style: { minWidth: 0 } },
          CE('div', { style: { color: '#6ee7b7', fontWeight: '700', fontSize: '14px' } }, '\u00dadaje na spoji'),
          CE('div', { style: { color: '#64748b', fontSize: '11px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } },
            stitokModal.link.cable_label || ('Spoj #' + stitokModal.link.id))
        ),
        CE('button', { style: { marginLeft: 'auto', background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '18px' },
          onClick: function() { setStitokModal(null); } }, '\u2715')
      ),
      CE('div', { style: { padding: '9px 14px', borderBottom: '1px solid #334155', fontSize: '11px', color: '#64748b', flexShrink: 0 } },
        'Za\u0161krtnite hodnoty, ktor\u00e9 sa maj\u00fa uk\u00e1za\u0165 priamo na spoji. Ni\u010d za\u0161krtnut\u00e9 = \u0161t\u00edtok sa nezobraz\u00ed.'),
      CE('div', { style: { overflowY: 'auto', minHeight: 0, overscrollBehavior: 'contain',
                           scrollbarWidth: 'thin', scrollbarColor: '#10b981 #1e293b' } },
        stitokModal.devices.map(function(zar) {
          return CE('div', { key: zar.id },
            CE('div', { style: { position: 'sticky', top: 0, background: '#172033', padding: '7px 14px',
                                 borderBottom: '1px solid #334155', fontSize: '11px', fontWeight: '700',
                                 color: '#93c5fd', textTransform: 'uppercase', letterSpacing: '0.04em' } },
              zar.name + '  \u00b7  ' + zar.ip_address),
            // Vyber portu, ktoreho prevadzka nas zaujima. Pri switchi je portov
            // vela, takze sa nedaju merat vsetky — vyberie sa jeden.
            (zar.interfaces || []).length > 0 && CE('div', {
              style: { display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', padding: '8px 14px',
                       borderBottom: '1px solid #243044', background: '#141d2e' } },
              CE('span', { style: { fontSize: '11px', color: '#64748b', flexShrink: 0 } }, 'prev\u00e1dzka portu:'),
              CE('select', {
                value: vybrateRozhranie[zar.id] || '',
                style: Object.assign({}, iStyle, { padding: '5px 8px', fontSize: '12px' }),
                onChange: function(ev) {
                  var v = ev.target.value;
                  setVybrateRozhranie(function(pr) { var n = Object.assign({}, pr); n[zar.id] = v; return n; });
                }
              },
                CE('option', { value: '' }, '\u2014 vyberte port \u2014'),
                (zar.interfaces || []).map(function(i) {
                  return CE('option', { key: i.id, value: String(i.id) },
                    i.name + (i.speed_mbps ? '  (' + i.speed_mbps + ' Mbit/s)' : ''));
                })
              ),
              CE('button', {
                disabled: !vybrateRozhranie[zar.id] || stitokUklada,
                style: { background: vybrateRozhranie[zar.id] ? '#065f46' : '#334155',
                         color: vybrateRozhranie[zar.id] ? '#a7f3d0' : '#64748b',
                         border: 'none', borderRadius: '6px', padding: '6px 10px', fontSize: '12px',
                         cursor: vybrateRozhranie[zar.id] ? 'pointer' : 'not-allowed', flexShrink: 0, fontWeight: '600' },
                onClick: function() { pridajPrevadzkuRozhrania(zar.id); }
              }, '\u2795 Prida\u0165'),
              // Sablona hardveru uz casto meria ten isty port cez firemny MIB pod
              // inym menom ("Eth2" = "p4_data_e2"). Sparovat sa to neda, tak to
              // aspon ukazeme — inak by na stitku bola ta ista hodnota dvakrat.
              uzMeranaPrevadzka(zar).length > 0 && CE('div', {
                style: { flexBasis: '100%', fontSize: '10.5px', color: '#fbbf24', lineHeight: '1.4' } },
                '\u26A0\uFE0F U\u017E sa meria prev\u00e1dzka: ' + uzMeranaPrevadzka(zar).join(', ')
                + ' \u2014 ak je to ten ist\u00fd port, bude\u0161 ma\u0165 t\u00fa ist\u00fa hodnotu dvakr\u00e1t.')
            ),
            zar.oids.length === 0
              ? CE('div', { style: { padding: '10px 14px', color: '#64748b', fontSize: '12px' } }, 'Zariadenie nem\u00e1 \u017eiadne SNMP hodnoty.')
              : zar.oids.map(function(o) {
                  var zvolene = jeZvolene(o.oid_id);
                  var hod = o.value === null || o.value === undefined ? '\u2014'
                          : (Math.abs(o.value) >= 100 ? o.value.toFixed(0)
                             : Math.abs(o.value) >= 10 ? o.value.toFixed(1) : o.value.toFixed(2))
                            .replace(/\.?0+$/, function(x) { return x.indexOf('.') === 0 ? '' : x; })
                            .replace('.', ',');
                  return CE('div', { key: o.oid_id,
                    style: { padding: '6px 14px', background: zvolene ? 'rgba(16,185,129,0.10)' : 'transparent' } },
                    CE('label', { style: { display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '13px', color: '#e2e8f0' } },
                      CE('input', { type: 'checkbox', checked: zvolene,
                        style: { accentColor: '#10b981', width: '15px', height: '15px', flexShrink: 0 },
                        onChange: function() { prepniZvolene(o.oid_id); } }),
                      CE('span', { style: { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, o.name),
                      CE('span', { style: { color: '#94a3b8', fontSize: '12px', flexShrink: 0 } },
                        hod + (o.unit ? ' ' + o.unit : ''))
                    ),
                    // Vlastny popis sa ponuka az ked je hodnota zapnuta - inak by
                    // okno zaplavilo 40 textovych poli.
                    zvolene && CE('div', { style: { display: 'flex', alignItems: 'center', gap: '8px', margin: '6px 0 2px 25px' } },
                      CE('span', { style: { fontSize: '11px', color: '#64748b', flexShrink: 0 } }, 'popis:'),
                      CE('input', { type: 'text', value: popisPre(o.oid_id), placeholder: o.name,
                        maxLength: 60,
                        style: Object.assign({}, iStyle, { padding: '5px 8px', fontSize: '12px' }),
                        onChange: function(ev) { nastavPopis(o.oid_id, ev.target.value); } })
                    )
                  );
                })
          );
        })
      ),
      CE('div', { style: { padding: '10px 14px', borderTop: '1px solid #334155', display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 } },
        CE('button', { style: { background: '#334155', color: '#94a3b8', border: 'none', borderRadius: '6px', padding: '8px 12px', cursor: 'pointer', fontSize: '12px' },
          onClick: function() { setStitokModal(function(p2) { return p2 ? Object.assign({}, p2, { selected: [] }) : p2; }); } },
          'Vypn\u00fa\u0165 v\u0161etko'),
        CE('span', { style: { fontSize: '12px', color: '#64748b' } }, 'zvolen\u00fdch: ' + stitokModal.selected.length),
        CE('button', { style: { marginLeft: 'auto', background: '#059669', color: '#fff', border: 'none', borderRadius: '6px',
                                padding: '8px 16px', cursor: stitokUklada ? 'wait' : 'pointer', fontSize: '13px', fontWeight: '600', opacity: stitokUklada ? 0.7 : 1 },
          disabled: stitokUklada, onClick: ulozStitokModal }, stitokUklada ? 'Ukladám\u2026' : '\uD83D\uDCBE Ulo\u017ei\u0165')
      )
    ),
    // ── Site device picker (draw-link mode) ──
    mapSiteDevPicker && CE('div', { style: { position: 'fixed', inset: 0, zIndex: 4100, background: 'rgba(0,0,0,0.45)' }, onClick: function() { setMapSiteDevPicker(null); } }),
    mapSiteDevPicker && (function() {
      var pickerDevs = devices.filter(function(dev) { return (mapSiteDevPicker.site.device_ids || []).indexOf(dev.id) >= 0; });
      var src = mapDrawLinkSrc;
      // Okno je vycentrovane, nie ukotvene pri kurzore. Povodne sa poloha
      // pocitala z poctu zariadeni a pri vacsom site vysla mimo obrazovky.
      // Vycentrovane okno s vlastnym posuvnikom tento problem nema vobec.
      return CE('div', { style: { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 4101, background: '#1e293b', border: '2px solid #7c3aed', borderRadius: '12px', boxShadow: '0 12px 40px rgba(0,0,0,0.7)', width: '320px', maxWidth: 'calc(100vw - 24px)', maxHeight: '70vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }, onClick: function(ev) { ev.stopPropagation(); } },
        CE('div', { style: { padding: '10px 14px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 } },
          CE('span', { style: { fontSize: '16px' } }, '\uD83C\uDFE2'),
          CE('span', { style: { color: '#a78bfa', fontWeight: '700', fontSize: '13px' } }, mapSiteDevPicker.site.name),
          CE('span', { style: { marginLeft: 'auto', fontSize: '11px', color: '#64748b' } }, (src ? '\u2192 Vyber cie\u013e' : '\u2192 Vyber zdroj') + '  \u00b7  ' + pickerDevs.length + ' zar.')
        ),
        CE('div', { style: { overflowY: 'auto', minHeight: 0, overscrollBehavior: 'contain', scrollbarWidth: 'thin', scrollbarColor: '#7c3aed #1e293b' } },
          pickerDevs.length === 0
            ? CE('div', { style: { padding: '14px', color: '#64748b', fontSize: '12px' } }, 'Site neobsahuje žiadne zariadenia.')
            : pickerDevs.map(function(dev) {
                var color = dev.is_reachable === true ? '#22c55e' : dev.is_reachable === false ? '#ef4444' : '#64748b';
                var isSrc = src && src.id === dev.id;
                return CE('div', { key: dev.id,
                  style: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', cursor: isSrc ? 'not-allowed' : 'pointer', background: isSrc ? '#1e3a5f' : 'transparent', borderBottom: '1px solid #0f172a', opacity: isSrc ? 0.5 : 1 },
                  onMouseEnter: function(ev) { if (!isSrc) ev.currentTarget.style.background = '#334155'; },
                  onMouseLeave: function(ev) { if (!isSrc) ev.currentTarget.style.background = 'transparent'; },
                  onClick: function(ev) { ev.stopPropagation(); if (!isSrc) handleMapPickDevice(dev); }
                },
                  CE('span', { style: { width: '10px', height: '10px', borderRadius: '50%', background: color, flexShrink: 0, display: 'inline-block' } }),
                  CE('div', { style: { minWidth: 0 } },
                    CE('div', { style: { color: '#e2e8f0', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }, dev.name),
                    CE('div', { style: { color: '#64748b', fontSize: '11px' } }, dev.ip_address)
                  )
                );
              })
        )
      );
    })(),

    // ── Delete device confirm dialog (Map) ──
    mapDeleteConfirm && CE('div', { style: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5000 },
      onClick: function() { setMapDeleteConfirm(null); } },
      CE('div', { style: { background: '#1e293b', border: '1px solid #7f1d1d', borderRadius: '12px', padding: '28px', width: '360px', maxWidth: '90vw', boxShadow: '0 16px 48px rgba(0,0,0,0.7)' },
        onClick: function(ev) { ev.stopPropagation(); } },
        CE('div', { style: { fontSize: '36px', textAlign: 'center', marginBottom: '12px' } }, '\uD83D\uDDD1\uFE0F'),
        CE('h3', { style: { margin: '0 0 8px', color: '#fca5a5', fontSize: '16px', textAlign: 'center' } }, 'Zmazať zariadenie?'),
        CE('p', { style: { margin: '0 0 20px', color: '#94a3b8', fontSize: '13px', textAlign: 'center', lineHeight: '1.5' } },
          'Naozaj chcete zmazať zariadenie\u00a0',
          CE('strong', { style: { color: '#e2e8f0' } }, mapDeleteConfirm.name),
          '\u00a0(', mapDeleteConfirm.ip_address, ')?',
          CE('br'), CE('span', { style: { color: '#64748b', fontSize: '11px' } }, 'Táto akcia sa nedá vrátiť späť.')
        ),
        CE('div', { style: { display: 'flex', gap: '10px' } },
          CE('button', { style: { flex: 1, background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: '8px', padding: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
            onClick: function() { setMapDeleteConfirm(null); } }, 'Zrušiť'),
          CE('button', { style: { flex: 1, background: '#dc2626', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '700' },
            onClick: function() { deleteMapDevice(mapDeleteConfirm); } }, '\uD83D\uDDD1\uFE0F Zmazať')
        )
      )
    ),

    // ── Site panel device context menu ──
    sitePanelCtx && CE('div', { style: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 3998 }, onClick: function() { setSitePanelCtx(null); } }),
    sitePanelCtx && CE('div', { style: { position: 'fixed', top: sitePanelCtx.y, left: sitePanelCtx.x, background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', boxShadow: '0 8px 32px rgba(0,0,0,0.55)', zIndex: 3999, minWidth: '200px', overflow: 'hidden' } },
      CE('div', { style: { padding: '8px 14px', borderBottom: '1px solid #334155', fontSize: '12px', color: '#64748b', fontWeight: '600' } },
        sitePanelCtx.device.name + ' \u2014 ' + sitePanelCtx.device.ip_address
      ),
      CE('div', {
        style: { padding: '10px 14px', cursor: 'pointer', fontSize: '13px', color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: '9px' },
        onClick: function() {
          setSitePanelCtx(null);
          openEditDevModal(sitePanelCtx.device);
        },
        onMouseEnter: function(ev) { ev.currentTarget.style.background = '#334155'; },
        onMouseLeave: function(ev) { ev.currentTarget.style.background = 'transparent'; },
      }, CE('span', null, '\u270F\uFE0F'), CE('span', null, 'Upravi\u0165 zariadenie')),
      sitePanelCtx.device.web_url && CE('div', {
        style: { padding: '10px 14px', cursor: 'pointer', fontSize: '13px', color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: '9px', borderTop: '1px solid #1e293b' },
        onClick: function() { window.open(sitePanelCtx.device.web_url, '_blank', 'noopener,noreferrer'); setSitePanelCtx(null); },
        onMouseEnter: function(ev) { ev.currentTarget.style.background = '#334155'; },
        onMouseLeave: function(ev) { ev.currentTarget.style.background = 'transparent'; },
      }, CE('span', null, '\uD83C\uDF10'), CE('span', null, 'Prejs\u0165 na str\u00E1nku'))
    ),
    // ── Site detail panel (right-side slide-in) ──
    currentSite && CE('div', { style: { position: 'fixed', top: 0, right: 0, bottom: 0, width: '320px', background: '#1e293b', borderLeft: '1px solid #334155', zIndex: 2000, display: 'flex', flexDirection: 'column', boxShadow: '-8px 0 32px rgba(0,0,0,0.5)' } },
      CE('div', { style: { padding: '16px 20px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', gap: '10px' } },
        CE('span', { style: { fontSize: '22px' } }, '\uD83C\uDFE2'),
        CE('div', { style: { flex: 1, minWidth: 0 } },
          CE('div', { style: { fontWeight: '700', color: '#e2e8f0', fontSize: '15px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }, currentSite.name),
          currentSite.description ? CE('div', { style: { fontSize: '12px', color: '#64748b', marginTop: '2px' } }, currentSite.description) : null
        ),
        CE('button', { style: { background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '20px', padding: '2px 6px', lineHeight: 1 }, onClick: function() { setCurrentSite(null); } }, '\u00D7')
      ),
      // Status badge
      CE('div', { style: { padding: '10px 20px', borderBottom: '1px solid #334155', display: 'flex', gap: '8px', alignItems: 'center' } },
        (function() {
          var sc = { up: { bg: '#14532d', fg: '#86efac', label: 'Všetky UP' }, down: { bg: '#7f1d1d', fg: '#fca5a5', label: 'Všetky DOWN' }, partial: { bg: '#78350f', fg: '#fde68a', label: 'Čiastočný výpadok' }, unknown: { bg: '#1e293b', fg: '#94a3b8', label: 'Neznámy stav' } };
          var c = sc[currentSite.status] || sc.unknown;
          return CE('span', { style: { background: c.bg, color: c.fg, borderRadius: '5px', padding: '3px 10px', fontSize: '12px', fontWeight: '600' } }, c.label);
        })(),
        CE('span', { style: { fontSize: '12px', color: '#64748b' } }, (currentSite.device_ids || []).length + ' zariadení')
      ),
      // Solárna elektráreň na tejto lokalite
      solarNaSite.length > 0 && CE('div', { style: { padding: '12px 20px', borderBottom: '1px solid #334155' } },
        solarNaSite.map(function(p) {
          var last = p.last || {};
          var dnes = p.today || {};
          var vek = p.last_ok ? (Date.now() - new Date(p.last_ok).getTime()) / 1000 : null;
          var zive = vek != null && vek <= (p.poll_interval || 120) * 3;
          var poruchy = last.fault_text || [];
          return CE('div', { key: p.id, style: { marginBottom: '4px' } },
            CE('div', { style: { display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '8px' } },
              CE('span', { style: { fontSize: '15px' } }, '☀️'),
              CE('span', { style: { color: '#e2e8f0', fontSize: '13px', fontWeight: '600', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }, p.name),
              CE('span', { style: { width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0,
                background: !p.enabled ? '#475569' : zive ? '#22c55e' : '#ef4444' } })),
            !zive && CE('div', { style: { color: '#fca5a5', fontSize: '11px', marginBottom: '8px' } },
              p.enabled ? (p.last_ok ? 'Regulátor neodpovedá' : 'Zatiaľ bez merania') : 'Meranie vypnuté'),
            poruchy.length > 0 && CE('div', { style: { background: '#7f1d1d', color: '#fca5a5', borderRadius: '5px', padding: '5px 8px', fontSize: '11px', marginBottom: '8px' } },
              '🔴 ' + poruchy.join(', ')),
            p.low_since && CE('div', { style: { background: '#78350f', color: '#fde68a', borderRadius: '5px', padding: '5px 8px', fontSize: '11px', marginBottom: '8px' } },
              '🪫 Batéria pod prahom'),
            CE('div', { style: { display: 'flex', gap: '6px' } },
              [['☀️ PV', last.pv_w == null ? '—' : last.pv_w + ' W', '#fbbf24'],
               ['🔋 Batéria', last.bat_v == null ? '—' : last.bat_v + ' V', '#38bdf8'],
               ['⚡ Záťaž', last.load_w == null ? '—' : last.load_w + ' W', '#a78bfa']].map(function(m, i) {
                return CE('div', { key: i, style: { flex: 1, background: '#0f172a', borderRadius: '6px', padding: '6px 8px', minWidth: 0 } },
                  CE('div', { style: { color: '#64748b', fontSize: '10px', whiteSpace: 'nowrap' } }, m[0]),
                  CE('div', { style: { color: m[2], fontSize: '14px', fontWeight: '700' } }, m[1]));
              })),
            dnes && CE('div', { style: { color: '#64748b', fontSize: '11px', marginTop: '7px', lineHeight: 1.6 } },
              'Dnes ' + (dnes.gen_wh == null ? '—' : (dnes.gen_wh / 1000).toFixed(2) + ' kWh') + ' vyrobené' +
              (dnes.con_wh == null ? '' : ', ' + (dnes.con_wh / 1000).toFixed(2) + ' kWh spotrebované') +
              (last.state_text ? ' · ' + last.state_text : '')));
        })
      ),
      // Device list
      CE('div', { style: { flex: 1, overflowY: 'auto', padding: '14px 20px' } },
        CE('div', { style: { fontSize: '11px', color: '#64748b', marginBottom: '10px', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.05em' } }, 'Zariadenia v site'),
        (currentSite.device_ids || []).length === 0
          ? CE('p', { style: { color: '#475569', fontSize: '13px' } }, 'Žiadne zariadenia.')
          : (currentSite.device_ids || []).map(function(dId) {
              var d = devices.find(function(x) { return x.id === dId; });
              if (!d) return null;
              var inMaint = d.maintenance_until && new Date(d.maintenance_until + 'Z') > new Date();
              var color = inMaint ? '#3b82f6' : d.is_reachable === true ? '#22c55e' : d.is_reachable === false ? '#ef4444' : '#64748b';
              var status = inMaint ? '🛠 Údržba' : d.is_reachable === true ? 'UP' : d.is_reachable === false ? 'DOWN' : '?';
              var ms = d.response_time_ms ? parseFloat(d.response_time_ms).toFixed(1) + ' ms' : '';
              // Downtime duration (mirrors map popup logic)
              var downtimeEl = null;
              if (d.is_reachable === false && d.down_since) {
                var diffMs2 = Date.now() - new Date(d.down_since).getTime();
                var diffMin2 = Math.floor(diffMs2 / 60000);
                var diffH2   = Math.floor(diffMin2 / 60);
                var diffD2   = Math.floor(diffH2 / 24);
                var durStr2;
                if (diffD2 > 0)       durStr2 = diffD2 + 'd ' + (diffH2 % 24) + 'h';
                else if (diffH2 > 0)  durStr2 = diffH2 + 'h ' + (diffMin2 % 60) + 'min';
                else                  durStr2 = diffMin2 + ' min';
                var sinceDate2 = new Date(d.down_since).toLocaleString('sk-SK', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
                downtimeEl = CE('div', { style: { fontSize: '10px', color: '#fca5a5', marginTop: '2px' } }, '\u23F1 ' + durStr2 + ' (od ' + sinceDate2 + ')');
              }
              // Long-press support for touch devices
              var holdTimer = { t: null };
              return CE('div', {
                key: dId,
                style: { display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', background: '#0f172a', borderRadius: '8px', marginBottom: '6px', border: '1px solid #1e293b', cursor: 'context-menu', userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' },
                onContextMenu: function(ev) {
                  ev.preventDefault();
                  setSitePanelCtx({ device: d, x: ev.clientX, y: ev.clientY });
                },
                onTouchStart: function(ev) {
                  // capture coords immediately — ev is recycled after the call returns
                  var cx = ev.touches[0].clientX;
                  var cy = ev.touches[0].clientY;
                  holdTimer.t = setTimeout(function() {
                    setSitePanelCtx({ device: d, x: cx, y: cy });
                  }, 600);
                },
                onTouchEnd:  function(ev) { ev.preventDefault(); clearTimeout(holdTimer.t); },
                onTouchMove: function()    { clearTimeout(holdTimer.t); },
              },
                CE('div', { style: { width: '10px', height: '10px', borderRadius: '50%', background: color, flexShrink: 0, boxShadow: '0 0 6px ' + color } }),
                CE('div', { style: { flex: 1, minWidth: 0 } },
                  CE('div', { style: { fontWeight: '600', color: '#e2e8f0', fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }, d.name),
                  CE('div', { style: { fontSize: '11px', color: '#64748b', fontFamily: 'monospace' } }, d.ip_address),
                  downtimeEl
                ),
                CE('div', { style: { textAlign: 'right', flexShrink: 0 } },
                  CE('div', { style: { fontSize: '11px', fontWeight: '700', color: color } }, status),
                  ms ? CE('div', { style: { fontSize: '10px', color: '#64748b' } }, ms) : null
                )
              );
            })
      ),
      // Actions
      CE('div', { style: { padding: '14px 20px', borderTop: '1px solid #334155', display: 'flex', flexDirection: 'column', gap: '8px' } },
        CE('button', { style: { width: '100%', background: '#14532d', color: '#86efac', border: 'none', borderRadius: '6px', padding: '9px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
          onClick: function() {
            setAddForm({ lat: currentSite.latitude, lng: currentSite.longitude, siteId: currentSite.id });
            setFormData({ name: '', ip: '', type: 'router', location: '' });
          } }, '\u2795 Prida\u0165 zariadenie'),
        CE('div', { style: { display: 'flex', gap: '8px' } },
          CE('button', { style: { flex: 1, background: '#1e3a5f', color: '#93c5fd', border: 'none', borderRadius: '6px', padding: '9px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
            onClick: function() {
              setSiteForm({ name: currentSite.name, description: currentSite.description || '', device_ids: currentSite.device_ids || [], lat: currentSite.latitude, lng: currentSite.longitude });
              setSiteModal({ mode: 'edit', site: currentSite, lat: currentSite.latitude, lng: currentSite.longitude });
            } }, '\u270F\uFE0F Upravi\u0165'),
          CE('button', { style: { flex: 1, background: '#7f1d1d', color: '#fca5a5', border: 'none', borderRadius: '6px', padding: '9px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
            onClick: function() { deleteSite(currentSite.id); } }, '\uD83D\uDDD1\uFE0F Zmazat')
        )
      )
    ),
    // ── Site create/edit modal ──
    siteModal && CE('div', { style: modalOv },
      CE('div', { style: Object.assign({}, modalBox, { width: '500px', maxHeight: '88vh', overflowY: 'auto' }) },
        CE('h3', { style: { margin: '0 0 14px', color: '#38bdf8', fontSize: '16px' } }, siteModal.mode === 'create' ? '\uD83C\uDFE2 Nov\u00FD Site' : '\u270F\uFE0F Upravi\u0165 Site'),
        CE('div', { style: { marginBottom: '12px' } },
          CE('label', { style: lStyle }, 'N\u00E1zov *'),
          CE('input', { type: 'text', value: siteForm.name, placeholder: 'napr. Bratislava DC', style: iStyle,
            onChange: function(ev) { setSiteForm(function(f) { return Object.assign({}, f, { name: ev.target.value }); }); } })
        ),
        CE('div', { style: { marginBottom: '12px' } },
          CE('label', { style: lStyle }, 'Popis'),
          CE('input', { type: 'text', value: siteForm.description, placeholder: 'napr. D\u00e1tov\u00e9 centrum Bratislava', style: iStyle,
            onChange: function(ev) { setSiteForm(function(f) { return Object.assign({}, f, { description: ev.target.value }); }); } })
        ),
        siteModal.mode === 'edit'
          ? CE('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' } },
              CE('div', null,
                CE('label', { style: lStyle }, '\uD83D\uDCCD Zem. \u0161\u00EDrka (Lat)'),
                CE('input', { type: 'number', step: '0.00001', value: siteForm.lat !== undefined ? siteForm.lat : siteModal.lat, style: iStyle,
                  onChange: function(ev) { setSiteForm(function(f) { return Object.assign({}, f, { lat: ev.target.value }); }); } })
              ),
              CE('div', null,
                CE('label', { style: lStyle }, '\uD83D\uDCCD Zem. d\u013a\u017eka (Lng)'),
                CE('input', { type: 'number', step: '0.00001', value: siteForm.lng !== undefined ? siteForm.lng : siteModal.lng, style: iStyle,
                  onChange: function(ev) { setSiteForm(function(f) { return Object.assign({}, f, { lng: ev.target.value }); }); } })
              )
            )
          : CE('p', { style: { fontSize: '12px', color: '#64748b', margin: '0 0 16px' } }, '\uD83D\uDCCD ' + siteModal.lat.toFixed(5) + ', ' + siteModal.lng.toFixed(5)),
        CE('div', { style: { borderTop: '1px solid #334155', paddingTop: '14px', marginBottom: '16px' } },
          CE('div', { style: { fontSize: '11px', color: '#64748b', marginBottom: '10px', textTransform: 'uppercase', fontWeight: '700' } }, 'Zariadenia v site'),
          CE('div', { style: { maxHeight: '260px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' } },
            devices.map(function(d) {
              var checked = (siteForm.device_ids || []).indexOf(d.id) >= 0;
              var color = d.is_reachable === true ? '#22c55e' : d.is_reachable === false ? '#ef4444' : '#64748b';
              return CE('label', { key: d.id, style: { display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', background: checked ? '#172033' : '#0f172a', borderRadius: '6px', cursor: 'pointer', border: '1px solid ' + (checked ? '#38bdf8' : 'transparent') } },
                CE('input', { type: 'checkbox', checked: checked, style: { width: '15px', height: '15px', flexShrink: 0 },
                  onChange: function() {
                    setSiteForm(function(f) {
                      var ids = (f.device_ids || []).slice();
                      var idx = ids.indexOf(d.id);
                      if (idx >= 0) ids.splice(idx, 1); else ids.push(d.id);
                      return Object.assign({}, f, { device_ids: ids });
                    });
                  }
                }),
                CE('div', { style: { width: '9px', height: '9px', borderRadius: '50%', background: color, flexShrink: 0 } }),
                CE('span', { style: { fontWeight: '600', color: '#e2e8f0', fontSize: '13px' } }, d.name),
                CE('span', { style: { color: '#64748b', fontSize: '11px', fontFamily: 'monospace' } }, d.ip_address)
              );
            })
          )
        ),
        CE('div', { style: { display: 'flex', gap: '8px' } },
          CE('button', { style: btnCancel, onClick: function() { setSiteModal(null); } }, 'Zru\u0161i\u0165'),
          CE('button', { style: btnPrimary, onClick: saveSite }, '\uD83C\uDFE2 Ulo\u017Ei\u0165 Site')
        )
      )
    ),
    // Edit-device tabbed modal (map view)
    editDevModal && CE('div', { style: modalOv },
      CE('div', { style: Object.assign({}, modalBox, { width: '580px', maxHeight: '90vh', overflowY: 'auto' }) },
        CE('h3', { style: { margin: '0 0 2px', color: '#38bdf8', fontSize: '16px' } }, '\u270F\uFE0F Upravi\u0165 zariadenie'),
        CE('p', { style: { fontSize: '12px', color: '#64748b', margin: '0 0 14px' } }, 'ID: ' + editDevModal.device.id + ' \u2014 ' + editDevModal.device.ip_address),
        CE('div', { style: { display: 'flex', gap: '3px', marginBottom: '16px', flexWrap: 'wrap' } },
          [['basic','\uD83D\uDCBB Z\u00E1kladn\u00E9'],['interfaces','\uD83D\uDD0C Interfacy'],['snmp','\uD83D\uDCE1 SNMP'],['notes','\uD83D\uDCDD Pozn\u00E1mky'],['wifi','\uD83D\uDCF6 WiFi'],['maintenance','\uD83D\uDEE0 \u00DAdržba'],['graphs','\uD83D\uDCC8 Grafy'],['alerts','\uD83D\uDEA8 Alerty'],['service','\uD83D\uDD27 Servis'],['diag','\uD83D\uDD0D Diagnostika']].map(function(t) {
            var active = editDevTab === t[0];
            return CE('button', { key: t[0], style: { border: 'none', borderRadius: '5px', padding: '5px 10px', cursor: 'pointer', fontSize: '11px', fontWeight: active ? '700' : '400', background: active ? '#1e40af' : '#334155', color: active ? '#fff' : '#94a3b8' },
              onClick: function() {
                setEditDevTab(t[0]);
                if (t[0] === 'service') {
                  setMapDevTicketsLoading(true);
                  fetch('/api/devices/' + editDevModal.device.id + '/tickets', { headers: { Authorization: 'Bearer ' + localStorage.getItem('nw_token') } })
                    .then(function(r) { return r.json(); }).then(function(d) { setMapDevTickets(d.tickets || []); setMapDevTicketsLoading(false); }).catch(function() { setMapDevTicketsLoading(false); });
                }
              } }, t[1]);
          })
        ),
        editDevTab === 'basic' && CE('div', null,
          CE('div', { style: { marginBottom: '10px' } }, CE('label', { style: lStyle }, 'N\u00E1zov *'), CE('input', { type: 'text', value: editDevForm.name, style: iStyle, onChange: function(ev) { setEditDevForm(function(f) { return Object.assign({}, f, { name: ev.target.value }); }); } })),
          CE('div', { style: { marginBottom: '10px' } }, CE('label', { style: lStyle }, 'IP Adresa *'), CE('input', { type: 'text', value: editDevForm.ip, style: iStyle, onChange: function(ev) { setEditDevForm(function(f) { return Object.assign({}, f, { ip: ev.target.value }); }); } })),
          CE('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' } },
            CE('div', null, CE('label', { style: lStyle }, 'Typ'), CE('select', { value: editDevForm.type, style: iStyle, onChange: function(ev) { setEditDevForm(function(f) { return Object.assign({}, f, { type: ev.target.value }); }); } }, CE('option', { value: 'router' }, 'Router'), CE('option', { value: 'switch' }, 'Switch'), CE('option', { value: 'ap' }, 'AP'), CE('option', { value: 'server' }, 'Server'), CE('option', { value: 'firewall' }, 'Firewall'), CE('option', { value: 'olt' }, 'OLT'), CE('option', { value: 'onu' }, 'ONU'), CE('option', { value: 'other' }, 'In\u00E9'))),
            CE('div', null, CE('label', { style: lStyle }, 'Lokalita'), CE('input', { type: 'text', value: editDevForm.location, style: iStyle, onChange: function(ev) { setEditDevForm(function(f) { return Object.assign({}, f, { location: ev.target.value }); }); } }))
          ),
          CE('div', { style: { marginBottom: '10px' } }, CE('label', { style: lStyle }, 'Ping interval (s)'), CE('input', { type: 'number', value: editDevPing, style: iStyle, onChange: function(ev) { setEditDevPing(ev.target.value); } })),
          CE('div', { style: { marginBottom: '10px' } }, CE('label', { style: lStyle }, '\uD83C\uDF10 Web URL (napr. http://10.0.0.1:8080)'), CE('input', { type: 'text', value: editDevForm.web_url, placeholder: 'http://192.168.1.1:8080', style: iStyle, onChange: function(ev) { setEditDevForm(function(f) { return Object.assign({}, f, { web_url: ev.target.value }); }); } }))
        ),
        editDevTab === 'interfaces' && CE('div', null,
          CE('div', { style: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' } },
            CE('button', { style: { background: '#0369a1', color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 14px', cursor: mapSnmpDiscovering ? 'wait' : 'pointer', fontSize: '12px', fontWeight: '600', opacity: mapSnmpDiscovering ? 0.7 : 1 }, onClick: discoverMapSnmpIfaces, disabled: mapSnmpDiscovering }, mapSnmpDiscovering ? '\u23F3 Na\u010D\u00EDtavam...' : '\uD83D\uDD0D Na\u010D\u00EDta\u0165 cez SNMP'),
            mapSnmpDiscovered !== null ? CE('span', { style: { color: '#64748b', fontSize: '11px' } }, 'N\u00E1jden\u00E9: ' + mapSnmpDiscovered.length) : null
          ),
          mapSnmpDiscovered !== null && mapSnmpDiscovered.length > 0 && CE('div', { style: { background: '#0c1829', border: '1px solid #0369a1', borderRadius: '8px', padding: '10px', marginBottom: '12px' } },
            CE('div', { style: { fontSize: '11px', color: '#38bdf8', marginBottom: '8px', textTransform: 'uppercase', fontWeight: '600' } }, 'Objaven\u00E9 SNMP interfacy'),
            CE('div', { style: { maxHeight: '220px', overflowY: 'auto' } },
              mapSnmpDiscovered.map(function(iface) {
                var alreadyAdded = editDevModal.ifaces.some(function(ex) { return ex.name === iface.name; });
                return CE('div', { key: iface.index, style: { display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 8px', background: alreadyAdded ? '#0f2e1a' : '#0f172a', borderRadius: '5px', marginBottom: '4px' } },
                  CE('span', { style: { color: iface.is_up ? '#86efac' : '#ef4444', fontSize: '10px' } }, iface.is_up ? '\u25CF' : '\u25CB'),
                  CE('span', { style: { color: '#e2e8f0', fontFamily: 'monospace', fontSize: '12px', flex: 1 } }, iface.name + (iface.alias ? ' (' + iface.alias + ')' : '')),
                  CE('span', { style: { color: '#64748b', fontSize: '11px' } }, iface.interface_type + (iface.speed_mbps ? ' | ' + iface.speed_mbps + 'M' : '')),
                  alreadyAdded ? CE('span', { style: { color: '#86efac', fontSize: '11px' } }, '\u2713') : CE('button', { style: { background: '#14532d', color: '#86efac', border: 'none', borderRadius: '4px', padding: '3px 8px', cursor: 'pointer', fontSize: '11px' }, onClick: function() { addMapSnmpIface(iface); } }, '+ Prida\u0165')
                );
              })
            )
          ),
          editDevModal.ifaces.length === 0
            ? CE('p', { style: { color: '#475569', fontSize: '12px', marginBottom: '10px' } }, '\u017Diadne interfacy.')
            : CE('div', { style: { marginBottom: '10px' } },
                editDevModal.ifaces.map(function(iface) {
                  var isEd = mapEditingIface && mapEditingIface.id === iface.id;
                  var btnSm = { border: 'none', borderRadius: '4px', padding: '3px 8px', cursor: 'pointer', fontSize: '11px' };
                  var smIn = Object.assign({}, iStyle, { fontSize: '12px', padding: '4px 7px' });
                  if (isEd) return CE('div', { key: iface.id, style: { display: 'grid', gridTemplateColumns: '1fr 75px 65px 1fr auto auto', gap: '4px', alignItems: 'center', padding: '5px 8px', background: '#172033', borderRadius: '6px', marginBottom: '5px', border: '1px solid #38bdf8' } },
                    CE('input', { type: 'text', value: mapEditingIface.name, style: smIn, onChange: function(ev) { setMapEditingIface(function(f) { return Object.assign({}, f, { name: ev.target.value }); }); } }),
                    CE('select', { value: mapEditingIface.type, style: smIn, onChange: function(ev) { setMapEditingIface(function(f) { return Object.assign({}, f, { type: ev.target.value }); }); } }, CE('option', { value: 'ethernet' }, 'ETH'), CE('option', { value: 'sfp' }, 'SFP'), CE('option', { value: 'wifi' }, 'WiFi'), CE('option', { value: 'gpon' }, 'GPON'), CE('option', { value: 'epon' }, 'EPON')),
                    CE('input', { type: 'number', value: mapEditingIface.speed, placeholder: 'Mbps', style: smIn, onChange: function(ev) { setMapEditingIface(function(f) { return Object.assign({}, f, { speed: ev.target.value }); }); } }),
                    CE('input', { type: 'text', value: mapEditingIface.ip, placeholder: 'IP', style: smIn, onChange: function(ev) { setMapEditingIface(function(f) { return Object.assign({}, f, { ip: ev.target.value }); }); } }),
                    CE('button', { style: Object.assign({}, btnSm, { background: '#14532d', color: '#86efac' }), onClick: patchMapEditDevIface }, '\u2705'),
                    CE('button', { style: Object.assign({}, btnSm, { background: '#334155', color: '#94a3b8' }), onClick: function() { setMapEditingIface(null); } }, '\u2716')
                  );
                  return CE('div', { key: iface.id, style: { display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', background: '#0f172a', borderRadius: '6px', marginBottom: '5px', fontSize: '13px' } },
                    CE('span', { style: { color: '#38bdf8', fontFamily: 'monospace', fontWeight: '600', minWidth: '110px' } }, iface.name),
                    CE('span', { style: { color: '#64748b', fontSize: '11px', flex: 1 } }, iface.interface_type + (iface.speed_mbps ? ' | ' + iface.speed_mbps + 'M' : '')),
                    iface.ip_address ? CE('span', { style: { color: '#94a3b8', fontSize: '11px', fontFamily: 'monospace' } }, iface.ip_address) : null,
                    CE('button', { style: { background: '#1e3a5f', color: '#93c5fd', border: 'none', borderRadius: '4px', padding: '3px 8px', cursor: 'pointer', fontSize: '11px' }, onClick: function() { setMapEditingIface({ id: iface.id, name: iface.name, type: iface.interface_type, speed: iface.speed_mbps ? String(iface.speed_mbps) : '', ip: iface.ip_address || '' }); } }, '\u270F\uFE0F'),
                    CE('button', { style: { background: '#7f1d1d', color: '#fca5a5', border: 'none', borderRadius: '4px', padding: '3px 8px', cursor: 'pointer', fontSize: '11px' }, onClick: function() { delMapEditDevIface(iface.id); } }, '\uD83D\uDDD1\uFE0F')
                  );
                })
              ),
          CE('div', { style: { borderTop: '1px solid #1e293b', paddingTop: '12px' } },
            CE('div', { style: { fontSize: '11px', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase' } }, '+ Prida\u0165 interface'),
            CE('div', { style: { display: 'grid', gridTemplateColumns: '1fr 80px 70px 1fr auto', gap: '6px', alignItems: 'flex-end' } },
              CE('div', null, CE('label', { style: lStyle }, 'Meno *'), CE('input', { type: 'text', value: mapNewIface.name, placeholder: 'napr. ether1', style: iStyle, onChange: function(ev) { setMapNewIface(function(f) { return Object.assign({}, f, { name: ev.target.value }); }); } })),
              CE('div', null, CE('label', { style: lStyle }, 'Typ'), CE('select', { value: mapNewIface.type, style: iStyle, onChange: function(ev) { setMapNewIface(function(f) { return Object.assign({}, f, { type: ev.target.value }); }); } }, CE('option', { value: 'ethernet' }, 'ETH'), CE('option', { value: 'sfp' }, 'SFP'), CE('option', { value: 'wifi' }, 'WiFi'), CE('option', { value: 'gpon' }, 'GPON'), CE('option', { value: 'epon' }, 'EPON'))),
              CE('div', null, CE('label', { style: lStyle }, 'Mbps'), CE('input', { type: 'number', value: mapNewIface.speed, placeholder: '1000', style: iStyle, onChange: function(ev) { setMapNewIface(function(f) { return Object.assign({}, f, { speed: ev.target.value }); }); } })),
              CE('div', null, CE('label', { style: lStyle }, 'IP'), CE('input', { type: 'text', value: mapNewIface.ip, placeholder: '192.168.1.1/30', style: iStyle, onChange: function(ev) { setMapNewIface(function(f) { return Object.assign({}, f, { ip: ev.target.value }); }); } })),
              CE('button', { style: { background: '#1e40af', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', alignSelf: 'flex-end' }, onClick: addMapEditDevIface }, '+ OK')
            )
          )
        ),
        editDevTab === 'snmp' && CE('div', null,
          CE('div', { style: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' } },
            CE('input', { type: 'checkbox', id: 'map-snmp-en', checked: editDevSnmp.enabled, onChange: function(ev) { setEditDevSnmp(function(f) { return Object.assign({}, f, { enabled: ev.target.checked }); }); }, style: { accentColor: '#38bdf8', width: '16px', height: '16px' } }),
            CE('label', { htmlFor: 'map-snmp-en', style: { color: '#e2e8f0', fontSize: '13px', cursor: 'pointer' } }, 'Povoli\u0165 SNMP polling')
          ),
          CE('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr 80px', gap: '10px', marginBottom: '12px' } },
            CE('div', null, CE('label', { style: lStyle }, 'Community string'), CE('input', { type: 'text', value: editDevSnmp.community, style: iStyle, onChange: function(ev) { setEditDevSnmp(function(f) { return Object.assign({}, f, { community: ev.target.value }); }); } })),
            CE('div', null, CE('label', { style: lStyle }, 'Verzia'), CE('select', { value: editDevSnmp.version, style: iStyle, onChange: function(ev) { setEditDevSnmp(function(f) { return Object.assign({}, f, { version: ev.target.value }); }); } }, CE('option', { value: 'v1' }, 'v1'), CE('option', { value: 'v2c' }, 'v2c'), CE('option', { value: 'v3' }, 'v3'))),
            CE('div', null, CE('label', { style: lStyle }, 'Port'), CE('input', { type: 'number', value: editDevSnmp.port, style: iStyle, onChange: function(ev) { setEditDevSnmp(function(f) { return Object.assign({}, f, { port: parseInt(ev.target.value) }); }); } }))
          ),

          /* --- Sablona podla typu hardveru --- */
          CE('div', { style: { background: '#0f2230', border: '1px solid #1e3a52', borderRadius: '8px', padding: '12px', marginBottom: '12px' } },
            CE('div', { style: { fontSize: '11px', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase', fontWeight: '600' } }, '\uD83D\uDCE6 Typ hardv\u00e9ru \u2014 sada OID-ov'),
            CE('div', { style: { display: 'flex', gap: '8px', alignItems: 'flex-end' } },
              CE('div', { style: { flex: 1 } },
                CE('select', { value: vybranaSablona, style: iStyle,
                  onChange: function(ev) { setVybranaSablona(ev.target.value); setSablonaNahlad(null); setSablonaMsg(null); } },
                  CE('option', { value: '' }, '\u2014 vyberte typ hardv\u00e9ru \u2014'),
                  snmpSablony.map(function(t) {
                    return CE('option', { key: t.id, value: String(t.id) }, t.name + '  (' + t.oid_count + ' OID)');
                  })
                )
              ),
              CE('button', { style: { background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: '6px', padding: '9px 14px', cursor: vybranaSablona ? 'pointer' : 'not-allowed', fontSize: '13px', opacity: vybranaSablona ? 1 : 0.5 },
                disabled: !vybranaSablona, onClick: nahladSablony }, 'N\u00e1h\u013ead'),
              CE('button', { style: { background: '#1e40af', color: '#fff', border: 'none', borderRadius: '6px', padding: '9px 14px', cursor: vybranaSablona ? 'pointer' : 'not-allowed', fontSize: '13px', fontWeight: '600', opacity: vybranaSablona ? 1 : 0.5 },
                disabled: !vybranaSablona, onClick: priradSablonu }, 'Priradi\u0165 OID-y')
            ),
            sablonaMsg && CE('div', { style: { marginTop: '8px', fontSize: '12px', color: '#94a3b8' } }, sablonaMsg),
            sablonaNahlad && CE('div', { style: { marginTop: '10px', fontSize: '12px' } },
              CE('div', { style: { color: '#94a3b8', marginBottom: '6px' } },
                sablonaNahlad.radio_index != null
                  ? 'R\u00e1diov\u00e9 rozhranie zisten\u00e9: index ' + sablonaNahlad.radio_index
                  : 'T\u00e1to \u0161abl\u00f3na index rozhrania nepotrebuje.'),
              CE('div', { style: { color: '#4ade80', marginBottom: '4px' } },
                'Prid\u00e1 sa: ' + sablonaNahlad.to_add.length +
                (sablonaNahlad.already_present.length ? '   \u2022   u\u017e existuje: ' + sablonaNahlad.already_present.length : '')),
              CE('div', { style: { maxHeight: '140px', overflowY: 'auto', background: '#0b1623', borderRadius: '6px', padding: '8px' } },
                sablonaNahlad.to_add.map(function(o, i) {
                  return CE('div', { key: i, style: { color: '#cbd5e1', fontSize: '11px', padding: '2px 0' } },
                    o.name + '  \u2014  ' + o.oid + (o.unit ? '  [' + o.unit + ']' : ''));
                })
              )
            )
          ),
          editDevSnmp.version === 'v3' && CE('div', { style: { background: '#0f2230', border: '1px solid #1e3a52', borderRadius: '8px', padding: '12px', marginBottom: '12px' } },
            CE('div', { style: { fontSize: '11px', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase', fontWeight: '600' } }, '\uD83D\uDD10 SNMPv3 Autentifik\u00e1cia'),
            CE('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' } },
              CE('div', null, CE('label', { style: lStyle }, 'Security Name (User)'), CE('input', { type: 'text', value: editDevSnmp.v3_user, style: iStyle, onChange: function(ev) { setEditDevSnmp(function(f) { return Object.assign({}, f, { v3_user: ev.target.value }); }); } })),
              CE('div', null, CE('label', { style: lStyle }, 'Security Level'), CE('select', { value: editDevSnmp.v3_level, style: iStyle, onChange: function(ev) { setEditDevSnmp(function(f) { return Object.assign({}, f, { v3_level: ev.target.value }); }); } }, CE('option', { value: 'noAuthNoPriv' }, 'noAuthNoPriv'), CE('option', { value: 'authNoPriv' }, 'authNoPriv'), CE('option', { value: 'authPriv' }, 'authPriv')))
            ),
            (editDevSnmp.v3_level === 'authNoPriv' || editDevSnmp.v3_level === 'authPriv') && CE('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' } },
              CE('div', null, CE('label', { style: lStyle }, 'Auth Protocol'), CE('select', { value: editDevSnmp.v3_auth_proto, style: iStyle, onChange: function(ev) { setEditDevSnmp(function(f) { return Object.assign({}, f, { v3_auth_proto: ev.target.value }); }); } }, CE('option', { value: 'MD5' }, 'MD5'), CE('option', { value: 'SHA' }, 'SHA'), CE('option', { value: 'SHA-256' }, 'SHA-256'))),
              CE('div', null, CE('label', { style: lStyle }, 'Auth Heslo'), CE('input', { type: 'password', value: editDevSnmp.v3_auth_pass, style: iStyle, onChange: function(ev) { setEditDevSnmp(function(f) { return Object.assign({}, f, { v3_auth_pass: ev.target.value }); }); } }))
            ),
            editDevSnmp.v3_level === 'authPriv' && CE('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' } },
              CE('div', null, CE('label', { style: lStyle }, 'Priv Protocol'), CE('select', { value: editDevSnmp.v3_priv_proto, style: iStyle, onChange: function(ev) { setEditDevSnmp(function(f) { return Object.assign({}, f, { v3_priv_proto: ev.target.value }); }); } }, CE('option', { value: 'DES' }, 'DES'), CE('option', { value: 'AES' }, 'AES'), CE('option', { value: 'AES-256' }, 'AES-256'))),
              CE('div', null, CE('label', { style: lStyle }, 'Priv Heslo'), CE('input', { type: 'password', value: editDevSnmp.v3_priv_pass, style: iStyle, onChange: function(ev) { setEditDevSnmp(function(f) { return Object.assign({}, f, { v3_priv_pass: ev.target.value }); }); } }))
            )
          ),
          CE('div', { style: { fontSize: '11px', color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' } }, 'Zobrazi\u0165 SNMP hodnoty na:'),
          CE('div', { style: { display: 'flex', gap: '16px', marginBottom: '16px' } },
            [['show_map','Mapa'],['show_topo','Topol\u00F3gia'],['show_info','Info panel']].map(function(item) {
              return CE('label', { key: item[0], style: { display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: '#e2e8f0', fontSize: '12px' } },
                CE('input', { type: 'checkbox', checked: editDevSnmp[item[0]], onChange: function(ev) { setEditDevSnmp(function(f) { return Object.assign({}, f, Object.fromEntries([[item[0], ev.target.checked]])); }); }, style: { accentColor: '#38bdf8' } }),
                item[1]);
            })
          ),
          CE('div', { style: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' } },
            CE('div', { style: { fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', flex: 1 } }, '\uD83D\uDCCA OID na polling'),
            CE('button', { style: { background: mapOidScanning ? '#334155' : '#0369a1', color: '#fff', border: 'none', borderRadius: '5px', padding: '4px 10px', cursor: mapOidScanning ? 'default' : 'pointer', fontSize: '11px', fontWeight: '600' },
              onClick: mapOidScanning ? null : scanMapOids },
              mapOidScanning ? '\u23F3 Skenuje...' : '\uD83D\uDD0D Sk\u00E1nov\u00E1 OID')
          ),
          mapOidResults !== null && mapOidResults.length === 0 && CE('div', { style: { fontSize: '12px', color: '#ef4444', marginBottom: '8px' } }, '\u274C \u017Diadne OID neodpovedali. Skontrolujte SNMP nastavenia.'),
          mapOidResults !== null && mapOidResults.length > 0 && CE('div', { style: { background: '#0c1829', border: '1px solid #0369a1', borderRadius: '8px', padding: '10px', marginBottom: '12px' } },
            CE('div', { style: { fontSize: '11px', color: '#38bdf8', marginBottom: '6px', fontWeight: '600', textTransform: 'uppercase' } }, 'N\u00E1jden\u00E9 OID (' + mapOidResults.length + ')'),
            CE('div', { style: { maxHeight: '200px', overflowY: 'auto' } },
              mapOidResults.map(function(entry) {
                var alreadyAdded = editDevOids.some(function(o) { return o.oid === entry.oid; });
                return CE('div', { key: entry.oid, style: { display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 6px', background: alreadyAdded ? '#0f2e1a' : '#0f172a', borderRadius: '5px', marginBottom: '3px' } },
                  CE('span', { style: { fontSize: '10px', color: '#64748b', minWidth: '56px' } }, entry.category),
                  CE('span', { style: { color: '#e2e8f0', fontSize: '12px', flex: 1 } }, entry.name),
                  CE('span', { style: { color: '#22c55e', fontFamily: 'monospace', fontSize: '11px', minWidth: '70px', textAlign: 'right' } }, entry.value + (entry.unit ? ' ' + entry.unit : '')),
                  alreadyAdded
                    ? CE('span', { style: { color: '#86efac', fontSize: '11px', minWidth: '50px', textAlign: 'center' } }, '\u2713')
                    : CE('button', { style: { background: '#14532d', color: '#86efac', border: 'none', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer', fontSize: '11px', minWidth: '50px' },
                        onClick: function() { addMapOidDirect(entry); } }, '+ Prida\u0165')
                );
              })
            )
          ),
          editDevOids.length > 0 && CE('div', { style: { marginBottom: '10px' } },
            editDevOids.map(function(o) {
              var lv = snmpLatest.find(function(v) { return v.oid_id === o.id; });
              var isEditing = mapEditingOid && mapEditingOid.id === o.id;
              if (isEditing) {
                return CE('div', { key: o.id, style: { display: 'flex', gap: '5px', alignItems: 'center', padding: '5px 8px', background: '#1e293b', borderRadius: '5px', marginBottom: '3px', flexWrap: 'wrap' } },
                  CE('input', { type: 'text', value: mapEditingOid.name, placeholder: 'N\u00E1zov', style: { background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: '4px', padding: '2px 6px', fontSize: '11px', width: '110px' }, onChange: function(ev) { setMapEditingOid(function(m) { return Object.assign({}, m, { name: ev.target.value }); }); } }),
                  CE('input', { type: 'text', value: mapEditingOid.unit, placeholder: 'Jedn.', style: { background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: '4px', padding: '2px 6px', fontSize: '11px', width: '55px' }, onChange: function(ev) { setMapEditingOid(function(m) { return Object.assign({}, m, { unit: ev.target.value }); }); } }),
                  CE('input', { type: 'number', value: mapEditingOid.multiplier, placeholder: 'Mult.', step: '0.01', style: { background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: '4px', padding: '2px 6px', fontSize: '11px', width: '60px' }, onChange: function(ev) { setMapEditingOid(function(m) { return Object.assign({}, m, { multiplier: ev.target.value }); }); } }),
                  CE('button', { style: { background: '#166534', color: '#86efac', border: 'none', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer', fontSize: '11px' }, onClick: function() { updateMapOid(o.id, { name: mapEditingOid.name, unit: mapEditingOid.unit || null, multiplier: parseFloat(mapEditingOid.multiplier) || 1 }); } }, '\uD83D\uDCBE Ulo\u017Ei\u0165'),
                  CE('button', { style: { background: '#374151', color: '#94a3b8', border: 'none', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer', fontSize: '11px' }, onClick: function() { setMapEditingOid(null); } }, 'Zru\u0161i\u0165')
                );
              }
              return CE('div', { key: o.id, style: { display: 'flex', gap: '6px', alignItems: 'center', padding: '5px 8px', background: '#0f172a', borderRadius: '5px', marginBottom: '3px', fontSize: '12px' } },
                CE('span', { style: { color: '#94a3b8', minWidth: '90px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, o.name),
                CE('span', { style: { color: '#64748b', fontFamily: 'monospace', fontSize: '10px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, o.oid),
                lv
                  ? CE('span', { title: lv.polled_at ? new Date(lv.polled_at).toLocaleTimeString() : '', style: { color: '#22c55e', fontFamily: 'monospace', fontWeight: '600', fontSize: '11px', minWidth: '72px', textAlign: 'right' } }, formatOidValue(lv.value, o.unit, o.oid) || lv.raw_value || '\u2014')
                  : CE('span', { style: { color: '#475569', fontSize: '11px', minWidth: '72px', textAlign: 'right' } }, '\u2014'),
                CE('span', { style: { color: o.enabled ? '#22c55e' : '#ef4444', fontSize: '10px' } }, o.enabled ? 'ON' : 'OFF'),
                CE('button', { title: 'Upravi\u0165', style: { background: '#1e3a5f', color: '#93c5fd', border: 'none', borderRadius: '4px', padding: '2px 5px', cursor: 'pointer', fontSize: '11px' }, onClick: function() { setMapEditingOid({ id: o.id, name: o.name, unit: o.unit || '', multiplier: String(o.multiplier != null ? o.multiplier : 1) }); } }, '\u270F\uFE0F'),
                CE('button', { style: { background: '#7f1d1d', color: '#fca5a5', border: 'none', borderRadius: '4px', padding: '2px 5px', cursor: 'pointer', fontSize: '11px' }, onClick: function() { delMapEditDevOid(o.id); } }, '\u2716')
              );
            })
          ),
          CE('div', { style: { background: '#0f172a', borderRadius: '6px', padding: '10px', border: '1px solid #334155' } },
            CE('div', { style: { fontSize: '11px', color: '#64748b', marginBottom: '8px' } }, '+ Prida\u0165 OID'),
            CE('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '6px' } },
              CE('div', null, CE('label', { style: lStyle }, 'N\u00E1zov'), CE('input', { type: 'text', value: newOidForm.name, placeholder: 'napr. CPU Load', style: iStyle, onChange: function(ev) { setNewOidForm(function(f) { return Object.assign({}, f, { name: ev.target.value }); }); } })),
              CE('div', null, CE('label', { style: lStyle }, 'OID'), CE('input', { type: 'text', value: newOidForm.oid, placeholder: '1.3.6.1.4.1.14988...', style: iStyle, onChange: function(ev) { setNewOidForm(function(f) { return Object.assign({}, f, { oid: ev.target.value }); }); } }))
            ),
            CE('div', { style: { display: 'grid', gridTemplateColumns: '1fr 80px 80px 1fr', gap: '6px', marginBottom: '8px' } },
              CE('div', null, CE('label', { style: lStyle }, 'Jednotka'), CE('input', { type: 'text', value: newOidForm.unit, placeholder: 'napr. %', style: iStyle, onChange: function(ev) { setNewOidForm(function(f) { return Object.assign({}, f, { unit: ev.target.value }); }); } })),
              CE('div', null, CE('label', { style: lStyle }, 'Multiplik\u00E1tor'), CE('input', { type: 'number', value: newOidForm.multiplier, style: iStyle, onChange: function(ev) { setNewOidForm(function(f) { return Object.assign({}, f, { multiplier: ev.target.value }); }); } })),
              CE('div', null, CE('label', { style: lStyle }, 'Kateg\u00F3ria'), CE('select', { value: newOidForm.category, style: iStyle, onChange: function(ev) { setNewOidForm(function(f) { return Object.assign({}, f, { category: ev.target.value }); }); } }, CE('option', { value: 'general' }, 'General'), CE('option', { value: 'cpu' }, 'CPU'), CE('option', { value: 'memory' }, 'Memory'), CE('option', { value: 'signal' }, 'Signal'), CE('option', { value: 'power' }, 'Power'), CE('option', { value: 'interface' }, 'Interface'))),
              CE('button', { style: { background: '#1e40af', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', alignSelf: 'flex-end' }, onClick: addMapEditDevOid }, '+ Prida\u0165')
            )
          )
        ),
        editDevTab === 'notes' && CE('div', null,
          CE('div', { style: { marginBottom: '12px' } }, CE('label', { style: lStyle }, 'Popis zariadenia'), CE('textarea', { value: editDevNotes.description, rows: 3, style: Object.assign({}, iStyle, { resize: 'vertical', fontFamily: 'inherit' }), onChange: function(ev) { setEditDevNotes(function(f) { return Object.assign({}, f, { description: ev.target.value }); }); }, placeholder: 'Kr\u00E1tky popis zariadenia...' })),
          CE('div', null, CE('label', { style: lStyle }, 'Intern\u00E1 pozn\u00E1mka (vidite\u013En\u00E1 len pre administr\u00E1torov)'), CE('textarea', { value: editDevNotes.notes, rows: 5, style: Object.assign({}, iStyle, { resize: 'vertical', fontFamily: 'inherit' }), onChange: function(ev) { setEditDevNotes(function(f) { return Object.assign({}, f, { notes: ev.target.value }); }); }, placeholder: 'Technick\u00E9 pozn\u00E1mky, hist\u00F3ria servisu, hesl\u00E1...' }))
        ),
        editDevTab === 'wifi' && CE('div', null,
          CE('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' } },
            CE('div', null, CE('label', { style: lStyle }, 'Azimut ant\u00E9ny (\u00B0)'), CE('input', { type: 'number', value: editDevWifi.azimuth, placeholder: '0-360', style: iStyle, onChange: function(ev) { setEditDevWifi(function(f) { return Object.assign({}, f, { azimuth: ev.target.value }); }); } })),
            CE('div', null, CE('label', { style: lStyle }, 'Sklon ant\u00E9ny (\u00B0)'), CE('input', { type: 'number', value: editDevWifi.tilt, placeholder: '0 = vodorovne', style: iStyle, onChange: function(ev) { setEditDevWifi(function(f) { return Object.assign({}, f, { tilt: ev.target.value }); }); } }))
          ),
          CE('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' } },
            CE('div', null, CE('label', { style: lStyle }, 'Polariz\u00E1cia'), CE('select', { value: editDevWifi.polarization, style: iStyle, onChange: function(ev) { setEditDevWifi(function(f) { return Object.assign({}, f, { polarization: ev.target.value }); }); } }, CE('option', { value: '' }, '— Nevybran\u00E9 —'), CE('option', { value: 'H' }, 'Horizont\u00E1lna'), CE('option', { value: 'V' }, 'Vertik\u00E1lna'), CE('option', { value: 'HV' }, 'MIMO H+V'))),
            CE('div', null, CE('label', { style: lStyle }, 'Frekvencia (GHz)'), CE('input', { type: 'text', value: editDevWifi.frequency, placeholder: 'napr. 5.8', style: iStyle, onChange: function(ev) { setEditDevWifi(function(f) { return Object.assign({}, f, { frequency: ev.target.value }); }); } }))
          ),
          CE('div', null, CE('label', { style: lStyle }, 'SSID (názov siete)'), CE('input', { type: 'text', value: editDevWifi.ssid, placeholder: 'napr. MyNetwork-5G', style: iStyle, onChange: function(ev) { setEditDevWifi(function(f) { return Object.assign({}, f, { ssid: ev.target.value }); }); } }))
        ),
        editDevTab === 'maintenance' && CE('div', null,
          CE('div', { style: { marginBottom: '16px', color: '#94a3b8', fontSize: '13px' } },
            editDevModal.device.maintenance_until
              ? CE('div', { style: { background: '#422006', border: '1px solid #92400e', borderRadius: '6px', padding: '10px 14px', color: '#fbbf24', marginBottom: '12px' } }, '\uD83D\uDEE0 Zariadenie je v re\u017Eime \u00FAdržby', new Date(editDevModal.device.maintenance_until).getFullYear() === 9999 ? ' (indefinit\u00EDvne)' : ' do ' + new Date(editDevModal.device.maintenance_until).toLocaleString('sk-SK'))
              : CE('div', { style: { color: '#22c55e', marginBottom: '12px' } }, '\u2705 Zariadenie sa akt\u00EDvne monitoruje'),
            CE('div', { style: { fontSize: '11px', color: '#64748b', marginBottom: '10px', textTransform: 'uppercase' } }, 'Nastavi\u0165 \u00FAdržbu:'),
            CE('div', { style: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' } },
              [[30,'30 min'],[60,'1 hod'],[120,'2 hod'],[240,'4 hod'],[480,'8 hod'],[0,'Indefinit\u00EDvne']].map(function(opt) {
                return CE('button', { key: opt[0], style: { background: '#1e3a5f', color: '#93c5fd', border: 'none', borderRadius: '6px', padding: '8px 14px', cursor: 'pointer', fontSize: '13px' }, onClick: function() { setMapMaintenance(opt[0]); } }, '\uD83D\uDEE0 ' + opt[1]);
              })
            ),
            CE('button', { style: { background: '#14532d', color: '#86efac', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }, onClick: function() { setMapMaintenance(-1); } }, '\u2705 Zru\u0161i\u0165 \u00FAdržbu')
          )
        ),
        editDevTab === 'graphs' && CE('div', null,
          editDevOids.length === 0
            ? CE('p', { style: { color: '#475569', fontSize: '13px' } }, 'Najprv pridajte SNMP OID v z\u00E1lo\u017Eke SNMP.')
            : CE('div', null,
                CE('div', { style: { marginBottom: '10px' } },
                  CE('label', { style: lStyle }, 'Vyber OID pre graf:'),
                  CE('select', { style: iStyle, onChange: function(ev) { if (ev.target.value) loadMapSnmpHistory(ev.target.value, 24); } },
                    CE('option', { value: '' }, '— Vyber OID —'),
                    editDevOids.map(function(o) { return CE('option', { key: o.id, value: o.id }, o.name + ' (' + o.oid + ')'); })
                  )
                ),
                snmpHistory.length > 0
                  ? CE('div', null,
                      CE('div', { style: { fontSize: '12px', color: '#64748b', marginBottom: '6px' } }, 'Posledn\u00FDch 24h \u2014 ' + snmpHistory.length + ' meran\u00ED'),
                      CE('div', { style: { background: '#0f172a', borderRadius: '6px', padding: '10px', overflowX: 'auto' } },
                        CE('canvas', { id: 'snmp-chart-map', width: '500', height: '200',
                          ref: function(canvas) {
                            if (!canvas || snmpHistory.length === 0) return;
                            var labels = snmpHistory.map(function(v) { return new Date(v.polled_at).toLocaleTimeString('sk-SK', { hour: '2-digit', minute: '2-digit' }); });
                            var values = snmpHistory.map(function(v) { return v.value; });
                            if (window.Chart) {
                              if (canvas._chartInstance) canvas._chartInstance.destroy();
                              canvas._chartInstance = new window.Chart(canvas, { type: 'line', data: { labels: labels, datasets: [{ label: snmpHistory[0] && snmpHistory[0].name || 'Hodnota', data: values, borderColor: '#38bdf8', backgroundColor: 'rgba(56,189,248,0.1)', tension: 0.3, pointRadius: 2, fill: true }] }, options: { responsive: false, scales: { x: { ticks: { color: '#64748b', maxRotation: 45 } }, y: { ticks: { color: '#64748b' } } }, plugins: { legend: { labels: { color: '#94a3b8' } } } } });
                            }
                          }
                        })
                      )
                    )
                  : CE('p', { style: { color: '#475569', fontSize: '12px' } }, 'Vyber OID na zobrazenie grafu.')
              )
        ),
        // TAB: alerts
        editDevTab === 'alerts' && CE('div', null,
          CE('div', { style: { marginBottom: '14px' } },
            CE('h4', { style: { margin: '0 0 10px', color: '#f59e0b', fontSize: '13px' } }, '\uD83D\uDEA8 Pravidl\u00E1 alertov'),
            mapAlertRules.length === 0
              ? CE('p', { style: { color: '#475569', fontSize: '12px' } }, 'Zatia\u013E \u017Eiadne pravidl\u00E1.')
              : CE('div', null, mapAlertRules.map(function(rule) {
                  var isEd = mapEditingAlert && mapEditingAlert.id === rule.id;
                  if (isEd) {
                    return CE('div', { key: rule.id, style: { padding: '8px 10px', background: '#172033', borderRadius: '6px', marginBottom: '6px', border: '1px solid #38bdf8', fontSize: '12px' } },
                      CE('div', { style: { marginBottom: '6px' } },
                        CE('label', { style: { fontSize: '10px', color: '#64748b', display: 'block', marginBottom: '2px', textTransform: 'uppercase' } }, 'N\u00E1zov'),
                        CE('input', { type: 'text', value: mapEditingAlert.name, style: Object.assign({}, iStyle, { fontSize: '12px', padding: '4px 7px' }),
                          onChange: function(ev) { var v = ev.target.value; setMapEditingAlert(function(f) { return Object.assign({}, f, { name: v }); }); } })
                      ),
                      rule.metric === 'ping'
                        ? CE('div', { style: { marginBottom: '6px' } },
                            CE('label', { style: { fontSize: '10px', color: '#64748b', display: 'block', marginBottom: '2px', textTransform: 'uppercase' } }, '\u23F1 \u010cas v\u00fdpadku pred alertom'),
                            CE('select', { value: parseFloat(mapEditingAlert.ping_minutes) || 10, style: Object.assign({}, iStyle, { fontSize: '12px', padding: '4px 7px' }),
                              onChange: function(ev) { var v = parseFloat(ev.target.value); setMapEditingAlert(function(f) { return Object.assign({}, f, { ping_minutes: v }); }); } },
                              PING_OPTS.map(function(o) { return CE('option', { key: o[0], value: o[0] }, o[1]); })
                            )
                          )
                        : CE('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '6px' } },
                            CE('div', null,
                              CE('label', { style: { fontSize: '10px', color: '#64748b', display: 'block', marginBottom: '2px', textTransform: 'uppercase' } }, 'Oper\u00E1tor'),
                              CE('select', { value: mapEditingAlert.operator, style: Object.assign({}, iStyle, { fontSize: '12px', padding: '4px 7px' }),
                                onChange: function(ev) { var v = ev.target.value; setMapEditingAlert(function(f) { return Object.assign({}, f, { operator: v }); }); } },
                                CE('option', { value: '>' }, '>'), CE('option', { value: '>=' }, '>='),
                                CE('option', { value: '<' }, '<'), CE('option', { value: '<=' }, '<='),
                                CE('option', { value: '==' }, '==')
                              )
                            ),
                            CE('div', null,
                              CE('label', { style: { fontSize: '10px', color: '#64748b', display: 'block', marginBottom: '2px', textTransform: 'uppercase' } }, 'Hodnota (pr\u00E1h)'),
                              CE('input', { type: 'number', value: mapEditingAlert.threshold, style: Object.assign({}, iStyle, { fontSize: '12px', padding: '4px 7px' }),
                                onChange: function(ev) { var v = ev.target.value; setMapEditingAlert(function(f) { return Object.assign({}, f, { threshold: v }); }); } })
                            )
                          ),
                      CE('div', { style: { marginBottom: '6px' } },
                        CE('label', { style: { fontSize: '10px', color: '#64748b', display: 'block', marginBottom: '2px', textTransform: 'uppercase' } }, '\u{1F4E7} Email príjemcovia (oddelené čiarkou)'),
                        CE('input', { type: 'text', value: mapEditingAlert.notify_emails, placeholder: 'admin@firma.sk, tech@firma.sk', style: Object.assign({}, iStyle, { fontSize: '12px', padding: '4px 7px' }),
                          onChange: function(ev) { var v = ev.target.value; setMapEditingAlert(function(f) { return Object.assign({}, f, { notify_emails: v }); }); } })
                      ),
                      CE('div', { style: { marginBottom: '6px' } },
                        CE('label', { style: { fontSize: '10px', color: '#64748b', display: 'block', marginBottom: '2px', textTransform: 'uppercase' } }, '\u{1F4F1} SMS príjemcovia (oddelené čiarkou)'),
                        CE('input', { type: 'text', value: mapEditingAlert.notify_phones, placeholder: '+421901123456, +421902654321', style: Object.assign({}, iStyle, { fontSize: '12px', padding: '4px 7px' }),
                          onChange: function(ev) { var v = ev.target.value; setMapEditingAlert(function(f) { return Object.assign({}, f, { notify_phones: v }); }); } })
                      ),
                      CE('div', { style: { display: 'flex', gap: '6px' } },
                        CE('button', { style: { background: '#16a34a', color: '#fff', border: 'none', borderRadius: '5px', padding: '4px 12px', cursor: 'pointer', fontSize: '11px', fontWeight: '700' },
                          onClick: function() {
                            var body = { name: mapEditingAlert.name, notify_emails: mapEditingAlert.notify_emails, notify_phones: mapEditingAlert.notify_phones };
                            if (rule.metric === 'ping') { body.ping_minutes = parseFloat(mapEditingAlert.ping_minutes) || 10; }
                            else { body.operator = mapEditingAlert.operator; body.threshold = parseFloat(mapEditingAlert.threshold); }
                            fetch('/api/alert-rules/' + rule.id, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
                              .then(function() {
                                fetch('/api/devices/' + editDevModal.device.id + '/alert-rules').then(function(r) { return r.json(); }).then(function(d) { setMapAlertRules(d.rules || []); });
                                setMapEditingAlert(null);
                              });
                          }
                        }, '\u2705 Ulo\u017Ei\u0165'),
                        CE('button', { style: { background: '#334155', color: '#94a3b8', border: 'none', borderRadius: '5px', padding: '4px 10px', cursor: 'pointer', fontSize: '11px' },
                          onClick: function() { setMapEditingAlert(null); }
                        }, 'Zru\u0161i\u0165')
                      )
                    );
                  }
                  return CE('div', { key: rule.id, style: { display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', background: '#0f172a', borderRadius: '6px', marginBottom: '6px', fontSize: '12px' } },
                    CE('span', { style: { flex: 1, color: '#e2e8f0' } },
                      rule.metric === 'ping'
                        ? '\uD83D\uDCF6 Ping — alert po ' + pingLabel(rule.ping_minutes) + ' v\u00fdpadku'
                        : '\uD83D\uDCCA ' + (rule.oid_name || 'OID') + ' ' + rule.operator + ' ' + rule.threshold + (rule.oid_unit ? ' ' + rule.oid_unit : '')
                    ),
                    CE('span', { style: { color: '#64748b', fontSize: '11px' } }, rule.name),
                    CE('button', { style: { background: 'none', border: 'none', color: '#38bdf8', cursor: 'pointer', fontSize: '13px', padding: '0 4px' },
                      onClick: function() { setMapEditingAlert({ id: rule.id, name: rule.name, operator: rule.operator || '>', threshold: rule.threshold != null ? rule.threshold : '', ping_minutes: rule.ping_minutes || 10, notify_emails: rule.notify_emails || '', notify_phones: rule.notify_phones || '' }); }
                    }, '\u270F\uFE0F'),
                    CE('button', { style: { background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px', padding: '0 4px' },
                      onClick: function() {
                        fetch('/api/alert-rules/' + rule.id, { method: 'DELETE' }).then(function() {
                          setMapAlertRules(function(prev) { return prev.filter(function(r) { return r.id !== rule.id; }); });
                        });
                      }
                    }, '\uD83D\uDDD1')
                  );
                }))
          ),
          CE('div', { style: { borderTop: '1px solid #1e293b', paddingTop: '12px' } },
            CE('h4', { style: { margin: '0 0 10px', color: '#94a3b8', fontSize: '12px', textTransform: 'uppercase' } }, '+ Nov\u00E9 pravidlo'),
            CE('div', { style: { marginBottom: '8px' } },
              CE('label', { style: lStyle }, 'N\u00E1zov pravidla'),
              CE('input', { type: 'text', value: mapNewAlert.name, style: iStyle, placeholder: 'napr. CPU pr\u00EDli\u0161 vysok\u00E9', onChange: function(ev) { var v = ev.target.value; setMapNewAlert(function(f) { return Object.assign({}, f, { name: v }); }); } })
            ),
            CE('div', { style: { marginBottom: '8px' } },
              CE('label', { style: lStyle }, 'Typ metriky'),
              CE('select', { value: mapNewAlert.metric, style: iStyle, onChange: function(ev) { var v = ev.target.value; setMapNewAlert(function(f) { return Object.assign({}, f, { metric: v, oid_id: '' }); }); } },
                CE('option', { value: 'ping' }, '\uD83D\uDCF6 Ping (dostupnos\u0165)'),
                CE('option', { value: 'snmp' }, '\uD83D\uDCCA SNMP hodnota')
              )
            ),
            mapNewAlert.metric === 'ping' && CE('div', { style: { marginBottom: '8px' } },
              CE('label', { style: lStyle }, '\u23F1 \u010cas v\u00fdpadku pred alertom'),
              CE('select', { value: parseFloat(mapNewAlert.ping_minutes) || 10, style: iStyle,
                onChange: function(ev) { var v = parseFloat(ev.target.value); setMapNewAlert(function(f) { return Object.assign({}, f, { ping_minutes: v }); }); } },
                PING_OPTS.map(function(o) { return CE('option', { key: o[0], value: o[0] }, o[1]); })
              )
            ),
            mapNewAlert.metric === 'snmp' && CE('div', null,
              CE('div', { style: { marginBottom: '8px' } },
                CE('label', { style: lStyle }, 'OID'),
                CE('select', { value: mapNewAlert.oid_id, style: iStyle, onChange: function(ev) { var v = ev.target.value; setMapNewAlert(function(f) { return Object.assign({}, f, { oid_id: v }); }); } },
                  CE('option', { value: '' }, '— Vyber OID —'),
                  editDevOids.map(function(o) { return CE('option', { key: o.id, value: o.id }, o.name + (o.unit ? ' (' + o.unit + ')' : '')); })
                )
              ),
              CE('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' } },
                CE('div', null, CE('label', { style: lStyle }, 'Oper\u00E1tor'),
                  CE('select', { value: mapNewAlert.operator, style: iStyle, onChange: function(ev) { var v = ev.target.value; setMapNewAlert(function(f) { return Object.assign({}, f, { operator: v }); }); } },
                    CE('option', { value: '>' }, '> v\u00E4\u010D\u0161\u00ED ako'),
                    CE('option', { value: '>=' }, '>= v\u00E4\u010D\u0161\u00ED-rovn\u00E9'),
                    CE('option', { value: '<' }, '< men\u0161\u00ED ako'),
                    CE('option', { value: '<=' }, '<= men\u0161\u00ED-rovn\u00E9'),
                    CE('option', { value: '==' }, '== rovn\u00E9')
                  )
                ),
                CE('div', null, CE('label', { style: lStyle }, 'Hodnota (pr\u00E1h)'),
                  CE('input', { type: 'number', value: mapNewAlert.threshold, style: iStyle, placeholder: 'napr. 80', onChange: function(ev) { var v = ev.target.value; setMapNewAlert(function(f) { return Object.assign({}, f, { threshold: v }); }); } })
                )
              )
            ),
            CE('div', { style: { marginBottom: '8px' } },
              CE('label', { style: lStyle }, '\u{1F4E7} Email príjemcovia (oddelené čiarkou, prázdne = globálne)'),
              CE('input', { type: 'text', value: mapNewAlert.notify_emails, placeholder: 'admin@firma.sk, tech@firma.sk', style: iStyle,
                onChange: function(ev) { var v = ev.target.value; setMapNewAlert(function(f) { return Object.assign({}, f, { notify_emails: v }); }); } })
            ),
            CE('div', { style: { marginBottom: '8px' } },
              CE('label', { style: lStyle }, '\u{1F4F1} SMS príjemcovia (oddelené čiarkou, prázdne = globálne)'),
              CE('input', { type: 'text', value: mapNewAlert.notify_phones, placeholder: '+421901123456, +421902654321', style: iStyle,
                onChange: function(ev) { var v = ev.target.value; setMapNewAlert(function(f) { return Object.assign({}, f, { notify_phones: v }); }); } })
            ),
            CE('button', { style: { background: '#f59e0b', color: '#000', border: 'none', borderRadius: '6px', padding: '7px 16px', cursor: 'pointer', fontSize: '12px', fontWeight: '700', marginTop: '4px' },
              onClick: function() {
                if (!mapNewAlert.name) { alert('Zadaj n\u00E1zov pravidla'); return; }
                if (mapNewAlert.metric === 'snmp' && !mapNewAlert.oid_id) { alert('Vyber OID'); return; }
                if (mapNewAlert.metric === 'snmp' && !mapNewAlert.threshold) { alert('Zadaj pr\u00E1h hodnoty'); return; }
                var body = { name: mapNewAlert.name, metric: mapNewAlert.metric, enabled: true, notify_emails: mapNewAlert.notify_emails, notify_phones: mapNewAlert.notify_phones };
                if (mapNewAlert.metric === 'ping') { body.ping_minutes = parseFloat(mapNewAlert.ping_minutes) || 10; }
                else { body.oid_id = parseInt(mapNewAlert.oid_id); body.operator = mapNewAlert.operator; body.threshold = parseFloat(mapNewAlert.threshold); }
                fetch('/api/devices/' + editDevModal.device.id + '/alert-rules', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
                  .then(function(r) { return r.json(); })
                  .then(function() {
                    fetch('/api/devices/' + editDevModal.device.id + '/alert-rules').then(function(r) { return r.json(); }).then(function(d) { setMapAlertRules(d.rules || []); });
                    setMapNewAlert({ name: '', metric: 'ping', oid_id: '', operator: '>', threshold: '', ping_minutes: 10, notify_emails: '', notify_phones: '' });
                  });
              }
            }, '+ Prida\u0165 pravidlo')
          )
        ),
        editDevTab === 'service' && CE('div', null,
          CE('div', { style: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' } },
            CE('h4', { style: { margin: 0, color: '#a78bfa', fontSize: '13px' } }, '\uD83C\uDFAB Tickety zariadenia'),
            CE('button', { style: { marginLeft: 'auto', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: '6px', padding: '5px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' },
              onClick: function() { onOpenTicket(editDevModal.device); }
            }, '+ Nov\u00FD ticket')
          ),
          mapDevTicketsLoading
            ? CE('p', { style: { color: '#64748b', fontSize: '12px' } }, '\u23F3 Na\u010D\u00EDtavam...')
            : mapDevTickets.length === 0
              ? CE('p', { style: { color: '#475569', fontSize: '12px' } }, '\u017diadne tickety pre toto zariadenie.')
              : CE('div', null, mapDevTickets.map(function(t) {
                  var prioColor = { critical: '#ef4444', high: '#f97316', medium: '#eab308', low: '#22c55e' }[t.priority] || '#64748b';
                  var statColor = { new: '#38bdf8', in_progress: '#f97316', resolved: '#22c55e', closed: '#64748b' }[t.status] || '#64748b';
                  var statLabel = { new: 'Nov\u00FD', in_progress: 'V rie\u0161en\u00ED', resolved: 'Vyrie\u0161en\u00FD', closed: 'Uzavret\u00FD' }[t.status] || t.status;
                  var prioLabel = { critical: '\uD83D\uDEA8 Kritick\u00E1', high: '\uD83D\uDD34 Vysok\u00E1', medium: '\uD83D\uDFE1 Stredn\u00E1', low: '\uD83D\uDFE2 N\u00EDzka' }[t.priority] || t.priority;
                  return CE('div', { key: t.id,
                    style: { background: '#0f172a', border: '1px solid #1e293b', borderRadius: '6px', padding: '9px 12px', marginBottom: '6px', fontSize: '12px', cursor: 'pointer' },
                    onClick: function() { onViewTicket(t.id); }
                  },
                    CE('div', { style: { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', flexWrap: 'wrap' } },
                      CE('span', { style: { background: prioColor, color: '#fff', borderRadius: '4px', padding: '1px 7px', fontSize: '10px', fontWeight: '700' } }, prioLabel),
                      CE('span', { style: { background: statColor, color: '#fff', borderRadius: '4px', padding: '1px 7px', fontSize: '10px', fontWeight: '700' } }, statLabel),
                      CE('span', { style: { color: '#64748b', fontSize: '11px', marginLeft: 'auto' } }, new Date(t.created_at + 'Z').toLocaleString('sk-SK'))
                    ),
                    CE('div', { style: { color: '#e2e8f0', fontWeight: '600', marginBottom: '2px' } }, '#' + t.id + ' ' + t.title),
                    CE('div', { style: { color: '#64748b' } }, (t.assigned_to_name || t.assigned_full_name) ? '\uD83D\uDC64 ' + (t.assigned_to_name || t.assigned_full_name) : '\uD83D\uDC64 Nepriraden\u00FD')
                  );
                }))
        ),
        // ── DIAGNOSTICS TAB (Map modal) ──
        editDevTab === 'diag' && CE('div', null,
          CE('h4', { style: { margin: '0 0 14px', color: '#34d399', fontSize: '13px' } }, '\uD83D\uDD0D Diagnostika \u2014 ' + (editDevModal.device.name || editDevModal.device.ip_address || 'N/A')),
          // Ping section
          CE('div', { style: { background: '#0f172a', border: '1px solid #1e293b', borderRadius: '10px', padding: '14px', marginBottom: '14px' } },
            CE('div', { style: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' } },
              CE('span', { style: { fontSize: '15px' } }, '\uD83D\uDCF6'),
              CE('span', { style: { color: '#e2e8f0', fontWeight: '700', fontSize: '13px' } }, 'Ping test'),
              mapDiagPingOk !== null && CE('span', { style: { marginLeft: '6px', background: mapDiagPingOk ? '#14532d' : '#7f1d1d', color: mapDiagPingOk ? '#86efac' : '#fca5a5', borderRadius: '5px', padding: '1px 9px', fontSize: '11px', fontWeight: '700' } }, mapDiagPingOk ? '\u2705 Dostupn\u00E9' : '\u274C Nedostupn\u00E9')
            ),
            CE('div', { style: { display: 'flex', gap: '6px', marginBottom: '8px' } },
              CE('input', { type: 'text', value: mapDiagCustomIp, placeholder: editDevModal.device.ip_address || 'IP adresa alebo hostname', style: { flex: 1, background: '#1e293b', border: '1px solid #334155', borderRadius: '6px', padding: '6px 10px', color: '#e2e8f0', fontSize: '12px' },
                onChange: function(ev) { setMapDiagCustomIp(ev.target.value); }
              }),
              CE('button', { style: { background: mapDiagPingLoading ? '#334155' : '#0369a1', color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 14px', cursor: mapDiagPingLoading ? 'wait' : 'pointer', fontSize: '12px', fontWeight: '600', opacity: mapDiagPingLoading ? 0.7 : 1, whiteSpace: 'nowrap' },
                disabled: mapDiagPingLoading,
                onClick: function() {
                  var ip = mapDiagCustomIp.trim() || editDevModal.device.ip_address || '';
                  if (!ip) return;
                  setMapDiagPingLoading(true); setMapDiagPing(''); setMapDiagPingOk(null);
                  var url = '/api/devices/' + editDevModal.device.id + '/diag/ping/stream?count=5&target_ip=' + encodeURIComponent(ip);
                  fetch(url, { headers: { 'Authorization': 'Bearer ' + localStorage.getItem('nw_token') } })
                    .then(function(res) {
                      if (!res.ok) { setMapDiagPing('HTTP chyba: ' + res.status); setMapDiagPingLoading(false); return; }
                      var reader = res.body.getReader(); var decoder = new TextDecoder();
                      function pump() {
                        reader.read().then(function(result) {
                          if (result.done) { setMapDiagPingLoading(false); return; }
                          var chunk = decoder.decode(result.value, { stream: true });
                          if (chunk.indexOf('__RC__:') !== -1) { setMapDiagPingOk(chunk.indexOf('__RC__:0') !== -1); chunk = chunk.replace(/__RC__:\d+\n?/g, ''); }
                          if (chunk) setMapDiagPing(function(p) { return p + chunk; });
                          pump();
                        }).catch(function(e) { setMapDiagPing(function(p) { return p + '\nChyba: ' + e; }); setMapDiagPingLoading(false); });
                      }
                      pump();
                    })
                    .catch(function(e) { setMapDiagPing('Chyba: ' + String(e)); setMapDiagPingLoading(false); });
                }
              }, mapDiagPingLoading ? '\u23F3 Ping...' : '\uD83D\uDCF6 Spusti\u0165')
            ),
            CE('pre', { style: { background: '#020617', color: mapDiagPingOk === false ? '#fca5a5' : '#4ade80', borderRadius: '6px', padding: '10px', fontSize: '11px', fontFamily: 'monospace', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', minHeight: '60px', maxHeight: '180px', overflowY: 'auto' } },
              mapDiagPing || (mapDiagPingLoading ? '' : '\u2014 Stla\u010dte Spusti\u0165 pre ping \u2014')
            )
          ),
          // DNS section
          CE('div', { style: { background: '#0f172a', border: '1px solid #1e293b', borderRadius: '10px', padding: '14px' } },
            CE('div', { style: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' } },
              CE('span', { style: { fontSize: '15px' } }, '\uD83C\uDF10'),
              CE('span', { style: { color: '#e2e8f0', fontWeight: '700', fontSize: '13px' } }, 'DNS Kontrola'),
              CE('span', { style: { marginLeft: '6px', background: '#1e3a5f', color: '#7dd3fc', borderRadius: '4px', padding: '1px 7px', fontSize: '10px', fontWeight: '600' } }, 'z pohľadu servera 10.10.10.18')
            ),
            CE('p', { style: { color: '#64748b', fontSize: '11px', margin: '0 0 10px' } }, 'Test prebieha na serveri — nie priamo na zariadení. Overuje či server vie preložiť zadanú doménu alebo IP.'),
            CE('div', { style: { display: 'flex', gap: '6px', marginBottom: '8px' } },
              CE('input', { type: 'text', value: mapDiagDnsHost, placeholder: 'hostname (napr. google.com)', style: { flex: 1, background: '#1e293b', border: '1px solid #334155', borderRadius: '6px', padding: '6px 10px', color: '#e2e8f0', fontSize: '12px' },
                onChange: function(ev) { setMapDiagDnsHost(ev.target.value); },
                onKeyDown: function(ev) { if (ev.key === 'Enter' && mapDiagDnsHost.trim() && !mapDiagDnsLoading) { setMapDiagDnsLoading(true); setMapDiagDns(null); fetch('/api/devices/' + editDevModal.device.id + '/diag/dns?hostname=' + encodeURIComponent(mapDiagDnsHost.trim()), { method: 'POST', headers: { Authorization: 'Bearer ' + localStorage.getItem('nw_token') } }).then(function(r){return r.json();}).then(function(d){setMapDiagDns(d);setMapDiagDnsLoading(false);}).catch(function(e){setMapDiagDns({ok:false,error:String(e)});setMapDiagDnsLoading(false);}); } }
              }),
              CE('button', { style: { background: mapDiagDnsLoading ? '#334155' : '#7c3aed', color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 14px', cursor: mapDiagDnsLoading ? 'wait' : 'pointer', fontSize: '12px', fontWeight: '600', opacity: (!mapDiagDnsHost.trim() || mapDiagDnsLoading) ? 0.6 : 1 },
                disabled: !mapDiagDnsHost.trim() || mapDiagDnsLoading,
                onClick: function() {
                  setMapDiagDnsLoading(true); setMapDiagDns(null);
                  fetch('/api/devices/' + editDevModal.device.id + '/diag/dns?hostname=' + encodeURIComponent(mapDiagDnsHost.trim()), { method: 'POST', headers: { Authorization: 'Bearer ' + localStorage.getItem('nw_token') } })
                    .then(function(r) { return r.json(); })
                    .then(function(d) { setMapDiagDns(d); setMapDiagDnsLoading(false); })
                    .catch(function(e) { setMapDiagDns({ ok: false, error: String(e) }); setMapDiagDnsLoading(false); });
                }
              }, mapDiagDnsLoading ? '\u23F3...' : '\uD83D\uDD0D H\u013Eada\u0165')
            ),
            mapDiagDns && CE('div', null,
              mapDiagDns.error && CE('p', { style: { color: '#fca5a5', fontSize: '12px', margin: '0 0 6px' } }, mapDiagDns.error),
              mapDiagDns.results && mapDiagDns.results.map(function(r, i) {
                return CE('div', { key: i, style: { marginBottom: '8px' } },
                  CE('div', { style: { display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '4px' } },
                    CE('span', { style: { background: r.ok ? '#14532d' : '#7f1d1d', color: r.ok ? '#86efac' : '#fca5a5', borderRadius: '4px', padding: '1px 8px', fontSize: '11px', fontWeight: '700' } }, r.ok ? '\u2705 OK' : '\u274C Chyba'),
                    CE('span', { style: { color: '#64748b', fontSize: '11px' } }, r.resolver)
                  ),
                  CE('pre', { style: { background: '#020617', color: '#94a3b8', borderRadius: '6px', padding: '8px', fontSize: '11px', fontFamily: 'monospace', overflowX: 'auto', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: '100px', overflowY: 'auto' } }, r.output)
                );
              })
            )
          )
        ),

        // ── IP Route (only in Diagnostics tab) ──
        editDevTab === 'diag' && CE('div', { style: { marginTop: '18px' } },
          CE('p', { style: { fontSize: '12px', fontWeight: '700', color: '#94a3b8', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.05em' } }, '\uD83D\uDDFA\uFE0F IP Route z poh\u013Eadu servera'),
          CE('div', { style: { display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' } },
            CE('input', { type: 'text', value: mapDiagRouteIp, placeholder: editDevModal.device.ip_address || 'IP adresa', style: { flex: 1, background: '#1e293b', border: '1px solid #334155', borderRadius: '6px', padding: '6px 10px', color: '#e2e8f0', fontSize: '12px' },
              onChange: function(ev) { setMapDiagRouteIp(ev.target.value); } }),
            CE('button', { style: { background: mapDiagRouteLoading ? '#334155' : '#0f766e', color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 14px', cursor: mapDiagRouteLoading ? 'wait' : 'pointer', fontSize: '12px', fontWeight: '600', opacity: mapDiagRouteLoading ? 0.7 : 1, whiteSpace: 'nowrap' },
              disabled: mapDiagRouteLoading,
              onClick: function() {
                var ip = mapDiagRouteIp.trim() || editDevModal.device.ip_address || '';
                if (!ip) return;
                setMapDiagRouteLoading(true); setMapDiagRoute('');
                fetch('/api/devices/' + editDevModal.device.id + '/diag/route/stream?target_ip=' + encodeURIComponent(ip), { headers: { Authorization: 'Bearer ' + localStorage.getItem('nw_token') } })
                  .then(function(res) {
                    var reader = res.body.getReader(); var dec = new TextDecoder();
                    function read() { reader.read().then(function(result) {
                      if (result.done) { setMapDiagRouteLoading(false); return; }
                      var chunk = dec.decode(result.value, { stream: true });
                      if (chunk.indexOf('__DONE__') !== -1) { chunk = chunk.replace('__DONE__\n',''); setMapDiagRouteLoading(false); }
                      if (chunk) setMapDiagRoute(function(p) { return p + chunk; });
                      if (!result.done) read();
                    }).catch(function(e) { setMapDiagRoute(function(p) { return p + '\nChyba: ' + e; }); setMapDiagRouteLoading(false); }); }
                    read();
                  }).catch(function(e) { setMapDiagRoute('Chyba: ' + String(e)); setMapDiagRouteLoading(false); });
              }}, mapDiagRouteLoading ? '\u23F3 Načítavam...' : '\uD83D\uDDFA\uFE0F Zobraziť trasu')
          ),
          CE('pre', { style: { background: '#020617', color: '#67e8f9', borderRadius: '6px', padding: '10px', fontSize: '11px', fontFamily: 'monospace', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', minHeight: '50px', maxHeight: '160px', overflowY: 'auto' } },
            mapDiagRoute || (mapDiagRouteLoading ? '' : '\u2014 Stlačte Zobraziť trasu \u2014'))
        ),

        // ── SSH Terminal button (only in Diagnostics tab) ──
        editDevTab === 'diag' && CE('div', { style: { marginTop: '16px', paddingTop: '14px', borderTop: '1px solid #1e293b' } },
          CE('button', { style: { background: '#1e293b', color: '#a78bfa', border: '1px solid #4c1d95', borderRadius: '7px', padding: '9px 18px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' },
            onClick: function() {
              var ip = editDevModal.device.ip_address || '';
              window.open('/ssh.html?v=4&host=' + encodeURIComponent(ip), '_blank');
            }}, '\uD83D\uDDA5\uFE0F Otvoriť SSH terminál pre ' + (editDevModal.device.name || editDevModal.device.ip_address))
        ),

        CE('div', { style: { display: 'flex', gap: '8px', marginTop: '20px', borderTop: '1px solid #334155', paddingTop: '16px' } },
          CE('button', { style: { background: '#334155', color: '#94a3b8', border: 'none', borderRadius: '6px', padding: '9px 20px', cursor: 'pointer', fontSize: '13px', flex: 1 }, onClick: function() { setEditDevModal(null); } }, 'Zru\u0161i\u0165'),
          CE('button', { style: { background: '#1e40af', color: '#fff', border: 'none', borderRadius: '6px', padding: '9px 20px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', flex: 2 }, onClick: saveMapEditDev }, '\u2705 Ulo\u017Ei\u0165 zariadenie')
        )
      )
    ),
    // Link modal
    mapLinkModal && CE('div', { style: modalOv },
      CE('div', { style: Object.assign({}, modalBox, { width: '420px' }) },
        CE('h3', { style: { margin: '0 0 16px', color: '#38bdf8', fontSize: '16px' } }, '\uD83D\uDD17 Prepojenie z: ' + mapLinkModal.device.name),
        CE('div', { style: { marginBottom: '12px' } },
          CE('label', { style: lStyle }, 'Zdrojov\u00fd interface (' + mapLinkModal.device.name + ')'),
          CE('select', { value: mapLinkData.srcIfaceId, style: iStyle,
            onChange: function(ev) { setMapLinkData(function(f) { return Object.assign({}, f, { srcIfaceId: ev.target.value }); }); } },
            CE('option', { value: '' }, '-- vyberte interface --'),
            mapLinkModal.ifaces.map(function(iface) {
              return CE('option', { key: iface.id, value: iface.id }, iface.name + (iface.speed_mbps ? ' (' + iface.speed_mbps + 'M)' : ''));
            })
          ),
          mapLinkModal.ifaces.length === 0 && CE('p', { style: { color: '#f59e0b', fontSize: '12px', marginTop: '4px' } }, '\u26A0\uFE0F Pridajte najprv interfacy k tomuto zariadeniu.')
        ),
        CE('div', { style: { marginBottom: '12px' } },
          CE('label', { style: lStyle }, 'Cie\u013eov\u00e9 zariadenie'),
          CE('select', { value: mapLinkData.tgtDeviceId, style: iStyle, onChange: function(ev) { onMapTgtDeviceChange(ev.target.value); } },
            CE('option', { value: '' }, '-- vyberte zariadenie --'),
            topo.devices.filter(function(d) { return d.id !== mapLinkModal.device.id; }).map(function(d) {
              return CE('option', { key: d.id, value: d.id }, d.name + ' (' + d.ip_address + ')');
            })
          )
        ),
        mapLinkData.tgtDeviceId && CE('div', { style: { marginBottom: '12px' } },
          CE('label', { style: lStyle }, 'Cie\u013eov\u00fd interface'),
          CE('select', { value: mapLinkData.tgtIfaceId, style: iStyle,
            onChange: function(ev) { setMapLinkData(function(f) { return Object.assign({}, f, { tgtIfaceId: ev.target.value }); }); } },
            CE('option', { value: '' }, '-- vyberte interface --'),
            mapTgtIfaces.map(function(iface) {
              return CE('option', { key: iface.id, value: iface.id }, iface.name + (iface.speed_mbps ? ' (' + iface.speed_mbps + 'M)' : ''));
            })
          ),
          mapTgtIfaces.length === 0 && CE('p', { style: { color: '#f59e0b', fontSize: '12px', marginTop: '4px' } }, '\u26A0\uFE0F Zariadenie nem\u00e1 interfacy.')
        ),
        CE('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' } },
          CE('div', null,
            CE('label', { style: lStyle }, 'Ozna\u010denie k\u00e1bla'),
            CE('input', { type: 'text', value: mapLinkData.cableLabel, placeholder: 'napr. CAB-001', style: iStyle,
              onChange: function(ev) { setMapLinkData(function(f) { return Object.assign({}, f, { cableLabel: ev.target.value }); }); } })
          ),
          CE('div', null,
            CE('label', { style: lStyle }, 'Typ k\u00e1bla'),
            CE('select', { value: mapLinkData.cableType, style: iStyle,
              onChange: function(ev) { setMapLinkData(function(f) { return Object.assign({}, f, { cableType: ev.target.value }); }); } },
              CE('option', { value: 'utp' }, 'UTP'), CE('option', { value: 'sfp' }, 'SFP / Fiber'),
              CE('option', { value: 'sfp+' }, 'SFP+ / 10G'), CE('option', { value: 'dac' }, 'DAC'),
              CE('option', { value: 'wifi' }, 'WiFi'), CE('option', { value: 'gpon' }, 'GPON / PON Fiber')
            )
          )
        ),
        CE('div', { style: { display: 'flex', gap: '8px' } },
          CE('button', { style: { flex: 1, background: '#334155', color: '#94a3b8', border: 'none', borderRadius: '6px', padding: '10px', cursor: 'pointer', fontSize: '13px' },
            onClick: function() { setMapLinkModal(null); setMapTgtIfaces([]); } }, 'Zru\u0161i\u0165'),
          CE('button', { style: { flex: 2, background: '#1e40af', color: '#fff', border: 'none', borderRadius: '6px', padding: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
            onClick: saveMapLink }, '\uD83D\uDD17 Pripoji\u0165')
        )
      )
    ),
    // Map container (relative so the toggle button can be absolutely positioned inside)
    // Vyska mapy: na pocitaci sa doteraz pouzivalo natvrdo 520 px, takze na
    // velkom monitore bola mapa uzky pas a pod nou zostalo prazdne miesto.
    // Teraz vyplni zvysok okna: nad mapou je odsadenie stranky 24 px + hlavicka
    // ~67 px + zalozky ~59 px, pod nou ~44 px -> spolu ~200 px.
    // minHeight drzi povodnych 520 px, aby na nizkom okne nebola mapa mensia nez predtym.
    CE('div', { style: Object.assign({}, S.mapWrap, { height: isMobile ? 'calc(100vh - 110px)' : 'calc(100vh - 200px)', minHeight: '520px', position: 'relative' }) },
      CE('div', { ref: mapRef, style: { height: isMobile ? 'calc(100vh - 110px)' : 'calc(100vh - 200px)', minHeight: '520px', width: '100%' } }),

      /* ── MOBILE: collapsed ⚙️ FAB menu ── */
      isMobile && CE('div', { style: { position: 'absolute', top: '10px', right: '10px', zIndex: 1000 } },
        CE('button', {
          onClick: function() { setMapMobileMenuOpen(function(v) { return !v; }); },
          style: { background: '#1e293b', border: '2px solid #334155', color: '#e2e8f0', borderRadius: '8px', padding: '7px 11px', cursor: 'pointer', fontSize: '17px', lineHeight: 1, boxShadow: '0 2px 10px rgba(0,0,0,0.5)' }
        }, mapMobileMenuOpen ? '\u2715' : '\u2699\uFE0F'),
        mapMobileMenuOpen && CE('div', {
          style: { position: 'absolute', top: '42px', right: 0, background: '#1e293b', border: '1px solid #334155', borderRadius: '10px', padding: '6px', minWidth: '190px', boxShadow: '0 8px 24px rgba(0,0,0,0.55)', display: 'flex', flexDirection: 'column', gap: '5px' }
        },
          CE('button', {
            onClick: function() { setShowLinks(function(v) { return !v; }); setMapMobileMenuOpen(false); },
            style: { background: showLinks ? '#1e40af' : '#0f172a', color: showLinks ? '#bfdbfe' : '#94a3b8', border: 'none', borderRadius: '6px', padding: '10px 12px', cursor: 'pointer', fontSize: '13px', textAlign: 'left', fontWeight: '600' }
          }, '\uD83D\uDD17 ' + (showLinks ? 'Skry\u0165 prepojenia' : 'Zobrazi\u0165 prepojenia')),

          CE('button', {
            onClick: function() { toggleMapDrawLinkMode(); setMapMobileMenuOpen(false); },
            style: { background: mapDrawLinkMode ? '#4c1d95' : '#0f172a', color: mapDrawLinkMode ? '#ede9fe' : '#94a3b8', border: 'none', borderRadius: '6px', padding: '10px 12px', cursor: 'pointer', fontSize: '13px', textAlign: 'left', fontWeight: '600' }
          }, '\u270F\uFE0F ' + (mapDrawLinkMode ? (mapDrawLinkSrc ? '\u2192 Vyber cie\u013e...' : 'Klikni zdroj...') : 'Kresli\u0165 prep.'))
        )
      ),

      /* ── DESKTOP: original floating buttons ── */
      !isMobile && CE('button', {
        onClick: function() { setShowLinks(function(v) { return !v; }); },
        title: showLinks ? 'Skry\u0165 prepojenia' : 'Zobrazi\u0165 prepojenia',
        style: {
          position: 'absolute', top: '10px', right: '10px', zIndex: 1000,
          background: showLinks ? '#1e40af' : '#1e293b',
          color: showLinks ? '#bfdbfe' : '#94a3b8',
          border: '2px solid ' + (showLinks ? '#3b82f6' : '#334155'),
          borderRadius: '8px', padding: '7px 13px', cursor: 'pointer',
          fontSize: '13px', fontWeight: '600',
          boxShadow: '0 2px 10px rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', gap: '6px',
          transition: 'background 0.2s, border-color 0.2s',
        }
      }, CE('span', null, '\uD83D\uDD17'), CE('span', null, showLinks ? 'Skry\u0165 prepojenia' : 'Zobrazi\u0165 prepojenia')),
      !isMobile && CE('button', {
        onClick: toggleMapDrawLinkMode,
        title: mapDrawLinkMode ? 'Ukon\u010Di\u0165 re\u017eim prepojeni' : 'Kresli\u0165 prepojenia (klikni 2 zariadenia)',
        style: {
          position: 'absolute', top: '55px', right: '10px', zIndex: 1000,
          background: mapDrawLinkMode ? (mapDrawLinkSrc ? '#7c3aed' : '#4c1d95') : '#1e293b',
          color: mapDrawLinkMode ? '#ede9fe' : '#94a3b8',
          border: '2px solid ' + (mapDrawLinkMode ? '#8b5cf6' : '#334155'),
          borderRadius: '8px', padding: '7px 13px', cursor: 'pointer',
          fontSize: '13px', fontWeight: '600',
          boxShadow: '0 2px 10px rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', gap: '6px',
          transition: 'background 0.2s, border-color 0.2s',
        }
      }, CE('span', null, '\u270F\uFE0F'), CE('span', null, mapDrawLinkMode ? (mapDrawLinkSrc ? '\u2192 Vyber cie\u013e...' : 'Klikni zdroj...') : 'Kresli\u0165 prep.'))
    ),
    // Quick-link modal (Draw Link Mode)
    mapQuickLinkModal && CE('div', { style: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' } },
      CE('div', { style: { background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '24px', width: '420px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.7)' } },
        CE('h3', { style: { margin: '0 0 16px', color: '#a78bfa', fontSize: '16px' } }, '\uD83D\uDD17 Prepojenie zariaden\u00ed'),
        CE('div', { style: { marginBottom: '12px', padding: '10px', background: 'rgba(59,130,246,0.1)', border: '1px solid #1e40af', borderRadius: '6px' } },
          CE('p', { style: { color: '#93c5fd', fontSize: '12px', fontWeight: '600', margin: '0 0 6px' } }, '\uD83D\uDCE4 Zdrojov\u00e9 zariadenie: ' + mapQuickLinkModal.srcDevice.name),
          CE('select', { value: mapQuickLinkData.srcIfaceId, style: { width: '100%', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: '6px', padding: '8px', fontSize: '13px' },
            onChange: function(ev) { setMapQuickLinkData(function(f) { return Object.assign({}, f, { srcIfaceId: ev.target.value }); }); } },
            CE('option', { value: '' }, '-- zdrojov\u00fd interface --'),
            mapQuickLinkModal.srcIfaces.map(function(i) { return CE('option', { key: i.id, value: i.id, disabled: !!i.is_occupied }, i.name + ' [' + popisDruhu(i) + ']' + (i.speed_mbps ? ' ' + i.speed_mbps + 'M' : '') + (i.is_occupied ? ' \u{1F512} OBSADEN\u00DD' : '')); })
          ),
          mapQuickLinkModal.srcIfaces.length === 0 && CE('p', { style: { color: '#f59e0b', fontSize: '12px', margin: '4px 0 0' } }, '\u26A0\uFE0F Zariadenie nem\u00e1 interfacy.')
        ),
        CE('div', { style: { marginBottom: '12px', padding: '10px', background: 'rgba(34,197,94,0.08)', border: '1px solid #166534', borderRadius: '6px' } },
          CE('p', { style: { color: '#86efac', fontSize: '12px', fontWeight: '600', margin: '0 0 6px' } }, '\uD83D\uDCE5 Cie\u013eov\u00e9 zariadenie: ' + mapQuickLinkModal.tgtDevice.name),
          CE('select', { value: mapQuickLinkData.tgtIfaceId, style: { width: '100%', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: '6px', padding: '8px', fontSize: '13px' },
            onChange: function(ev) { setMapQuickLinkData(function(f) { return Object.assign({}, f, { tgtIfaceId: ev.target.value }); }); } },
            CE('option', { value: '' }, '-- cie\u013eov\u00fd interface --'),
            mapQuickLinkModal.tgtIfaces.map(function(i) { return CE('option', { key: i.id, value: i.id, disabled: !!i.is_occupied }, i.name + ' [' + popisDruhu(i) + ']' + (i.speed_mbps ? ' ' + i.speed_mbps + 'M' : '') + (i.is_occupied ? ' \u{1F512} OBSADEN\u00DD' : '')); })
          ),
          mapQuickLinkModal.tgtIfaces.length === 0 && CE('p', { style: { color: '#f59e0b', fontSize: '12px', margin: '4px 0 0' } }, '\u26A0\uFE0F Zariadenie nem\u00e1 interfacy.')
        ),
        CE('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' } },
          CE('div', null,
            CE('label', { style: { display: 'block', fontSize: '11px', color: '#64748b', marginBottom: '4px' } }, 'Ozna\u010denie k\u00e1bla'),
            CE('input', { type: 'text', value: mapQuickLinkData.cableLabel, placeholder: 'napr. CAB-001', style: { width: '100%', boxSizing: 'border-box', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: '6px', padding: '8px', fontSize: '13px' },
              onChange: function(ev) { setMapQuickLinkData(function(f) { return Object.assign({}, f, { cableLabel: ev.target.value }); }); } })
          ),
          CE('div', null,
            CE('label', { style: { display: 'block', fontSize: '11px', color: '#64748b', marginBottom: '4px' } }, 'Typ k\u00e1bla'),
            CE('select', { value: mapQuickLinkData.cableType, style: { width: '100%', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: '6px', padding: '8px', fontSize: '13px' },
              onChange: function(ev) { setMapQuickLinkData(function(f) { return Object.assign({}, f, { cableType: ev.target.value }); }); } },
              CE('option', { value: 'utp' }, 'UTP'), CE('option', { value: 'sfp' }, 'SFP / Fiber'),
              CE('option', { value: 'sfp+' }, 'SFP+ / 10G'), CE('option', { value: 'dac' }, 'DAC'),
              CE('option', { value: 'wifi' }, 'WiFi'), CE('option', { value: 'gpon' }, 'GPON / PON Fiber')
            )
          )
        ),
        CE('div', { style: { display: 'flex', gap: '8px' } },
          CE('button', { style: { flex: 1, background: '#334155', color: '#94a3b8', border: 'none', borderRadius: '6px', padding: '10px', cursor: 'pointer', fontSize: '13px' },
            onClick: function() { setMapQuickLinkModal(null); } }, 'Zru\u0161i\u0165'),
          CE('button', { style: { flex: 2, background: '#7c3aed', color: '#fff', border: 'none', borderRadius: '6px', padding: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
            onClick: saveMapQuickLink }, '\uD83D\uDD17 Pripoji\u0165')
        )
      )
    ),
    // ── Edit link modal (map) ──
    mapEditLinkModal && CE('div', { style: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' } },
      CE('div', { style: { background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '24px', width: '420px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.7)' } },
        CE('h3', { style: { margin: '0 0 4px', color: '#f59e0b', fontSize: '16px' } }, '\u270F\uFE0F Editova\u0165 spoj'),
        CE('p', { style: { color: '#64748b', fontSize: '11px', margin: '0 0 16px' } }, 'Spoj #' + mapEditLinkModal.link.id + (mapEditLinkModal.link.cable_label ? ' \u2014 ' + mapEditLinkModal.link.cable_label : '')),
        CE('div', { style: { marginBottom: '12px', padding: '10px', background: 'rgba(59,130,246,0.1)', border: '1px solid #1e40af', borderRadius: '6px' } },
          CE('p', { style: { color: '#93c5fd', fontSize: '12px', fontWeight: '600', margin: '0 0 6px' } }, '\uD83D\uDCE4 ' + mapEditLinkModal.devA.name),
          CE('select', { value: mapEditLinkData.srcIfaceId, style: { width: '100%', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: '6px', padding: '8px', fontSize: '13px' },
            onChange: function(ev) { setMapEditLinkData(function(f) { return Object.assign({}, f, { srcIfaceId: ev.target.value }); }); } },
            CE('option', { value: '' }, '-- zdrojov\u00fd interface --'),
            mapEditLinkModal.ifacesA.map(function(i) {
              var busy = i.is_occupied && String(i.id) !== mapEditLinkData.srcIfaceId;
              return CE('option', { key: i.id, value: i.id, disabled: busy },
                i.name + ' [' + popisDruhu(i) + ']' + (i.speed_mbps ? ' ' + i.speed_mbps + 'M' : '') + (busy ? ' \uD83D\uDD12 OBSADEN\u00DD' : ''));
            })
          )
        ),
        CE('div', { style: { marginBottom: '12px', padding: '10px', background: 'rgba(34,197,94,0.08)', border: '1px solid #166534', borderRadius: '6px' } },
          CE('p', { style: { color: '#86efac', fontSize: '12px', fontWeight: '600', margin: '0 0 6px' } }, '\uD83D\uDCE5 ' + mapEditLinkModal.devB.name),
          CE('select', { value: mapEditLinkData.tgtIfaceId, style: { width: '100%', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: '6px', padding: '8px', fontSize: '13px' },
            onChange: function(ev) { setMapEditLinkData(function(f) { return Object.assign({}, f, { tgtIfaceId: ev.target.value }); }); } },
            CE('option', { value: '' }, '-- cie\u013eov\u00fd interface --'),
            mapEditLinkModal.ifacesB.map(function(i) {
              var busy = i.is_occupied && String(i.id) !== mapEditLinkData.tgtIfaceId;
              return CE('option', { key: i.id, value: i.id, disabled: busy },
                i.name + ' [' + popisDruhu(i) + ']' + (i.speed_mbps ? ' ' + i.speed_mbps + 'M' : '') + (busy ? ' \uD83D\uDD12 OBSADEN\u00DD' : ''));
            })
          )
        ),
        CE('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' } },
          CE('div', null,
            CE('label', { style: { display: 'block', fontSize: '11px', color: '#64748b', marginBottom: '4px' } }, 'Ozna\u010denie k\u00e1bla'),
            CE('input', { type: 'text', value: mapEditLinkData.cableLabel, placeholder: 'napr. CAB-001', style: { width: '100%', boxSizing: 'border-box', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: '6px', padding: '8px', fontSize: '13px' },
              onChange: function(ev) { setMapEditLinkData(function(f) { return Object.assign({}, f, { cableLabel: ev.target.value }); }); } })
          ),
          CE('div', null,
            CE('label', { style: { display: 'block', fontSize: '11px', color: '#64748b', marginBottom: '4px' } }, 'Typ k\u00e1bla'),
            CE('select', { value: mapEditLinkData.cableType, style: { width: '100%', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: '6px', padding: '8px', fontSize: '13px' },
              onChange: function(ev) { setMapEditLinkData(function(f) { return Object.assign({}, f, { cableType: ev.target.value }); }); } },
              CE('option', { value: 'utp' }, 'UTP'), CE('option', { value: 'sfp' }, 'SFP / Fiber'),
              CE('option', { value: 'sfp+' }, 'SFP+ / 10G'), CE('option', { value: 'dac' }, 'DAC'),
              CE('option', { value: 'wifi' }, 'WiFi'), CE('option', { value: 'gpon' }, 'GPON / PON Fiber')
            )
          )
        ),
        CE('div', { style: { display: 'flex', gap: '8px' } },
          CE('button', { style: { flex: 1, background: '#334155', color: '#94a3b8', border: 'none', borderRadius: '6px', padding: '10px', cursor: 'pointer', fontSize: '13px' },
            onClick: function() { setMapEditLinkModal(null); } }, 'Zru\u0161i\u0165'),
          CE('button', { style: { flex: 2, background: '#b45309', color: '#fff', border: 'none', borderRadius: '6px', padding: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
            onClick: saveMapEditLink }, '\u2705 Ulo\u017ei\u0165 zmeny')
        )
      )
    )
  );
}

// ─── Topology View (Cytoscape.js) ───────────────────────────────────────────

export default MapView;
